import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Tooltip,
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
  CheckCircle,
  Image as ImageIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { analyzeWizardUploads, getWizardJurisprudences } from '../../services/wizardService';
import { useCredit } from '../../contexts/CreditContext';
import AudioRecorder from '../AudioRecorder';
import DocumentTypeSelector from './DocumentTypeSelector';
import DynamicDocumentForm from './DynamicDocumentForm';
import { DocumentType, getDocumentTypeById } from './documentTypes';

interface DocumentTemplate {
  id: string;
  nome: string;
  area: string;
  descricao: string;
  topicos: TopicoJuridico[];
}

interface TopicoJuridico {
  id: string;
  titulo: string;
  conteudo: string;
  jurisprudencias: JurisprudenciaReferencia[];
  obrigatorio: boolean;
}

interface JurisprudenciaReferencia {
  id: string;
  tribunal: string;
  numero_processo: string;
  ementa_resumo: string;
  relevancia: number;
}

interface PedidoSelecionado {
  id: string;
  titulo: string;
  descricao: string;
  baseLegal: string;
  valor?: string;
  incluirValor: boolean;
  customizado: boolean;
  natureza?: 'principal' | 'acessorio' | 'subsidiario' | 'alternativo' | 'condicional';
  tipoValor?: 'certo' | 'estimado' | 'percentual' | 'sem_valor';
  condicao?: string;
  impactoValor?: 'nao_altera' | 'altera' | 'depende_apuracao';
}

interface WizardData {
  tipo_documento: string; // ID do tipo de documento selecionado
  area_direito: string;
  nome_acao: string;
  acoes_selecionadas: string[]; // Para múltiplas ações selecionadas
  descricao_fatos: string;
  tribunais_selecionados: string[];
  topicos_selecionados: string[];
  pedidos_selecionados: PedidoSelecionado[];
  rito_processual: string; // Novo campo para rito
  competencia: string; // Novo campo para competência/vara
  dados_autor: {
    nome: string;
    cpf_cnpj: string;
    endereco: string;
  };
  dados_reu: {
    nome: string;
    cpf_cnpj: string;
    endereco: string;
  };
  valor_causa: string;
  observacoes: string;
  // Campos dinâmicos do formulário por tipo de documento
  campos_dinamicos: { [key: string]: any };
}

const DocumentWizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const chatContext = location.state?.context;
  const fromChat = location.state?.fromChat;

  const { credits, shouldShowWarning, isBlocked, overdraftUsed } = useCredit();

  const [activeStep, setActiveStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false); // Prevenir múltiplos cliques
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null); // Tipo de documento selecionado
  const [aiAssistedMode, setAiAssistedMode] = useState(false); // Modo assistido pela IA (quando usuário não sabe o tipo)
  const [wizardData, setWizardData] = useState<WizardData>({
    tipo_documento: '', // ID do tipo de documento
    area_direito: '', // Mantido como string por compatibilidade, mas usaremos array
    nome_acao: '',
    acoes_selecionadas: [],
    descricao_fatos: chatContext || '', // Pré-preencher com contexto do chat
    tribunais_selecionados: [],
    topicos_selecionados: [],
    pedidos_selecionados: [],
    rito_processual: '', // Novo campo para rito
    competencia: '', // Novo campo para competência/vara
    dados_autor: {
      nome: '',
      cpf_cnpj: '',
      endereco: ''
    },
    dados_reu: {
      nome: '',
      cpf_cnpj: '',
      endereco: ''
    },
    valor_causa: '',
    observacoes: '',
    campos_dinamicos: {} // Campos dinâmicos do formulário
  });

  // Ritos processuais disponíveis
  const ritosProcessuais = [
    { id: 'ordinario', nome: 'Procedimento Comum (Ordinário)', descricao: 'Ações cíveis acima de 40 salários mínimos', areas: ['civil', 'consumidor'] },
    { id: 'sumarissimo', nome: 'Juizado Especial (Sumaríssimo)', descricao: 'Ações até 40 salários mínimos - Lei 9.099/95', areas: ['civil', 'consumidor'] },
    { id: 'trabalhista', nome: 'Rito Trabalhista (CLT)', descricao: 'Ações perante a Justiça do Trabalho', areas: ['trabalhista'] },
    { id: 'penal_comum', nome: 'Rito Penal Comum (CPP)', descricao: 'Ações penais de procedimento comum', areas: ['penal'] },
    { id: 'penal_sumarissimo', nome: 'JECRIM (Menor Potencial)', descricao: 'Crimes com pena até 2 anos - Lei 9.099/95', areas: ['penal'] },
    { id: 'familia', nome: 'Vara de Família', descricao: 'Divórcio, guarda, alimentos, reconhecimento', areas: ['familia'] },
    { id: 'fazenda', nome: 'Vara da Fazenda Pública', descricao: 'Ações contra entes públicos', areas: ['administrativo'] },
    { id: 'mandado_seguranca', nome: 'Mandado de Segurança', descricao: 'Contra ato ilegal de autoridade - Lei 12.016/09', areas: ['administrativo', 'tributario'] }
  ];

  // Competências/Varas disponíveis
  const competenciasDisponiveis = [
    { id: 'vara_civel', nome: 'Vara Cível', descricao: 'Ações cíveis em geral' },
    { id: 'juizado_especial', nome: 'Juizado Especial Cível', descricao: 'Causas até 40 SM' },
    { id: 'vara_trabalho', nome: 'Vara do Trabalho', descricao: 'Relações de emprego' },
    { id: 'vara_criminal', nome: 'Vara Criminal', descricao: 'Ações penais' },
    { id: 'jecrim', nome: 'Juizado Especial Criminal', descricao: 'Crimes de menor potencial' },
    { id: 'vara_familia', nome: 'Vara de Família e Sucessões', descricao: 'Questões familiares' },
    { id: 'vara_fazenda', nome: 'Vara da Fazenda Pública', descricao: 'Entes públicos como parte' },
    { id: 'violencia_domestica', nome: 'Juizado de Violência Doméstica', descricao: 'Lei Maria da Penha' }
  ];

  // Array para múltiplas áreas selecionadas
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);

  // Novos states para upload e análise
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [extractedContext, setExtractedContext] = useState<string>('');
  const [textBySource, setTextBySource] = useState<any>(null); // Dados separados por fonte
  const [suggestedAreas, setSuggestedAreas] = useState<any[]>([]); // Objetos completos com {id, nome, confidence, reasoning}
  const [showAllAreas, setShowAllAreas] = useState(false); // Controle para expandir/recolher áreas
  const [aiSuggestedTopics, setAiSuggestedTopics] = useState<any[]>([]); // Tópicos da IA
  const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]); // IDs pré-selecionados
  const [aiSuggestedFundamentos, setAiSuggestedFundamentos] = useState<any[]>([]); // Fundamentos da IA
  const [aiSuggestedPedidos, setAiSuggestedPedidos] = useState<any[]>([]); // Pedidos sugeridos pela IA
  const [pandoraJurisprudences, setPandoraJurisprudences] = useState<any[]>([]);
  const [loadingJurisprudences, setLoadingJurisprudences] = useState(false);
  const [analyzingFiles, setAnalyzingFiles] = useState(false);
  const [showExtractedText, setShowExtractedText] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [needsClarification, setNeedsClarification] = useState(false);
  const [clarificationMessage, setClarificationMessage] = useState('');

  // Estados para gerenciamento de imagens
  const [imagePreviews, setImagePreviews] = useState<Map<string, string>>(new Map());
  const [imageInclusionMode, setImageInclusionMode] = useState<Map<string, 'body' | 'attachment'>>(new Map());

  // Ref para controlar inicialização de tópicos (evitar re-seleção automática)
  const topicsInitializedRef = useRef(false);

  // Ref para scroll ao topo do wizard
  const wizardTopRef = useRef<HTMLDivElement>(null);

  // Novos states para configuração do documento (Step 5)
  const [documentName, setDocumentName] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<string>(''); // ID da pasta ou '' para raiz
  const [availableFolders, setAvailableFolders] = useState<any[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Estado para modal de seleção de tribunais
  const [showTribunalModal, setShowTribunalModal] = useState(false);
  const [expandedTribunalGroup, setExpandedTribunalGroup] = useState<string | null>(null);
  const [tribunalSearchTerm, setTribunalSearchTerm] = useState('');

  // Estado para modal de pedido personalizado
  const [showPedidoModal, setShowPedidoModal] = useState(false);
  const [novoPedido, setNovoPedido] = useState({
    titulo: '',
    natureza: 'principal',
    descricao: '',
    baseLegal: '',
    tipoValor: 'sem_valor',
    valor: '',
    incluirValor: false,
    condicao: '',
    impactoValor: 'nao_altera'
  });

  // Estado para modal de edição de áreas
  const [showAreaEditDialog, setShowAreaEditDialog] = useState(false);

  // Opções de formatação profissional
  const [formatOptions, setFormatOptions] = useState({
    estilo: 'tradicional' as 'tradicional' | 'moderno' | 'minimalista',
    fonte: 'times',
    tamanhoFonte: '12',
    espacamento: '1.5',
    margens: 'oab',
    numerarPaginas: true,
    incluirCabecalho: true,
    posicaoImagens: 'anexo'
  });

  // Função para aplicar configurações baseadas no estilo selecionado
  const aplicarEstilo = (estilo: 'tradicional' | 'moderno' | 'minimalista') => {
    switch (estilo) {
      case 'tradicional':
        setFormatOptions({
          estilo: 'tradicional',
          fonte: 'times',
          tamanhoFonte: '12',
          espacamento: '1.5',
          margens: 'oab',
          numerarPaginas: true,
          incluirCabecalho: true,
          posicaoImagens: 'anexo'
        });
        break;
      case 'moderno':
        setFormatOptions({
          estilo: 'moderno',
          fonte: 'calibri',
          tamanhoFonte: '12',
          espacamento: '1.5',
          margens: 'normais',
          numerarPaginas: true,
          incluirCabecalho: true,
          posicaoImagens: 'anexo'
        });
        break;
      case 'minimalista':
        setFormatOptions({
          estilo: 'minimalista',
          fonte: 'arial',
          tamanhoFonte: '11',
          espacamento: '1.0',
          margens: 'estreitas',
          numerarPaginas: false,
          incluirCabecalho: false,
          posicaoImagens: 'anexo'
        });
        break;
    }
  };

  // Handler para selecionar arquivos
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // Criar preview para imagens
    const newPreviews = new Map(imagePreviews);
    const newModes = new Map(imageInclusionMode);

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        // Criar URL de preview
        const previewUrl = URL.createObjectURL(file);
        newPreviews.set(file.name, previewUrl);
        // Por padrão, imagens vão como anexo
        newModes.set(file.name, 'attachment');
      }
    });

    setImagePreviews(newPreviews);
    setImageInclusionMode(newModes);
    setUploadedFiles(prev => [...prev, ...files]);
  };

  // Handler para remover arquivo
  const handleRemoveFile = (index: number) => {
    const fileToRemove = uploadedFiles[index];

    // Limpar preview se for imagem
    if (fileToRemove && imagePreviews.has(fileToRemove.name)) {
      const previewUrl = imagePreviews.get(fileToRemove.name);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      const newPreviews = new Map(imagePreviews);
      newPreviews.delete(fileToRemove.name);
      setImagePreviews(newPreviews);

      const newModes = new Map(imageInclusionMode);
      newModes.delete(fileToRemove.name);
      setImageInclusionMode(newModes);
    }

    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handler para gravação de áudio concluída
  const handleRecordingComplete = (audioFile: File) => {
    setUploadedFiles(prev => [...prev, audioFile]);
  };

  // Highlight suggested topics visually but do NOT auto-select them
  useEffect(() => {
    if (activeStep === 4 && !topicsInitializedRef.current) {
      topicsInitializedRef.current = true;

      if (aiSuggestedTopics.length > 0) {
        setSuggestedTopics(aiSuggestedTopics.map(t => t.id));
      } else if (selectedAreas.length > 0) {
        const todosTopicos: any[] = [];
        selectedAreas.forEach(areaId => {
          const topicos = topicosJuridicos[areaId as keyof typeof topicosJuridicos] || [];
          todosTopicos.push(...topicos);
        });
        setSuggestedTopics(todosTopicos.slice(0, 3).map(t => t.id));
      }
    }

    if (activeStep < 4) {
      topicsInitializedRef.current = false;
    }
  }, [activeStep, selectedAreas, aiSuggestedTopics]);

  // Buscar jurisprudências VALIDADAS quando tiver tribunais selecionados
  useEffect(() => {
    if (wizardData.tribunais_selecionados.length > 0 && pandoraJurisprudences.length === 0) {
      const fetchJurisprudences = async () => {
        try {
          setLoadingJurisprudences(true);

          // Importar serviço de jurisprudências validadas
          const { searchJurisprudence } = await import('../../services/jurisprudenceService');

          // Construir query inteligente baseada no contexto
          let searchQuery = '';

          // 1. Usar palavras-chave do contexto extraído
          if (extractedContext) {
            // Pegar primeiras palavras relevantes (max 100 caracteres)
            searchQuery = extractedContext.substring(0, 100);
          }

          // 2. Se não tem contexto, usar descrição dos fatos
          if (!searchQuery && wizardData.descricao_fatos) {
            searchQuery = wizardData.descricao_fatos.substring(0, 100);
          }

          // 3. Se ainda não tem nada, usar tópicos selecionados
          if (!searchQuery && wizardData.topicos_selecionados.length > 0) {
            searchQuery = wizardData.topicos_selecionados.join(' ');
          }

          // Buscar jurisprudências REAIS do banco
          if (searchQuery.length >= 3) {
            const result = await searchJurisprudence(searchQuery, {
              limit: 10  // Buscar até 10 jurisprudências relevantes
            });

            // Mapear para o formato esperado
            const mappedJurisprudences = result.items.map((item: any) => ({
              id: item.id,
              titulo: item.title,
              tribunal: item.court_name,
              ementa: item.ementa,
              tese_vinculante: item.binding_thesis,
              resumo: item.summary,
              data_julgamento: item.judgment_date,
              numero_cnj: item.cnj_number,
              relevancia: 0.8  // Score fixo, pode ser melhorado com busca semântica
            }));

            setPandoraJurisprudences(mappedJurisprudences);
          }
        } catch (error) {
          console.error('Erro ao buscar jurisprudências validadas:', error);
          // Em caso de erro, deixa array vazio ao invés de falhar
          setPandoraJurisprudences([]);
        } finally {
          setLoadingJurisprudences(false);
        }
      };

      fetchJurisprudences();
    }
  }, [activeStep, extractedContext, wizardData.descricao_fatos, wizardData.topicos_selecionados]);

  // Carregar pastas e pré-preencher nome quando chegar no Step 6 (Configurações do Documento)
  useEffect(() => {
    if (activeStep === 6) {
      // Pré-preencher nome se ainda não foi definido
      const fullActionName = [
        ...(wizardData.acoes_selecionadas || []),
        wizardData.nome_acao
      ].filter(Boolean).join(', ');

      if (!documentName && fullActionName) {
        setDocumentName(fullActionName);
      }

      // Carregar pastas se ainda não foram carregadas
      if (availableFolders.length === 0) {
        const loadFolders = async () => {
          try {
            setLoadingFolders(true);
            const api = (await import('../../services/api')).default;
            const timestamp = new Date().getTime();
            const response = await api.get(`/documents/folders?_t=${timestamp}`);

            // O backend retorna { status: "success", data: folders_data }
            const folders = response.data?.data || [];

            setAvailableFolders(folders);
          } catch (error) {
            setAvailableFolders([]); // Garantir array vazio em caso de erro
          } finally {
            setLoadingFolders(false);
          }
        };

        loadFolders();
      }
    }
  }, [activeStep, wizardData.nome_acao, wizardData.acoes_selecionadas, documentName, availableFolders.length]);

  // Handler para analisar documentos
  const handleAnalyzeDocuments = async () => {
    // Verificar se tem pelo menos texto OU arquivos
    if (!wizardData.descricao_fatos && uploadedFiles.length === 0) return;

    try {
      setAnalyzingFiles(true);

      const result = await analyzeWizardUploads(
        wizardData.descricao_fatos,
        uploadedFiles.length > 0 ? uploadedFiles : undefined
      );

      // Atualizar estados - armazenar objetos completos e dados separados
      setExtractedContext(result.data.extracted_text);
      setTextBySource((result.data as any).text_by_source || null);
      setSuggestedAreas(result.data.suggested_areas || []);

      // Verificar se a IA precisa de esclarecimento
      const aiData = result.data as any;
      if (aiData.needs_clarification) {
        setNeedsClarification(true);
        setClarificationMessage(aiData.clarification_message || 'Forneça mais detalhes sobre o caso para uma classificação precisa.');
      }

    } catch (error: any) {
      alert(error.response?.data?.detail || 'Erro ao analisar arquivos');
    } finally {
      setAnalyzingFiles(false);
    }
  };

  // Mapeamento de tipos de documento para tipo de ação automático
  // Para documentos específicos, o tipo de ação é óbvio e pode ser preenchido automaticamente
  // Para documentos genéricos (null), o usuário pode detalhar o tipo de ação
  const tipoAcaoAutomatico: Record<string, string | null> = {
    // Criminal - Tipos específicos com ação automática
    'habeas_corpus': 'Habeas Corpus',
    'queixa_crime': 'Queixa-Crime',
    'revisao_criminal': 'Revisão Criminal',
    'liberdade_provisoria': 'Pedido de Liberdade Provisória',
    'recurso_sentido_estrito': 'Recurso em Sentido Estrito',
    'apelacao_criminal': 'Apelação Criminal',
    'pedido_progressao_regime': 'Pedido de Progressão de Regime',

    // Cível - Tipos específicos
    'mandado_seguranca': 'Mandado de Segurança',
    'agravo_instrumento': 'Agravo de Instrumento',
    'agravo_interno': 'Agravo Interno',
    'recurso_especial': 'Recurso Especial',
    'recurso_extraordinario': 'Recurso Extraordinário',
    'embargos_declaracao': 'Embargos de Declaração',
    'embargos_divergencia': 'Embargos de Divergência',
    'embargos_terceiro': 'Embargos de Terceiro',
    'embargos_execucao': 'Embargos à Execução',
    'apelacao': 'Apelação Cível',
    'acao_rescisoria': 'Ação Rescisória',
    'cumprimento_sentenca': 'Cumprimento de Sentença',
    'execucao_titulo_extrajudicial': 'Execução de Título Extrajudicial',
    'tutela_provisoria_cautelar': 'Tutela Cautelar',
    'tutela_provisoria_antecipada': 'Tutela Antecipada',
    'adjudicacao_compulsoria': 'Adjudicação Compulsória',
    'sustacao_protesto': 'Sustação de Protesto',
    'busca_apreensao_menor': 'Busca e Apreensão de Menor',
    'recuperacao_judicial': 'Recuperação Judicial',

    // Trabalhista - Tipos específicos
    'reclamacao_trabalhista': 'Reclamação Trabalhista',
    'agravo_peticao': 'Agravo de Petição',
    'recurso_ordinario_trabalhista': 'Recurso Ordinário Trabalhista',
    'recurso_revista': 'Recurso de Revista',

    // Previdenciário - Tipos específicos
    'concessao_beneficio': 'Concessão de Benefício Previdenciário',
    'revisao_beneficio': 'Revisão de Benefício Previdenciário',

    // Trânsito - Tipos específicos
    'defesa_previa_detran': 'Defesa Administrativa de Trânsito',
    'recurso_jari': 'Recurso à JARI',
    'recurso_cetran': 'Recurso ao CETRAN',

    // Tipos genéricos - null indica que usuário pode detalhar
    'peticao_inicial_civel': null,
    'contestacao_civel': null,
    'replica_civel': null,
    'manifestacao_processual': null,
    'alegacoes_finais': null,
    'contestacao_trabalhista': null,
    'replica_trabalhista': null,
    'defesa_previa_criminal': null,
    'resposta_acusacao': null,
    'alegacoes_finais_criminais': null,
  };

  // Função para obter o tipo de ação baseado no documento selecionado
  const getTipoAcaoParaDocumento = (): { automatico: boolean; valor: string } => {
    if (!selectedDocumentType) {
      return { automatico: false, valor: '' };
    }

    const tipoAcao = tipoAcaoAutomatico[selectedDocumentType.id];

    if (tipoAcao !== undefined && tipoAcao !== null) {
      return { automatico: true, valor: tipoAcao };
    }

    // Se não está no mapeamento ou é null, permite edição livre
    // Combinar ações selecionadas com o texto livre
    const acoes = [
      ...(wizardData.acoes_selecionadas || []),
      wizardData.nome_acao
    ].filter(Boolean);

    return {
      automatico: false,
      valor: acoes.length > 0 ? acoes.join(', ') : ''
    };
  };

  // Dados simulados
  const areasDisponiveis = [
    { id: 'civil', nome: 'Direito Civil', icone: '⚖️' },
    { id: 'penal', nome: 'Direito Penal', icone: '🚔' },
    { id: 'trabalhista', nome: 'Direito Trabalhista', icone: '👷' },
    { id: 'consumidor', nome: 'Direito do Consumidor', icone: '🛒' },
    { id: 'administrativo', nome: 'Direito Administrativo', icone: '🏛️' },
    { id: 'tributario', nome: 'Direito Tributário', icone: '💰' },
    { id: 'previdenciario', nome: 'Direito Previdenciário', icone: '🏥' },
    { id: 'empresarial', nome: 'Direito Empresarial', icone: '🏢' },
    { id: 'familia', nome: 'Direito de Família', icone: '👨‍👩‍👧‍👦' },
    { id: 'imobiliario', nome: 'Direito Imobiliário', icone: '🏠' },
    { id: 'bancario', nome: 'Direito Bancário', icone: '🏦' },
    { id: 'ambiental', nome: 'Direito Ambiental', icone: '🌱' },
    { id: 'constitucional', nome: 'Direito Constitucional', icone: '📜' },
    { id: 'eleitoral', nome: 'Direito Eleitoral', icone: '🗳️' },
    { id: 'internacional', nome: 'Direito Internacional', icone: '🌍' },
    { id: 'digital', nome: 'Direito Digital', icone: '💻' },
    { id: 'saude', nome: 'Direito da Saúde', icone: '⚕️' },
    { id: 'educacional', nome: 'Direito Educacional', icone: '🎓' },
    { id: 'desportivo', nome: 'Direito Desportivo', icone: '⚽' },
    { id: 'autoral', nome: 'Direito Autoral', icone: '©️' },
    { id: 'propriedade_intelectual', nome: 'Propriedade Intelectual', icone: '💡' },
    { id: 'sucessoes', nome: 'Direito das Sucessões', icone: '🏺' },
    { id: 'agrario', nome: 'Direito Agrário', icone: '🚜' },
    { id: 'maritimo', nome: 'Direito Marítimo', icone: '⚓' },
    { id: 'aeronautico', nome: 'Direito Aeronáutico', icone: '✈️' },
    { id: 'militar', nome: 'Direito Militar', icone: '🎖️' },
    { id: 'urbanistico', nome: 'Direito Urbanístico', icone: '🏙️' },
    { id: 'financeiro', nome: 'Direito Financeiro', icone: '💸' },
    { id: 'securitario', nome: 'Direito Securitário', icone: '🛡️' },
    { id: 'concorrencial', nome: 'Direito Concorrencial', icone: '🔄' }
  ];

  const acoesDisponiveis = {
    civil: [
      'Ação de Indenização por Danos Morais',
      'Ação de Cobrança',
      'Ação de Despejo',
      'Ação de Usucapião',
      'Ação de Reintegração de Posse',
      'Ação de Reivindicatória',
      'Ação de Dissolução de União Estável',
      'Ação de Nulidade de Negócio Jurídico',
      'Ação de Consignação em Pagamento',
      'Ação de Prestação de Contas'
    ],
    penal: [
      'Queixa-Crime',
      'Queixa-Crime por Calúnia',
      'Queixa-Crime por Difamação',
      'Queixa-Crime por Injúria',
      'Representação Criminal',
      'Representação por Ameaça',
      'Representação por Lesão Corporal Leve',
      'Pedido de Medidas Protetivas (Lei Maria da Penha)',
      'Notícia-Crime',
      'Habeas Corpus',
      'Revisão Criminal',
      'Ação Penal Privada Subsidiária da Pública',
      'Relaxamento de Prisão',
      'Liberdade Provisória',
      'Revogação de Prisão Preventiva',
      'Recurso em Sentido Estrito',
      'Apelação Criminal',
      'Embargos de Declaração Criminal',
      'Recurso Especial Criminal',
      'Recurso Extraordinário Criminal'
    ],
    trabalhista: [
      'Reclamação Trabalhista',
      'Ação de Adicional de Insalubridade',
      'Ação de Horas Extras',
      'Ação de Rescisão Indireta',
      'Ação de Equiparação Salarial',
      'Ação de Estabilidade no Emprego',
      'Ação de Reintegração no Emprego',
      'Ação de Adicional de Periculosidade',
      'Ação de Danos Morais Trabalhistas',
      'Ação de Acidente de Trabalho'
    ],
    consumidor: [
      'Ação de Reparação de Danos Materiais e Morais',
      'Ação de Obrigação de Fazer',
      'Ação de Rescisão Contratual',
      'Ação de Repetição de Indébito',
      'Ação de Vício do Produto',
      'Ação de Publicidade Enganosa',
      'Ação de Cobrança Indevida',
      'Ação de Cancelamento de Inscrição em Órgão de Proteção',
      'Ação de Revisão de Cláusulas Abusivas',
      'Ação de Danos Morais por Negativação Indevida'
    ],
    administrativo: [
      'Mandado de Segurança',
      'Ação Popular',
      'Ação Civil Pública',
      'Mandado de Injunção',
      'Habeas Data',
      'Ação Anulatória de Ato Administrativo',
      'Ação de Improbidade Administrativa',
      'Ação Ordinária contra a Fazenda Pública',
      'Ação de Responsabilidade Civil do Estado',
      'Ação de Servidor Público'
    ],
    tributario: [
      'Ação Anulatória de Débito Fiscal',
      'Ação de Repetição de Indébito Tributário',
      'Ação Declaratória de Inexistência de Relação Jurídico-Tributária',
      'Mandado de Segurança Tributário',
      'Ação de Consignação em Pagamento Tributário',
      'Ação Cautelar Fiscal',
      'Embargos à Execução Fiscal',
      'Exceção de Pré-Executividade',
      'Ação de Restituição de Tributos',
      'Ação de Compensação Tributária'
    ],
    previdenciario: [
      'Ação de Aposentadoria por Tempo de Contribuição',
      'Ação de Aposentadoria por Invalidez',
      'Ação de Auxílio-Doença',
      'Ação de Pensão por Morte',
      'Ação de Salário-Maternidade',
      'Ação de Auxílio-Acidente',
      'Ação de Revisão de Benefício',
      'Ação de Restabelecimento de Benefício',
      'Ação de Reconhecimento de Tempo de Serviço',
      'Ação de Desaposentação'
    ],
    empresarial: [
      'Ação de Dissolução de Sociedade',
      'Ação de Apuração de Haveres',
      'Ação de Responsabilidade de Administrador',
      'Ação de Recuperação Judicial',
      'Ação de Falência',
      'Ação Revocatória Falimentar',
      'Ação de Nulidade de Assembleia',
      'Ação de Exclusão de Sócio',
      'Ação de Cobrança Empresarial',
      'Ação de Concorrência Desleal'
    ],
    familia: [
      'Ação de Divórcio',
      'Ação de Separação',
      'Ação de Alimentos',
      'Ação de Guarda',
      'Ação de Visitação',
      'Ação de Investigação de Paternidade',
      'Ação de Adoção',
      'Ação de União Estável',
      'Ação de Tutela',
      'Ação de Curatela'
    ],
    imobiliario: [
      'Ação de Usucapião',
      'Ação de Reintegração de Posse',
      'Ação de Despejo',
      'Ação de Cobrança de Aluguel',
      'Ação Revisional de Aluguel',
      'Ação de Adjudicação Compulsória',
      'Ação de Imissão na Posse',
      'Ação de Nunciação de Obra Nova',
      'Ação de Dano Infecto',
      'Ação de Retificação de Área'
    ],
    bancario: [
      'Ação Revisional de Contrato Bancário',
      'Ação de Repetição de Indébito Bancário',
      'Ação de Danos Morais Bancários',
      'Ação de Busca e Apreensão',
      'Ação de Execução de Título Extrajudicial',
      'Ação de Consignação em Pagamento Bancário',
      'Ação de Cancelamento de Protesto',
      'Ação de Anatocismo',
      'Ação de Onerosidade Excessiva',
      'Ação de Quitação de Financiamento'
    ],
    ambiental: [
      'Ação Civil Pública Ambiental',
      'Ação Popular Ambiental',
      'Ação de Responsabilidade por Dano Ambiental',
      'Ação Cautelar Ambiental',
      'Ação de Obrigação de Fazer Ambiental',
      'Ação de Compensação Ambiental',
      'Ação de Licenciamento Ambiental',
      'Ação de Embargo de Obra',
      'Ação de Recuperação de Área Degradada',
      'Ação de Crimes Ambientais'
    ],
    constitucional: [
      'Ação Direta de Inconstitucionalidade',
      'Ação Declaratória de Constitucionalidade',
      'Arguição de Descumprimento de Preceito Fundamental',
      'Mandado de Segurança Constitucional',
      'Habeas Corpus Constitucional',
      'Mandado de Injunção',
      'Habeas Data',
      'Ação Popular Constitucional',
      'Recurso Extraordinário',
      'Reclamação Constitucional'
    ],
    eleitoral: [
      'Ação de Impugnação de Mandato Eletivo',
      'Ação de Investigação Judicial Eleitoral',
      'Recurso contra Expedição de Diploma',
      'Ação de Impugnação de Registro de Candidatura',
      'Representação por Captação Ilícita de Sufrágio',
      'Representação por Condutas Vedadas',
      'Ação Penal Eleitoral',
      'Recurso Eleitoral',
      'Mandado de Segurança Eleitoral',
      'Habeas Corpus Eleitoral'
    ],
    internacional: [
      'Ação de Homologação de Sentença Estrangeira',
      'Ação de Exequatur de Carta Rogatória',
      'Ação de Extradição',
      'Ação de Conflito de Jurisdição Internacional',
      'Ação de Arbitragem Internacional',
      'Ação de Reconhecimento de União Estável Internacional',
      'Ação de Alimentos Internacionais',
      'Ação de Sequestro Internacional de Menores',
      'Ação de Imunidade Diplomática',
      'Ação de Direitos Humanos'
    ],
    digital: [
      'Ação de Direito ao Esquecimento',
      'Ação de Vazamento de Dados',
      'Ação de Crimes Cibernéticos',
      'Ação de Violação de Privacidade Digital',
      'Ação de Marco Civil da Internet',
      'Ação LGPD',
      'Ação de E-commerce',
      'Ação de Contratos Eletrônicos',
      'Ação de Propriedade Intelectual Digital',
      'Ação de Responsabilidade de Provedores'
    ],
    saude: [
      'Ação de Fornecimento de Medicamentos',
      'Ação de Cirurgia pelo SUS',
      'Ação de Erro Médico',
      'Ação de Plano de Saúde',
      'Ação de Internação Compulsória',
      'Ação de Autorização de Tratamento',
      'Ação de Responsabilidade Hospitalar',
      'Ação de Vigilância Sanitária',
      'Ação de Direito do Paciente',
      'Ação de Saúde Mental'
    ],
    educacional: [
      'Ação de Acesso à Educação',
      'Ação de Responsabilidade de Instituição de Ensino',
      'Ação de Mensalidade Escolar',
      'Ação de Transferência Escolar',
      'Ação de Inclusão Educacional',
      'Ação de Educação Especial',
      'Ação de Ensino Superior',
      'Ação de Reconhecimento de Diploma',
      'Ação de Bolsa de Estudos',
      'Ação de Direitos do Estudante'
    ],
    desportivo: [
      'Ação de Contrato de Atleta',
      'Ação de Transferência Desportiva',
      'Ação de Direitos de Imagem Desportiva',
      'Ação de Responsabilidade de Clube',
      'Ação de Doping',
      'Ação de Arbitragem Desportiva',
      'Ação de Direitos Televisivos',
      'Ação de Patrocínio Desportivo',
      'Ação de Violência no Esporte',
      'Ação de Justiça Desportiva'
    ],
    autoral: [
      'Ação de Violação de Direitos Autorais',
      'Ação de Plágio',
      'Ação de Uso Indevido de Obra',
      'Ação de Direitos Conexos',
      'Ação de Domínio Público',
      'Ação de Licenciamento de Obra',
      'Ação de Royalties Autorais',
      'Ação de Direito Moral do Autor',
      'Ação de Cessão de Direitos Autorais',
      'Ação de Proteção de Software'
    ],
    propriedade_intelectual: [
      'Ação de Violação de Marca',
      'Ação de Violação de Patente',
      'Ação de Concorrência Desleal PI',
      'Ação de Desenho Industrial',
      'Ação de Indicação Geográfica',
      'Ação de Segredo Industrial',
      'Ação de Nulidade de Registro',
      'Ação de Caducidade de Marca',
      'Ação de Licenciamento de PI',
      'Ação de Transfer Technology'
    ],
    sucessoes: [
      'Ação de Inventário',
      'Ação de Arrolamento',
      'Ação de Partilha',
      'Ação de Sonegados',
      'Ação de Petição de Herança',
      'Ação de Indignidade',
      'Ação de Deserdação',
      'Ação de Nulidade de Testamento',
      'Ação de Colação',
      'Ação de Redução de Liberalidades'
    ],
    agrario: [
      'Ação de Desapropriação Rural',
      'Ação de Reforma Agrária',
      'Ação de Usucapião Rural',
      'Ação Possessória Rural',
      'Ação de ITR',
      'Ação de Módulo Rural',
      'Ação de Cadastro Rural',
      'Ação de Crédito Rural',
      'Ação de Seguro Rural',
      'Ação de Cooperativa Agrícola'
    ],
    maritimo: [
      'Ação de Avaria Marítima',
      'Ação de Salvamento Marítimo',
      'Ação de Seguro Marítimo',
      'Ação de Contrato de Afretamento',
      'Ação de Responsabilidade do Armador',
      'Ação de Poluição Marítima',
      'Ação de Trabalho Marítimo',
      'Ação de Praticagem',
      'Ação de Porto',
      'Ação de Navegação'
    ],
    aeronautico: [
      'Ação de Acidente Aeronáutico',
      'Ação de Responsabilidade da Companhia Aérea',
      'Ação de Seguro Aeronáutico',
      'Ação de Transporte Aéreo',
      'Ação de Trabalho Aeronáutico',
      'Ação de Infraestrutura Aeroportuária',
      'Ação de Regulação da Aviação',
      'Ação de Direitos do Passageiro',
      'Ação de Carga Aérea',
      'Ação de Aviação Geral'
    ],
    militar: [
      'Ação de Direitos do Militar',
      'Ação de Pensão Militar',
      'Ação de Reforma Militar',
      'Ação de Transferência Militar',
      'Ação de Disciplina Militar',
      'Ação de Justiça Militar',
      'Ação de Serviço Militar',
      'Ação de Veteranos',
      'Ação de Hierarquia Militar',
      'Ação de Crimes Militares'
    ],
    urbanistico: [
      'Ação de Zoneamento Urbano',
      'Ação de Licenciamento Urbanístico',
      'Ação de Parcelamento do Solo',
      'Ação de Regularização Fundiária',
      'Ação de Plano Diretor',
      'Ação de Operação Urbana',
      'Ação de Patrimônio Histórico',
      'Ação de Mobilidade Urbana',
      'Ação de Estatuto da Cidade',
      'Ação de Desapropriação Urbanística'
    ],
    financeiro: [
      'Ação de Mercado de Capitais',
      'Ação de Sistema Financeiro',
      'Ação de Lavagem de Dinheiro',
      'Ação de Crimes Financeiros',
      'Ação de Investimentos',
      'Ação de Fundos de Investimento',
      'Ação de Seguro Financeiro',
      'Ação de Previdência Privada',
      'Ação de Consórcio',
      'Ação de Câmbio'
    ],
    securitario: [
      'Ação de Seguro de Vida',
      'Ação de Seguro de Automóvel',
      'Ação de Seguro Residencial',
      'Ação de Seguro Empresarial',
      'Ação de Seguro de Responsabilidade Civil',
      'Ação de Resseguro',
      'Ação de Corretagem de Seguros',
      'Ação de Regulação de Sinistros',
      'Ação de Má-fé da Seguradora',
      'Ação de Seguro Agrícola'
    ],
    concorrencial: [
      'Ação de Cartel',
      'Ação de Abuso de Posição Dominante',
      'Ação de Concentração Econômica',
      'Ação de Práticas Restritivas',
      'Ação de Defesa da Concorrência',
      'Ação de Dumping',
      'Ação de Preços Predatórios',
      'Ação de Acordo de Leniência',
      'Ação de Livre Concorrência',
      'Ação de Mercado Relevante'
    ]
  };

  const tribunaisDisponiveis = [
    // Tribunais Superiores
    { codigo: 'STF', nome: 'Supremo Tribunal Federal', grupo: 'Superior' },
    { codigo: 'STJ', nome: 'Superior Tribunal de Justiça', grupo: 'Superior' },
    { codigo: 'TST', nome: 'Tribunal Superior do Trabalho', grupo: 'Superior' },
    { codigo: 'TSE', nome: 'Tribunal Superior Eleitoral', grupo: 'Superior' },
    { codigo: 'STM', nome: 'Superior Tribunal Militar', grupo: 'Superior' },

    // Tribunais Regionais Federais
    { codigo: 'TRF1', nome: 'Tribunal Regional Federal da 1ª Região', grupo: 'Federal' },
    { codigo: 'TRF2', nome: 'Tribunal Regional Federal da 2ª Região', grupo: 'Federal' },
    { codigo: 'TRF3', nome: 'Tribunal Regional Federal da 3ª Região', grupo: 'Federal' },
    { codigo: 'TRF4', nome: 'Tribunal Regional Federal da 4ª Região', grupo: 'Federal' },
    { codigo: 'TRF5', nome: 'Tribunal Regional Federal da 5ª Região', grupo: 'Federal' },
    { codigo: 'TRF6', nome: 'Tribunal Regional Federal da 6ª Região', grupo: 'Federal' },

    // Tribunais Regionais do Trabalho
    { codigo: 'TRT1', nome: 'Tribunal Regional do Trabalho da 1ª Região (RJ)', grupo: 'Trabalhista' },
    { codigo: 'TRT2', nome: 'Tribunal Regional do Trabalho da 2ª Região (SP)', grupo: 'Trabalhista' },
    { codigo: 'TRT3', nome: 'Tribunal Regional do Trabalho da 3ª Região (MG)', grupo: 'Trabalhista' },
    { codigo: 'TRT4', nome: 'Tribunal Regional do Trabalho da 4ª Região (RS)', grupo: 'Trabalhista' },
    { codigo: 'TRT5', nome: 'Tribunal Regional do Trabalho da 5ª Região (BA)', grupo: 'Trabalhista' },
    { codigo: 'TRT6', nome: 'Tribunal Regional do Trabalho da 6ª Região (PE)', grupo: 'Trabalhista' },
    { codigo: 'TRT7', nome: 'Tribunal Regional do Trabalho da 7ª Região (CE)', grupo: 'Trabalhista' },
    { codigo: 'TRT8', nome: 'Tribunal Regional do Trabalho da 8ª Região (PA/AP)', grupo: 'Trabalhista' },
    { codigo: 'TRT9', nome: 'Tribunal Regional do Trabalho da 9ª Região (PR)', grupo: 'Trabalhista' },
    { codigo: 'TRT10', nome: 'Tribunal Regional do Trabalho da 10ª Região (DF/TO)', grupo: 'Trabalhista' },
    { codigo: 'TRT11', nome: 'Tribunal Regional do Trabalho da 11ª Região (AM/RR)', grupo: 'Trabalhista' },
    { codigo: 'TRT12', nome: 'Tribunal Regional do Trabalho da 12ª Região (SC)', grupo: 'Trabalhista' },
    { codigo: 'TRT13', nome: 'Tribunal Regional do Trabalho da 13ª Região (PB)', grupo: 'Trabalhista' },
    { codigo: 'TRT14', nome: 'Tribunal Regional do Trabalho da 14ª Região (RO/AC)', grupo: 'Trabalhista' },
    { codigo: 'TRT15', nome: 'Tribunal Regional do Trabalho da 15ª Região (Interior SP)', grupo: 'Trabalhista' },
    { codigo: 'TRT16', nome: 'Tribunal Regional do Trabalho da 16ª Região (MA)', grupo: 'Trabalhista' },
    { codigo: 'TRT17', nome: 'Tribunal Regional do Trabalho da 17ª Região (ES)', grupo: 'Trabalhista' },
    { codigo: 'TRT18', nome: 'Tribunal Regional do Trabalho da 18ª Região (GO)', grupo: 'Trabalhista' },
    { codigo: 'TRT19', nome: 'Tribunal Regional do Trabalho da 19ª Região (AL)', grupo: 'Trabalhista' },
    { codigo: 'TRT20', nome: 'Tribunal Regional do Trabalho da 20ª Região (SE)', grupo: 'Trabalhista' },
    { codigo: 'TRT21', nome: 'Tribunal Regional do Trabalho da 21ª Região (RN)', grupo: 'Trabalhista' },
    { codigo: 'TRT22', nome: 'Tribunal Regional do Trabalho da 22ª Região (PI)', grupo: 'Trabalhista' },
    { codigo: 'TRT23', nome: 'Tribunal Regional do Trabalho da 23ª Região (MT)', grupo: 'Trabalhista' },
    { codigo: 'TRT24', nome: 'Tribunal Regional do Trabalho da 24ª Região (MS)', grupo: 'Trabalhista' },

    // Tribunais Regionais Eleitorais
    { codigo: 'TREAC', nome: 'Tribunal Regional Eleitoral do Acre', grupo: 'Eleitoral' },
    { codigo: 'TREAL', nome: 'Tribunal Regional Eleitoral de Alagoas', grupo: 'Eleitoral' },
    { codigo: 'TREAP', nome: 'Tribunal Regional Eleitoral do Amapá', grupo: 'Eleitoral' },
    { codigo: 'TREAM', nome: 'Tribunal Regional Eleitoral do Amazonas', grupo: 'Eleitoral' },
    { codigo: 'TREBA', nome: 'Tribunal Regional Eleitoral da Bahia', grupo: 'Eleitoral' },
    { codigo: 'TRECE', nome: 'Tribunal Regional Eleitoral do Ceará', grupo: 'Eleitoral' },
    { codigo: 'TREDF', nome: 'Tribunal Regional Eleitoral do Distrito Federal', grupo: 'Eleitoral' },
    { codigo: 'TREES', nome: 'Tribunal Regional Eleitoral do Espírito Santo', grupo: 'Eleitoral' },
    { codigo: 'TREGO', nome: 'Tribunal Regional Eleitoral de Goiás', grupo: 'Eleitoral' },
    { codigo: 'TREMA', nome: 'Tribunal Regional Eleitoral do Maranhão', grupo: 'Eleitoral' },
    { codigo: 'TREMT', nome: 'Tribunal Regional Eleitoral de Mato Grosso', grupo: 'Eleitoral' },
    { codigo: 'TREMS', nome: 'Tribunal Regional Eleitoral de Mato Grosso do Sul', grupo: 'Eleitoral' },
    { codigo: 'TREMG', nome: 'Tribunal Regional Eleitoral de Minas Gerais', grupo: 'Eleitoral' },
    { codigo: 'TREPA', nome: 'Tribunal Regional Eleitoral do Pará', grupo: 'Eleitoral' },
    { codigo: 'TREPB', nome: 'Tribunal Regional Eleitoral da Paraíba', grupo: 'Eleitoral' },
    { codigo: 'TREPR', nome: 'Tribunal Regional Eleitoral do Paraná', grupo: 'Eleitoral' },
    { codigo: 'TREPE', nome: 'Tribunal Regional Eleitoral de Pernambuco', grupo: 'Eleitoral' },
    { codigo: 'TREPI', nome: 'Tribunal Regional Eleitoral do Piauí', grupo: 'Eleitoral' },
    { codigo: 'TRERJ', nome: 'Tribunal Regional Eleitoral do Rio de Janeiro', grupo: 'Eleitoral' },
    { codigo: 'TRERN', nome: 'Tribunal Regional Eleitoral do Rio Grande do Norte', grupo: 'Eleitoral' },
    { codigo: 'TRERS', nome: 'Tribunal Regional Eleitoral do Rio Grande do Sul', grupo: 'Eleitoral' },
    { codigo: 'TRERO', nome: 'Tribunal Regional Eleitoral de Rondônia', grupo: 'Eleitoral' },
    { codigo: 'TRERR', nome: 'Tribunal Regional Eleitoral de Roraima', grupo: 'Eleitoral' },
    { codigo: 'TRESC', nome: 'Tribunal Regional Eleitoral de Santa Catarina', grupo: 'Eleitoral' },
    { codigo: 'TRESP', nome: 'Tribunal Regional Eleitoral de São Paulo', grupo: 'Eleitoral' },
    { codigo: 'TRESE', nome: 'Tribunal Regional Eleitoral de Sergipe', grupo: 'Eleitoral' },
    { codigo: 'TRETO', nome: 'Tribunal Regional Eleitoral do Tocantins', grupo: 'Eleitoral' },

    // Tribunais de Justiça Estaduais
    { codigo: 'TJAC', nome: 'Tribunal de Justiça do Acre', grupo: 'Estadual' },
    { codigo: 'TJAL', nome: 'Tribunal de Justiça de Alagoas', grupo: 'Estadual' },
    { codigo: 'TJAP', nome: 'Tribunal de Justiça do Amapá', grupo: 'Estadual' },
    { codigo: 'TJAM', nome: 'Tribunal de Justiça do Amazonas', grupo: 'Estadual' },
    { codigo: 'TJBA', nome: 'Tribunal de Justiça da Bahia', grupo: 'Estadual' },
    { codigo: 'TJCE', nome: 'Tribunal de Justiça do Ceará', grupo: 'Estadual' },
    { codigo: 'TJDF', nome: 'Tribunal de Justiça do Distrito Federal e Territórios', grupo: 'Estadual' },
    { codigo: 'TJES', nome: 'Tribunal de Justiça do Espírito Santo', grupo: 'Estadual' },
    { codigo: 'TJGO', nome: 'Tribunal de Justiça de Goiás', grupo: 'Estadual' },
    { codigo: 'TJMA', nome: 'Tribunal de Justiça do Maranhão', grupo: 'Estadual' },
    { codigo: 'TJMT', nome: 'Tribunal de Justiça de Mato Grosso', grupo: 'Estadual' },
    { codigo: 'TJMS', nome: 'Tribunal de Justiça de Mato Grosso do Sul', grupo: 'Estadual' },
    { codigo: 'TJMG', nome: 'Tribunal de Justiça de Minas Gerais', grupo: 'Estadual' },
    { codigo: 'TJPA', nome: 'Tribunal de Justiça do Pará', grupo: 'Estadual' },
    { codigo: 'TJPB', nome: 'Tribunal de Justiça da Paraíba', grupo: 'Estadual' },
    { codigo: 'TJPR', nome: 'Tribunal de Justiça do Paraná', grupo: 'Estadual' },
    { codigo: 'TJPE', nome: 'Tribunal de Justiça de Pernambuco', grupo: 'Estadual' },
    { codigo: 'TJPI', nome: 'Tribunal de Justiça do Piauí', grupo: 'Estadual' },
    { codigo: 'TJRJ', nome: 'Tribunal de Justiça do Rio de Janeiro', grupo: 'Estadual' },
    { codigo: 'TJRN', nome: 'Tribunal de Justiça do Rio Grande do Norte', grupo: 'Estadual' },
    { codigo: 'TJRS', nome: 'Tribunal de Justiça do Rio Grande do Sul', grupo: 'Estadual' },
    { codigo: 'TJRO', nome: 'Tribunal de Justiça de Rondônia', grupo: 'Estadual' },
    { codigo: 'TJRR', nome: 'Tribunal de Justiça de Roraima', grupo: 'Estadual' },
    { codigo: 'TJSC', nome: 'Tribunal de Justiça de Santa Catarina', grupo: 'Estadual' },
    { codigo: 'TJSP', nome: 'Tribunal de Justiça de São Paulo', grupo: 'Estadual' },
    { codigo: 'TJSE', nome: 'Tribunal de Justiça de Sergipe', grupo: 'Estadual' },
    { codigo: 'TJTO', nome: 'Tribunal de Justiça do Tocantins', grupo: 'Estadual' },

    // Tribunais de Justiça Militar Estaduais
    { codigo: 'TJMMG', nome: 'Tribunal de Justiça Militar de Minas Gerais', grupo: 'Militar' },
    { codigo: 'TJMRS', nome: 'Tribunal de Justiça Militar do Rio Grande do Sul', grupo: 'Militar' },
    { codigo: 'TJMSP', nome: 'Tribunal de Justiça Militar de São Paulo', grupo: 'Militar' },

    // Tribunais de Contas
    { codigo: 'TCU', nome: 'Tribunal de Contas da União', grupo: 'Contas' },
    { codigo: 'TCDF', nome: 'Tribunal de Contas do Distrito Federal', grupo: 'Contas' },
    { codigo: 'TCEAC', nome: 'Tribunal de Contas do Acre', grupo: 'Contas' },
    { codigo: 'TCEAL', nome: 'Tribunal de Contas de Alagoas', grupo: 'Contas' },
    { codigo: 'TCEAP', nome: 'Tribunal de Contas do Amapá', grupo: 'Contas' },
    { codigo: 'TCEAM', nome: 'Tribunal de Contas do Amazonas', grupo: 'Contas' },
    { codigo: 'TCEBA', nome: 'Tribunal de Contas da Bahia', grupo: 'Contas' },
    { codigo: 'TCECE', nome: 'Tribunal de Contas do Ceará', grupo: 'Contas' },
    { codigo: 'TCEES', nome: 'Tribunal de Contas do Espírito Santo', grupo: 'Contas' },
    { codigo: 'TCEGO', nome: 'Tribunal de Contas de Goiás', grupo: 'Contas' },
    { codigo: 'TCEMA', nome: 'Tribunal de Contas do Maranhão', grupo: 'Contas' },
    { codigo: 'TCEMT', nome: 'Tribunal de Contas de Mato Grosso', grupo: 'Contas' },
    { codigo: 'TCEMS', nome: 'Tribunal de Contas de Mato Grosso do Sul', grupo: 'Contas' },
    { codigo: 'TCEMG', nome: 'Tribunal de Contas de Minas Gerais', grupo: 'Contas' },
    { codigo: 'TCEPA', nome: 'Tribunal de Contas do Pará', grupo: 'Contas' },
    { codigo: 'TCEPB', nome: 'Tribunal de Contas da Paraíba', grupo: 'Contas' },
    { codigo: 'TCEPR', nome: 'Tribunal de Contas do Paraná', grupo: 'Contas' },
    { codigo: 'TCEPE', nome: 'Tribunal de Contas de Pernambuco', grupo: 'Contas' },
    { codigo: 'TCEPI', nome: 'Tribunal de Contas do Piauí', grupo: 'Contas' },
    { codigo: 'TCERJ', nome: 'Tribunal de Contas do Rio de Janeiro', grupo: 'Contas' },
    { codigo: 'TCERN', nome: 'Tribunal de Contas do Rio Grande do Norte', grupo: 'Contas' },
    { codigo: 'TCERS', nome: 'Tribunal de Contas do Rio Grande do Sul', grupo: 'Contas' },
    { codigo: 'TCERO', nome: 'Tribunal de Contas de Rondônia', grupo: 'Contas' },
    { codigo: 'TCERR', nome: 'Tribunal de Contas de Roraima', grupo: 'Contas' },
    { codigo: 'TCESC', nome: 'Tribunal de Contas de Santa Catarina', grupo: 'Contas' },
    { codigo: 'TCESP', nome: 'Tribunal de Contas de São Paulo', grupo: 'Contas' },
    { codigo: 'TCESE', nome: 'Tribunal de Contas de Sergipe', grupo: 'Contas' },
    { codigo: 'TCETO', nome: 'Tribunal de Contas do Tocantins', grupo: 'Contas' }
  ];

  const topicosJuridicos = {
    consumidor: [
      {
        id: '1',
        titulo: 'Da responsabilidade objetiva do fornecedor de serviços',
        conteudo: 'Conforme o artigo 14 do Código de Defesa do Consumidor, a responsabilidade pela reparação dos danos causados aos consumidores por defeitos relativos à prestação dos serviços é objetiva, cabendo à Ré a obrigação de responder pelos prejuízos apontados pelo Autor devido à falha na prestação do serviço.',
        baseLegal: 'Art. 14, CDC',
        categoria: 'fundamentacao',
        jurisprudencias: [
          {
            id: '1',
            tribunal: 'TJSP',
            numero_processo: '1000001-23.2024.8.26.0100',
            ementa_resumo: 'RESPONSABILIDADE CIVIL. CONSUMIDOR. FALHA NA PRESTAÇÃO DO SERVIÇO...',
            relevancia: 0.95
          }
        ],
        obrigatorio: true
      },
      {
        id: '2',
        titulo: 'Do dever de informação e comunicação',
        conteudo: 'Nos termos do artigo 6º, inciso III, do Código de Defesa do Consumidor, é direito básico do consumidor ser informado de maneira adequada e clara sobre os diferentes produtos e serviços.',
        baseLegal: 'Art. 6º, III, CDC',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: 'cons_dano_moral',
        titulo: 'Dos danos morais',
        conteudo: 'O dano moral decorre da própria violação do direito do consumidor, configurando dano in re ipsa, dispensando prova do prejuízo efetivo.',
        baseLegal: 'Art. 6º, VI, CDC',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'cons_inversao_onus',
        titulo: 'Da inversão do ônus da prova',
        conteudo: 'Presentes a verossimilhança das alegações e a hipossuficiência do consumidor, impõe-se a inversão do ônus da prova.',
        baseLegal: 'Art. 6º, VIII, CDC',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '3',
        titulo: 'Da gratuidade de justiça',
        conteudo: 'O Requerente não possui condições financeiras para arcar com as custas processuais, sem prejuízo do seu sustento e de sua família, razão pela qual postula litigar sob o pálio da gratuidade judiciária.',
        baseLegal: 'Art. 98, CPC',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '4',
        titulo: 'Da prioridade de tramitação',
        conteudo: 'O Autor requer seja concedida e anotada a prioridade de tramitação aos autos, por se tratar de pessoa idosa, conforme documentação anexa.',
        baseLegal: 'Art. 1.048, CPC',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      }
    ],
    civil: [
      {
        id: '5',
        titulo: 'Da responsabilidade civil por danos materiais e morais',
        conteudo: 'Conforme os artigos 186 e 927 do Código Civil, aquele que, por ação ou omissão voluntária, negligência ou imprudência, violar direito e causar dano a outrem, ainda que exclusivamente moral, comete ato ilícito e fica obrigado a repará-lo.',
        baseLegal: 'Arts. 186 e 927, CC',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: '6',
        titulo: 'Do nexo de causalidade',
        conteudo: 'Para configuração da responsabilidade civil, é necessário demonstrar o nexo de causalidade entre a conduta do agente e o dano sofrido, conforme jurisprudência consolidada dos tribunais superiores.',
        baseLegal: 'Arts. 186 e 403, CC',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: '7',
        titulo: 'Da quantificação dos danos',
        conteudo: 'Os danos materiais devem ser comprovados documentalmente, enquanto os danos morais são presumidos em razão da lesão sofrida, devendo ser fixados com base nos princípios da razoabilidade e proporcionalidade.',
        baseLegal: 'Art. 944, CC',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'civ_lucros_cessantes',
        titulo: 'Dos lucros cessantes',
        conteudo: 'Além dos danos emergentes, deve-se considerar os lucros que razoavelmente deixaram de ser auferidos em razão do evento danoso.',
        baseLegal: 'Art. 402, CC',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'civ_juros_correcao',
        titulo: 'Dos juros e correção monetária',
        conteudo: 'Os valores devidos devem ser acrescidos de correção monetária desde o evento danoso e juros moratórios desde a citação.',
        baseLegal: 'Arts. 389 e 405, CC',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '8',
        titulo: 'Da gratuidade de justiça',
        conteudo: 'O Requerente não possui condições financeiras para arcar com as custas processuais, sem prejuízo do seu sustento e de sua família, razão pela qual postula litigar sob o pálio da gratuidade judiciária.',
        baseLegal: 'Art. 98, CPC',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      }
    ],
    trabalhista: [
      {
        id: '9',
        titulo: 'Da relação de emprego',
        conteudo: 'Restou configurada a relação de emprego entre as partes, nos termos dos artigos 2º e 3º da CLT, tendo em vista a presença dos elementos: pessoalidade, não eventualidade, onerosidade e subordinação.',
        baseLegal: 'Arts. 2º e 3º, CLT',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: '10',
        titulo: 'Das horas extras e adicional noturno',
        conteudo: 'O trabalho prestado além da jornada normal deve ser remunerado com adicional de, no mínimo, 50%, conforme artigo 7º, XVI da Constituição Federal e artigo 59 da CLT.',
        baseLegal: 'Art. 7º, XVI, CF e Art. 59, CLT',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'trab_verbas_rescisorias',
        titulo: 'Das verbas rescisórias',
        conteudo: 'São devidas as verbas rescisórias como saldo de salário, aviso prévio, férias + 1/3, 13º salário proporcional e multa de 40% do FGTS.',
        baseLegal: 'Arts. 477 e 487, CLT',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'trab_dano_moral',
        titulo: 'Do dano moral trabalhista',
        conteudo: 'O assédio moral, tratamento vexatório ou condições degradantes de trabalho configuram dano moral indenizável no âmbito laboral.',
        baseLegal: 'Art. 223-C, CLT',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '11',
        titulo: 'Do FGTS e contribuições previdenciárias',
        conteudo: 'O empregador tem o dever de efetuar os depósitos do FGTS e recolher as contribuições previdenciárias durante todo o período do contrato de trabalho, conforme legislação específica.',
        baseLegal: 'Lei 8.036/90 e Lei 8.212/91',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '12',
        titulo: 'Da gratuidade de justiça',
        conteudo: 'O Requerente não possui condições financeiras para arcar com as custas processuais, sendo beneficiário da justiça gratuita, nos termos da Lei 1060/50.',
        baseLegal: 'Art. 790, §3º, CLT',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      }
    ],
    penal: [
      // FUNDAMENTAÇÃO
      {
        id: 'pen_tipificacao',
        titulo: 'Da tipificação penal',
        conteudo: 'Os fatos narrados se amoldam perfeitamente ao tipo penal descrito no Código Penal, restando configurados todos os elementos objetivos e subjetivos do delito.',
        baseLegal: 'Arts. 13 e 18, CP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: 'pen_materialidade',
        titulo: 'Da materialidade e autoria',
        conteudo: 'A materialidade delitiva resta comprovada pelos elementos de prova colacionados aos autos, bem como a autoria é certa e induvidosa, conforme testemunhas e documentos apresentados.',
        baseLegal: 'Art. 156, CPP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: 'pen_dolo',
        titulo: 'Do dolo e culpabilidade',
        conteudo: 'O agente atuou com dolo, ou seja, com consciência e vontade de praticar a conduta descrita no tipo penal, sendo plenamente imputável.',
        baseLegal: 'Art. 18, I, CP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_qualificadoras',
        titulo: 'Das qualificadoras e agravantes',
        conteudo: 'Incidem no caso as qualificadoras/agravantes previstas na lei, que aumentam a reprovabilidade da conduta.',
        baseLegal: 'Arts. 61 e 62, CP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      // PROCESSUAL - QUEIXA-CRIME
      {
        id: 'pen_queixa_crime',
        titulo: 'Da queixa-crime (ação penal privada)',
        conteudo: 'Tratando-se de crime de ação penal privada, a vítima oferece a presente queixa-crime dentro do prazo decadencial de 6 meses contados do conhecimento da autoria.',
        baseLegal: 'Arts. 30, 38 e 41, CPP',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_queixa_legitimidade',
        titulo: 'Da legitimidade do querelante',
        conteudo: 'O querelante é pessoa legitimada para propor a ação penal privada, sendo o ofendido direto pela conduta criminosa.',
        baseLegal: 'Art. 30, CPP',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_queixa_prazo',
        titulo: 'Do prazo decadencial',
        conteudo: 'A presente queixa-crime é tempestiva, sendo oferecida dentro do prazo de 6 meses a contar da data em que o querelante tomou conhecimento da autoria do crime.',
        baseLegal: 'Art. 38, CPP',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      },
      // PROCESSUAL - REPRESENTAÇÃO
      {
        id: 'pen_representacao',
        titulo: 'Da representação criminal',
        conteudo: 'A vítima manifesta expressamente seu interesse na persecução penal, oferecendo representação contra o autor do fato, condição de procedibilidade nos crimes de ação pública condicionada.',
        baseLegal: 'Arts. 24 e 25, CPP',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_representacao_prazo',
        titulo: 'Do prazo para representação',
        conteudo: 'A representação é oferecida dentro do prazo decadencial de 6 meses, contados do conhecimento da autoria.',
        baseLegal: 'Art. 38, CPP',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      },
      // CRIMES ESPECÍFICOS
      {
        id: 'pen_crimes_honra',
        titulo: 'Dos crimes contra a honra',
        conteudo: 'A conduta do querelado configura crime contra a honra (calúnia, difamação ou injúria), ofendendo a dignidade ou decoro do querelante.',
        baseLegal: 'Arts. 138, 139 e 140, CP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_ameaca',
        titulo: 'Do crime de ameaça',
        conteudo: 'O querelado ameaçou o querelante de mal injusto e grave, por palavra, escrito, gesto ou qualquer outro meio simbólico.',
        baseLegal: 'Art. 147, CP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_lesao_corporal',
        titulo: 'Da lesão corporal',
        conteudo: 'O réu ofendeu a integridade corporal ou a saúde da vítima, causando-lhe lesões comprovadas por laudo pericial.',
        baseLegal: 'Art. 129, CP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_estelionato',
        titulo: 'Do estelionato',
        conteudo: 'O agente obteve, para si ou para outrem, vantagem ilícita, em prejuízo alheio, induzindo ou mantendo alguém em erro, mediante artifício, ardil ou qualquer outro meio fraudulento.',
        baseLegal: 'Art. 171, CP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_apropriacao_indebita',
        titulo: 'Da apropriação indébita',
        conteudo: 'O agente apropriou-se de coisa alheia móvel, de que tinha a posse ou a detenção.',
        baseLegal: 'Art. 168, CP',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      // VIOLÊNCIA DOMÉSTICA
      {
        id: 'pen_violencia_domestica',
        titulo: 'Da violência doméstica e familiar',
        conteudo: 'Os fatos ocorreram no âmbito da unidade doméstica ou relação íntima de afeto, configurando violência doméstica nos termos da Lei Maria da Penha.',
        baseLegal: 'Art. 5º, Lei 11.340/06',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_medidas_protetivas',
        titulo: 'Das medidas protetivas de urgência',
        conteudo: 'Requer-se a concessão de medidas protetivas de urgência em favor da vítima, como afastamento do lar, proibição de aproximação e contato.',
        baseLegal: 'Arts. 22 e 23, Lei 11.340/06',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      // PEDIDOS
      {
        id: 'pen_reparacao_danos',
        titulo: 'Da reparação de danos',
        conteudo: 'O crime causou prejuízos materiais e morais à vítima, devendo o réu responder civilmente pelos danos causados.',
        baseLegal: 'Art. 91, I, CP e Art. 387, IV, CPP',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_prisao_preventiva',
        titulo: 'Da prisão preventiva',
        conteudo: 'Presentes os requisitos legais (garantia da ordem pública, conveniência da instrução criminal ou assegurar aplicação da lei penal), requer-se a decretação da prisão preventiva.',
        baseLegal: 'Art. 312, CPP',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_busca_apreensao',
        titulo: 'Da busca e apreensão',
        conteudo: 'Requer-se a expedição de mandado de busca e apreensão para localizar e apreender objetos relacionados ao crime.',
        baseLegal: 'Arts. 240 a 250, CPP',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'pen_sequestro_bens',
        titulo: 'Do sequestro de bens',
        conteudo: 'Requer-se o sequestro dos bens imóveis e/ou móveis adquiridos com os proventos da infração.',
        baseLegal: 'Arts. 125 a 133, CPP',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      }
    ],
    familia: [
      {
        id: '17',
        titulo: 'Do melhor interesse da criança',
        conteudo: 'Todas as decisões relacionadas à criança devem ter como parâmetro seu melhor interesse, conforme artigo 227 da Constituição Federal e Estatuto da Criança e do Adolescente.',
        baseLegal: 'Art. 227, CF e Art. 1.583, CC',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: '18',
        titulo: 'Da guarda compartilhada',
        conteudo: 'A guarda compartilhada deve ser aplicada sempre que possível, conforme Lei 13.058/14, garantindo a convivência da criança com ambos os genitores.',
        baseLegal: 'Art. 1.584, §2º, CC',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'fam_divorcio',
        titulo: 'Do divórcio consensual ou litigioso',
        conteudo: 'O divórcio é direito potestativo do cônjuge, podendo ser concedido independentemente de culpa ou prazo de separação.',
        baseLegal: 'Art. 226, §6º, CF',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'fam_partilha',
        titulo: 'Da partilha de bens',
        conteudo: 'Os bens adquiridos onerosamente na constância do casamento (comunhão parcial) devem ser partilhados igualmente entre os cônjuges.',
        baseLegal: 'Art. 1.658, CC',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '19',
        titulo: 'Dos alimentos',
        conteudo: 'O dever de prestar alimentos decorre do poder familiar e deve ser fixado considerando as necessidades do alimentando e as possibilidades do alimentante (binômio necessidade x possibilidade).',
        baseLegal: 'Art. 1.694, CC',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'fam_alienacao',
        titulo: 'Da alienação parental',
        conteudo: 'A interferência na formação psicológica da criança, promovida por genitor, que cause repúdio ao outro genitor, configura alienação parental.',
        baseLegal: 'Art. 2º, Lei 12.318/10',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '20',
        titulo: 'Da gratuidade de justiça',
        conteudo: 'O Requerente não possui condições financeiras para arcar com as custas processuais, razão pela qual postula os benefícios da justiça gratuita.',
        baseLegal: 'Art. 98, CPC',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      }
    ],
    // Tópicos genéricos para outras áreas
    administrativo: [
      {
        id: '21',
        titulo: 'Dos princípios da administração pública',
        conteudo: 'A administração pública deve observar os princípios da legalidade, impessoalidade, moralidade, publicidade e eficiência, conforme artigo 37 da Constituição Federal.',
        baseLegal: 'Art. 37, caput, CF',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: 'adm_mandado_seguranca',
        titulo: 'Do mandado de segurança',
        conteudo: 'O mandado de segurança é cabível para proteger direito líquido e certo, não amparado por habeas corpus ou habeas data, lesado ou ameaçado por ato de autoridade.',
        baseLegal: 'Art. 5º, LXIX, CF e Lei 12.016/09',
        categoria: 'processual',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'adm_ato_vinculado',
        titulo: 'Da vinculação do ato administrativo',
        conteudo: 'O ato administrativo vinculado não comporta juízo de oportunidade e conveniência, devendo a autoridade praticá-lo quando presentes os requisitos legais.',
        baseLegal: 'Art. 2º, parágrafo único, Lei 9.784/99',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '22',
        titulo: 'Do direito líquido e certo',
        conteudo: 'O direito pleiteado é líquido e certo, não havendo necessidade de dilação probatória, sendo cabível a concessão da segurança.',
        baseLegal: 'Art. 1º, Lei 12.016/09',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      }
    ],
    tributario: [
      {
        id: '23',
        titulo: 'Da legalidade tributária',
        conteudo: 'Nos termos do artigo 150, I da Constituição Federal, é vedado exigir ou aumentar tributo sem lei que o estabeleça, devendo ser observado o princípio da legalidade.',
        baseLegal: 'Art. 150, I, CF',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: true
      },
      {
        id: 'trib_anterioridade',
        titulo: 'Do princípio da anterioridade',
        conteudo: 'É vedada a cobrança de tributos no mesmo exercício financeiro ou antes de 90 dias da publicação da lei que os instituiu ou aumentou.',
        baseLegal: 'Art. 150, III, b e c, CF',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'trib_base_calculo',
        titulo: 'Da base de cálculo e alíquota',
        conteudo: 'A base de cálculo utilizada pela Fazenda Pública está em desacordo com a previsão legal, resultando em cobrança indevida.',
        baseLegal: 'Art. 97, IV, CTN',
        categoria: 'fundamentacao',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: '24',
        titulo: 'Da repetição de indébito',
        conteudo: 'O contribuinte tem direito à restituição dos valores pagos indevidamente, acrescidos de correção monetária e juros, conforme artigo 165 do CTN.',
        baseLegal: 'Art. 165, CTN',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      },
      {
        id: 'trib_compensacao',
        titulo: 'Da compensação tributária',
        conteudo: 'O contribuinte tem direito à compensação dos valores pagos indevidamente com débitos vincendos da mesma espécie.',
        baseLegal: 'Art. 170, CTN',
        categoria: 'pedido',
        jurisprudencias: [],
        obrigatorio: false
      }
    ]
  };

  // Mapeamento de categorias para exibição
  const categoriaLabels: Record<string, { label: string; color: 'primary' | 'secondary' | 'success' | 'warning' | 'info' }> = {
    fundamentacao: { label: 'Fundamentação', color: 'primary' },
    pedido: { label: 'Pedido', color: 'success' },
    processual: { label: 'Processual', color: 'info' }
  };

  // Estrutura de pedidos por área do direito
  const pedidosPorArea: Record<string, Array<{
    id: string;
    titulo: string;
    descricao: string;
    baseLegal: string;
    categoria: 'principal' | 'acessorio' | 'processual';
    permiteValor: boolean;
    sugestaoValor?: string;
  }>> = {
    consumidor: [
      { id: 'cons_rescisao', titulo: 'Rescisão contratual', descricao: 'Declaração de rescisão do contrato com devolução de valores pagos', baseLegal: 'Art. 18 e 35, CDC', categoria: 'principal', permiteValor: true },
      { id: 'cons_restituicao', titulo: 'Restituição de valores', descricao: 'Devolução em dobro dos valores pagos indevidamente', baseLegal: 'Art. 42, parágrafo único, CDC', categoria: 'principal', permiteValor: true },
      { id: 'cons_dano_moral', titulo: 'Danos morais', descricao: 'Indenização por danos morais decorrentes da violação dos direitos do consumidor', baseLegal: 'Art. 6º, VI, CDC', categoria: 'principal', permiteValor: true, sugestaoValor: 'R$ 5.000,00 a R$ 20.000,00' },
      { id: 'cons_dano_material', titulo: 'Danos materiais', descricao: 'Indenização pelos prejuízos materiais efetivamente sofridos', baseLegal: 'Art. 6º, VI, CDC', categoria: 'principal', permiteValor: true },
      { id: 'cons_obrigacao_fazer', titulo: 'Obrigação de fazer', descricao: 'Cumprimento forçado da obrigação contratual', baseLegal: 'Art. 84, CDC', categoria: 'principal', permiteValor: false },
      { id: 'cons_tutela_antec', titulo: 'Tutela de urgência', descricao: 'Medida liminar para cessar imediatamente a prática abusiva', baseLegal: 'Art. 84, §3º, CDC', categoria: 'acessorio', permiteValor: false },
      { id: 'cons_justica_gratuita', titulo: 'Justiça gratuita', descricao: 'Concessão dos benefícios da justiça gratuita', baseLegal: 'Art. 98, CPC', categoria: 'processual', permiteValor: false },
      { id: 'cons_inversao', titulo: 'Inversão do ônus da prova', descricao: 'Inversão do ônus probatório em favor do consumidor', baseLegal: 'Art. 6º, VIII, CDC', categoria: 'processual', permiteValor: false },
    ],
    civil: [
      { id: 'civ_indenizacao_moral', titulo: 'Indenização por danos morais', descricao: 'Reparação por danos extrapatrimoniais sofridos', baseLegal: 'Arts. 186 e 927, CC', categoria: 'principal', permiteValor: true, sugestaoValor: 'R$ 5.000,00 a R$ 50.000,00' },
      { id: 'civ_indenizacao_material', titulo: 'Indenização por danos materiais', descricao: 'Reparação pelos prejuízos patrimoniais causados', baseLegal: 'Art. 402, CC', categoria: 'principal', permiteValor: true },
      { id: 'civ_lucros_cessantes', titulo: 'Lucros cessantes', descricao: 'Indenização pelo que razoavelmente deixou de lucrar', baseLegal: 'Art. 402, CC', categoria: 'principal', permiteValor: true },
      { id: 'civ_obrigacao_fazer', titulo: 'Obrigação de fazer/não fazer', descricao: 'Cumprimento de obrigação específica', baseLegal: 'Art. 497, CPC', categoria: 'principal', permiteValor: false },
      { id: 'civ_resolucao_contrato', titulo: 'Resolução do contrato', descricao: 'Extinção do contrato por inadimplemento', baseLegal: 'Art. 475, CC', categoria: 'principal', permiteValor: false },
      { id: 'civ_juros_correcao', titulo: 'Juros e correção monetária', descricao: 'Incidência de juros moratórios e atualização monetária', baseLegal: 'Arts. 389 e 405, CC', categoria: 'acessorio', permiteValor: false },
      { id: 'civ_honorarios', titulo: 'Honorários advocatícios', descricao: 'Condenação em honorários sucumbenciais', baseLegal: 'Art. 85, CPC', categoria: 'acessorio', permiteValor: false },
      { id: 'civ_justica_gratuita', titulo: 'Justiça gratuita', descricao: 'Concessão dos benefícios da justiça gratuita', baseLegal: 'Art. 98, CPC', categoria: 'processual', permiteValor: false },
    ],
    trabalhista: [
      { id: 'trab_vinculo', titulo: 'Reconhecimento de vínculo', descricao: 'Reconhecimento da relação de emprego e anotação na CTPS', baseLegal: 'Arts. 2º e 3º, CLT', categoria: 'principal', permiteValor: false },
      { id: 'trab_verbas_rescisorias', titulo: 'Verbas rescisórias', descricao: 'Saldo de salário, aviso prévio, férias + 1/3, 13º proporcional', baseLegal: 'Arts. 477 e 487, CLT', categoria: 'principal', permiteValor: true },
      { id: 'trab_fgts_multa', titulo: 'FGTS + multa 40%', descricao: 'Depósitos de FGTS de todo o período e multa rescisória', baseLegal: 'Lei 8.036/90', categoria: 'principal', permiteValor: true },
      { id: 'trab_horas_extras', titulo: 'Horas extras', descricao: 'Pagamento de horas extras com adicional de 50% ou 100%', baseLegal: 'Art. 59, CLT', categoria: 'principal', permiteValor: true },
      { id: 'trab_adicional_noturno', titulo: 'Adicional noturno', descricao: 'Adicional de 20% sobre a hora noturna', baseLegal: 'Art. 73, CLT', categoria: 'principal', permiteValor: true },
      { id: 'trab_dano_moral', titulo: 'Dano moral trabalhista', descricao: 'Indenização por assédio, humilhação ou tratamento degradante', baseLegal: 'Art. 223-C, CLT', categoria: 'principal', permiteValor: true, sugestaoValor: 'Até 50x o salário' },
      { id: 'trab_estabilidade', titulo: 'Indenização por estabilidade', descricao: 'Indenização por dispensa durante período de estabilidade', baseLegal: 'Art. 10, ADCT', categoria: 'principal', permiteValor: true },
      { id: 'trab_justica_gratuita', titulo: 'Justiça gratuita', descricao: 'Concessão dos benefícios da justiça gratuita', baseLegal: 'Art. 790, §3º, CLT', categoria: 'processual', permiteValor: false },
    ],
    penal: [
      // Pedidos principais - Queixa-crime
      { id: 'pen_recebimento_queixa', titulo: 'Recebimento da queixa-crime', descricao: 'Recebimento da queixa-crime e citação do querelado', baseLegal: 'Art. 396, CPP', categoria: 'principal', permiteValor: false },
      { id: 'pen_condenacao', titulo: 'Condenação do réu/querelado', descricao: 'Condenação nas penas do crime imputado', baseLegal: 'Art. 59, CP', categoria: 'principal', permiteValor: false },
      { id: 'pen_reparacao_danos', titulo: 'Reparação de danos', descricao: 'Fixação de valor mínimo para reparação dos danos causados pelo crime', baseLegal: 'Art. 387, IV, CPP', categoria: 'principal', permiteValor: true, sugestaoValor: 'R$ 5.000 a R$ 50.000' },
      // Pedidos principais - Representação
      { id: 'pen_instauracao_ip', titulo: 'Instauração de inquérito', descricao: 'Instauração de inquérito policial para apuração dos fatos', baseLegal: 'Art. 5º, II, CPP', categoria: 'principal', permiteValor: false },
      { id: 'pen_oferecimento_denuncia', titulo: 'Oferecimento de denúncia', descricao: 'Encaminhamento ao MP para oferecimento de denúncia', baseLegal: 'Art. 24, CPP', categoria: 'principal', permiteValor: false },
      // Medidas cautelares
      { id: 'pen_prisao_preventiva', titulo: 'Prisão preventiva', descricao: 'Decretação da prisão preventiva do acusado', baseLegal: 'Art. 312, CPP', categoria: 'principal', permiteValor: false },
      { id: 'pen_prisao_temporaria', titulo: 'Prisão temporária', descricao: 'Decretação de prisão temporária durante as investigações', baseLegal: 'Lei 7.960/89', categoria: 'principal', permiteValor: false },
      { id: 'pen_medidas_protetivas', titulo: 'Medidas protetivas de urgência', descricao: 'Afastamento do lar, proibição de aproximação e contato', baseLegal: 'Arts. 22 e 23, Lei 11.340/06', categoria: 'principal', permiteValor: false },
      { id: 'pen_afastamento_lar', titulo: 'Afastamento do lar', descricao: 'Afastamento imediato do agressor do lar conjugal', baseLegal: 'Art. 22, II, Lei 11.340/06', categoria: 'principal', permiteValor: false },
      { id: 'pen_proibicao_aproximacao', titulo: 'Proibição de aproximação', descricao: 'Proibição de aproximação da vítima e familiares', baseLegal: 'Art. 22, III, a, Lei 11.340/06', categoria: 'principal', permiteValor: false },
      { id: 'pen_proibicao_contato', titulo: 'Proibição de contato', descricao: 'Proibição de qualquer forma de contato com a vítima', baseLegal: 'Art. 22, III, b, Lei 11.340/06', categoria: 'principal', permiteValor: false },
      // Pedidos acessórios
      { id: 'pen_busca_apreensao', titulo: 'Busca e apreensão', descricao: 'Expedição de mandado de busca e apreensão', baseLegal: 'Art. 240, CPP', categoria: 'acessorio', permiteValor: false },
      { id: 'pen_sequestro_bens', titulo: 'Sequestro de bens', descricao: 'Sequestro de bens adquiridos com proventos do crime', baseLegal: 'Art. 125, CPP', categoria: 'acessorio', permiteValor: false },
      { id: 'pen_restritiva_direitos', titulo: 'Medidas cautelares diversas', descricao: 'Proibição de frequentar lugares, recolhimento noturno', baseLegal: 'Art. 319, CPP', categoria: 'acessorio', permiteValor: false },
      { id: 'pen_suspensao_porte_arma', titulo: 'Suspensão de porte de arma', descricao: 'Suspensão do porte e apreensão de armas', baseLegal: 'Art. 22, I, Lei 11.340/06', categoria: 'acessorio', permiteValor: false },
      { id: 'pen_alimentos_provisionais', titulo: 'Alimentos provisionais', descricao: 'Fixação de alimentos provisionais para vítima e dependentes', baseLegal: 'Art. 22, V, Lei 11.340/06', categoria: 'acessorio', permiteValor: true, sugestaoValor: '30% da renda' },
      { id: 'pen_perda_cargo', titulo: 'Perda de cargo público', descricao: 'Perda de cargo, função pública ou mandato eletivo', baseLegal: 'Art. 92, CP', categoria: 'acessorio', permiteValor: false },
      // Pedidos processuais
      { id: 'pen_retratacao_publica', titulo: 'Retratação pública', descricao: 'Determinação de retratação pública nos crimes contra a honra', baseLegal: 'Art. 143, CP', categoria: 'processual', permiteValor: false },
      { id: 'pen_producao_provas', titulo: 'Produção de provas', descricao: 'Oitiva de testemunhas, perícias e juntada de documentos', baseLegal: 'Art. 400, CPP', categoria: 'processual', permiteValor: false },
    ],
    familia: [
      { id: 'fam_divorcio', titulo: 'Divórcio', descricao: 'Decretação do divórcio do casal', baseLegal: 'Art. 226, §6º, CF', categoria: 'principal', permiteValor: false },
      { id: 'fam_guarda', titulo: 'Guarda dos filhos', descricao: 'Fixação de guarda unilateral ou compartilhada', baseLegal: 'Art. 1.584, CC', categoria: 'principal', permiteValor: false },
      { id: 'fam_alimentos', titulo: 'Alimentos', descricao: 'Fixação de pensão alimentícia mensal', baseLegal: 'Art. 1.694, CC', categoria: 'principal', permiteValor: true, sugestaoValor: '30% do salário mínimo a 30% da renda' },
      { id: 'fam_alimentos_provisorios', titulo: 'Alimentos provisórios', descricao: 'Fixação liminar de alimentos provisórios', baseLegal: 'Art. 4º, Lei 5.478/68', categoria: 'principal', permiteValor: true },
      { id: 'fam_partilha', titulo: 'Partilha de bens', descricao: 'Partilha igualitária dos bens adquiridos na constância do casamento', baseLegal: 'Art. 1.658, CC', categoria: 'principal', permiteValor: false },
      { id: 'fam_visitas', titulo: 'Regulamentação de visitas', descricao: 'Fixação de regime de convivência e visitas', baseLegal: 'Art. 1.589, CC', categoria: 'acessorio', permiteValor: false },
      { id: 'fam_justica_gratuita', titulo: 'Justiça gratuita', descricao: 'Concessão dos benefícios da justiça gratuita', baseLegal: 'Art. 98, CPC', categoria: 'processual', permiteValor: false },
    ],
    administrativo: [
      { id: 'adm_anulacao_ato', titulo: 'Anulação de ato administrativo', descricao: 'Anulação do ato administrativo ilegal ou abusivo', baseLegal: 'Art. 5º, LXIX, CF', categoria: 'principal', permiteValor: false },
      { id: 'adm_concessao_direito', titulo: 'Concessão de direito', descricao: 'Determinação para concessão do direito pleiteado', baseLegal: 'Lei 12.016/09', categoria: 'principal', permiteValor: false },
      { id: 'adm_liminar', titulo: 'Liminar', descricao: 'Concessão de medida liminar para suspensão do ato', baseLegal: 'Art. 7º, III, Lei 12.016/09', categoria: 'principal', permiteValor: false },
      { id: 'adm_indenizacao', titulo: 'Indenização do Estado', descricao: 'Responsabilização civil do Estado por danos causados', baseLegal: 'Art. 37, §6º, CF', categoria: 'principal', permiteValor: true },
    ],
    tributario: [
      { id: 'trib_declaracao_inexigibilidade', titulo: 'Declaração de inexigibilidade', descricao: 'Declaração de inexistência de relação jurídico-tributária', baseLegal: 'Art. 19, CPC', categoria: 'principal', permiteValor: false },
      { id: 'trib_restituicao', titulo: 'Restituição de tributo', descricao: 'Devolução de valores pagos indevidamente', baseLegal: 'Art. 165, CTN', categoria: 'principal', permiteValor: true },
      { id: 'trib_compensacao', titulo: 'Compensação tributária', descricao: 'Compensação com débitos vincendos', baseLegal: 'Art. 170, CTN', categoria: 'principal', permiteValor: true },
      { id: 'trib_suspensao_exigibilidade', titulo: 'Suspensão da exigibilidade', descricao: 'Suspensão da cobrança do tributo questionado', baseLegal: 'Art. 151, CTN', categoria: 'acessorio', permiteValor: false },
    ]
  };

  const steps = [
    'Tipo de Documento',
    'Descrição do Caso',
    'Tipo de Ação e Tribunal',
    'Tópicos Jurídicos',
    'Pedidos',
    'Configurações do Documento',
    'Revisão Final'
  ];

  // Função helper para scroll ao topo do wizard
  const scrollToTop = () => {
    // Tentar múltiplas abordagens para garantir scroll
    if (wizardTopRef.current) {
      wizardTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Fallback para window.scrollTo
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Fallback adicional para containers com scroll
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const handleNext = async () => {
    // Step 0: Tipo de Documento - apenas avançar
    if (activeStep === 0) {
      // Quando seleciona tipo, pré-preencher area_direito baseado no tipo
      if (selectedDocumentType) {
        setSelectedAreas([selectedDocumentType.area]);
        setWizardData(prev => ({
          ...prev,
          tipo_documento: selectedDocumentType.id,
          area_direito: selectedDocumentType.area,
          // Transferir contexto do chat para campos_dinamicos.fatos_caso
          campos_dinamicos: {
            ...prev.campos_dinamicos,
            fatos_caso: prev.campos_dinamicos?.fatos_caso || prev.descricao_fatos || ''
          }
        }));
      }
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      scrollToTop();
      return;
    }

    // Se está saindo do Step 1 (Descrição do Caso), analisar antes de avançar
    if (activeStep === 1 && (wizardData.descricao_fatos || wizardData.campos_dinamicos.fatos_caso || uploadedFiles.length > 0)) {
      try {
        setAnalyzingFiles(true);

        // Usar fatos do campo dinâmico ou descrição direta
        const fatosTexto = wizardData.campos_dinamicos.fatos_caso || wizardData.descricao_fatos;

        // Combinar texto digitado + arquivos para análise
        const result = await analyzeWizardUploads(
          fatosTexto,
          uploadedFiles.length > 0 ? uploadedFiles : undefined
        );

        // Atualizar estados com o resultado - armazenar objetos completos
        setExtractedContext(result.data.extracted_text);
        setTextBySource((result.data as any).text_by_source || null);
        setSuggestedAreas(result.data.suggested_areas || []);

        // Verificar se a IA precisa de esclarecimento
        const aiData = result.data as any;
        if (aiData.needs_clarification) {
          setNeedsClarification(true);
          setClarificationMessage(aiData.clarification_message || 'Forneça mais detalhes sobre o caso para uma classificação precisa.');
        } else {
          setNeedsClarification(false);
        }

        // Capturar tópicos sugeridos pela IA
        const suggestedTopics = (result.data as any).suggested_topics;
        if (suggestedTopics && Array.isArray(suggestedTopics) && suggestedTopics.length > 0) {
          setAiSuggestedTopics(suggestedTopics);
        }

        // Capturar fundamentos sugeridos pela IA
        const suggestedFundamentos = (result.data as any).suggested_fundamentos;
        if (suggestedFundamentos && Array.isArray(suggestedFundamentos) && suggestedFundamentos.length > 0) {
          setAiSuggestedFundamentos(suggestedFundamentos);
        }

        // Capturar pedidos sugeridos pela IA
        const suggestedPedidosAI = (result.data as any).suggested_pedidos;
        if (suggestedPedidosAI && Array.isArray(suggestedPedidosAI) && suggestedPedidosAI.length > 0) {
          setAiSuggestedPedidos(suggestedPedidosAI);
        }

        // Manter a área do tipo de documento selecionado, mas adicionar sugestões
        const areasIds = result.data.suggested_areas.map((a: any) => a.id);

        // Lógica de refinamento: Se tiver "familia", remover "civel" para evitar redundância
        // "Direito de Família" já é uma especialização do Cível
        const hasFamilia = areasIds.includes('familia') || (selectedDocumentType && selectedDocumentType.area === 'familia');
        let filteredAreas = areasIds;

        if (hasFamilia) {
          filteredAreas = areasIds.filter((id: string) => id !== 'civel');
        }

        if (selectedDocumentType) {
          // Manter área do tipo selecionado e adicionar sugestões que façam sentido
          // Se a área do tipo selecionado for 'familia', removemos 'civel' das sugestões também
          let finalSuggestions = filteredAreas;
          if (selectedDocumentType.area === 'familia') {
            finalSuggestions = filteredAreas.filter((id: string) => id !== 'civel');
          }

          const areasToSet = [selectedDocumentType.area, ...finalSuggestions.filter((a: string) => a !== selectedDocumentType.area)];
          setSelectedAreas(areasToSet.slice(0, 3)); // Máximo 3 áreas
        } else {
          setSelectedAreas(filteredAreas);
        }

        // Atualizar area_direito para compatibilidade
        if (areasIds.length > 0 && !selectedDocumentType) {
          setWizardData(prev => ({
            ...prev,
            area_direito: areasIds[0]
          }));
        }
      } catch (error: any) {
        alert(error.response?.data?.detail || 'Erro ao analisar caso');
        setAnalyzingFiles(false);
        return; // Não avançar se houver erro
      } finally {
        setAnalyzingFiles(false);
      }

      // Pré-preencher nome_acao se for tipo de documento com ação automática
      if (selectedDocumentType) {
        const tipoAcaoMapeado = tipoAcaoAutomatico[selectedDocumentType.id];
        if (tipoAcaoMapeado !== undefined && tipoAcaoMapeado !== null) {
          setWizardData(prev => ({
            ...prev,
            nome_acao: tipoAcaoMapeado
          }));
        }
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // Scroll para o topo da página ao avançar
    scrollToTop();
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    // Scroll para o topo da página ao voltar
    scrollToTop();
  };

  const handleAreaChange = (area: string) => {
    setSelectedAreas(prev => {
      // Toggle: se já está selecionada, remove; se não, adiciona
      const newAreas = prev.includes(area)
        ? prev.filter(id => id !== area)
        : [...prev, area];

      // Atualizar area_direito com a primeira selecionada (compatibilidade)
      setWizardData(prevData => ({
        ...prevData,
        area_direito: newAreas[0] || '',
        nome_acao: '', // Reset ação quando muda área
        topicos_selecionados: [] // Reset tópicos
      }));

      return newAreas;
    });
  };

  const handleTribunalToggle = (tribunal: string) => {
    setWizardData(prev => ({
      ...prev,
      tribunais_selecionados: prev.tribunais_selecionados.includes(tribunal)
        ? prev.tribunais_selecionados.filter(t => t !== tribunal)
        : [...prev.tribunais_selecionados, tribunal]
    }));
  };

  const handleTopicoToggle = (topicoId: string) => {
    setWizardData(prev => ({
      ...prev,
      topicos_selecionados: prev.topicos_selecionados.includes(topicoId)
        ? prev.topicos_selecionados.filter(t => t !== topicoId)
        : [...prev.topicos_selecionados, topicoId]
    }));
  };

  const handleFinish = () => {
    // Prevenir múltiplos cliques
    if (isGenerating) {
      return;
    }

    try {
      setIsGenerating(true);

      // Coletar tópicos detalhados de TODAS as áreas selecionadas
      const allTopicosDetalhados: any[] = [];
      selectedAreas.forEach(area => {
        const topicosArea = topicosJuridicos[area as keyof typeof topicosJuridicos] || [];
        const topicosFiltrados = topicosArea.filter(topico =>
          wizardData.topicos_selecionados.includes(topico.id)
        );
        allTopicosDetalhados.push(...topicosFiltrados);
      });

      // Preparar informações de imagens
      const imageFiles = uploadedFiles.filter(f => f.type.startsWith('image/'));
      const imageInfo = imageFiles.map(file => ({
        name: file.name,
        file: file,
        inclusionMode: imageInclusionMode.get(file.name) || 'attachment',
        previewUrl: imagePreviews.get(file.name)
      }));

      // Gerar documento com os dados coletados
      const documentData = {
        ...wizardData,
        selected_areas: selectedAreas,  // Enviar TODAS as áreas selecionadas
        context_text: extractedContext,  // Incluir texto extraído de uploads
        topicos_detalhados: allTopicosDetalhados,  // Tópicos de todas as áreas
        pedidos: wizardData.pedidos_selecionados,  // Pedidos selecionados pelo usuário
        document_name: documentName || [
          ...(wizardData.acoes_selecionadas || []),
          wizardData.nome_acao
        ].filter(Boolean).join(', ') || 'Documento Jurídico',  // Nome com fallback
        folder_id: selectedFolder || null,  // ID da pasta (null para raiz)
        images: imageInfo,  // Informações das imagens
        has_images_in_body: imageInfo.some(img => img.inclusionMode === 'body'),
        has_image_attachments: imageInfo.some(img => img.inclusionMode === 'attachment'),
        // Opções de formatação da Advogada Parceira
        format_options: {
          estilo: formatOptions.estilo,  // tradicional, moderno, minimalista
          fonte: formatOptions.fonte,
          tamanho_fonte: formatOptions.tamanhoFonte,
          espacamento: formatOptions.espacamento,
          margens: formatOptions.margens,
          numerar_paginas: formatOptions.numerarPaginas,
          incluir_cabecalho: formatOptions.incluirCabecalho,
          posicao_imagens: formatOptions.posicaoImagens
        }
      };

      // Dados enviados ao editor

      // Navegar para o editor com os dados
      navigate('/documents/editor-advanced/new', {
        state: {
          documentData,
          fromWizard: true
        }
      });
    } catch (error) {
      console.error('Erro ao processar dados do wizard:', error);
      alert('Erro ao processar dados do wizard');
      setIsGenerating(false);
    }
  };

  const renderStepContent = (step: number) => {
    // Tribunais disponíveis - TODOS os tribunais brasileiros
    const tribunaisDisponiveis = [
      // Tribunais Superiores
      { codigo: 'STF', nome: 'Supremo Tribunal Federal', grupo: 'Superior' },
      { codigo: 'STJ', nome: 'Superior Tribunal de Justiça', grupo: 'Superior' },
      { codigo: 'TST', nome: 'Tribunal Superior do Trabalho', grupo: 'Superior' },
      { codigo: 'TSE', nome: 'Tribunal Superior Eleitoral', grupo: 'Superior' },
      { codigo: 'STM', nome: 'Superior Tribunal Militar', grupo: 'Superior' },

      // Tribunais Regionais Federais
      { codigo: 'TRF1', nome: 'TRF 1ª Região (DF, GO, TO, MT, BA, PI, MA, PA, AP, RR, RO, AC, AM)', grupo: 'Federal' },
      { codigo: 'TRF2', nome: 'TRF 2ª Região (RJ, ES)', grupo: 'Federal' },
      { codigo: 'TRF3', nome: 'TRF 3ª Região (SP, MS)', grupo: 'Federal' },
      { codigo: 'TRF4', nome: 'TRF 4ª Região (RS, PR, SC)', grupo: 'Federal' },
      { codigo: 'TRF5', nome: 'TRF 5ª Região (CE, AL, SE, PB, RN, PE)', grupo: 'Federal' },
      { codigo: 'TRF6', nome: 'TRF 6ª Região (MG)', grupo: 'Federal' },

      // Tribunais Regionais do Trabalho
      { codigo: 'TRT1', nome: 'TRT 1ª Região (RJ)', grupo: 'Trabalhista' },
      { codigo: 'TRT2', nome: 'TRT 2ª Região (SP)', grupo: 'Trabalhista' },
      { codigo: 'TRT3', nome: 'TRT 3ª Região (MG)', grupo: 'Trabalhista' },
      { codigo: 'TRT4', nome: 'TRT 4ª Região (RS)', grupo: 'Trabalhista' },
      { codigo: 'TRT5', nome: 'TRT 5ª Região (BA)', grupo: 'Trabalhista' },
      { codigo: 'TRT6', nome: 'TRT 6ª Região (PE)', grupo: 'Trabalhista' },
      { codigo: 'TRT7', nome: 'TRT 7ª Região (CE)', grupo: 'Trabalhista' },
      { codigo: 'TRT8', nome: 'TRT 8ª Região (PA, AP)', grupo: 'Trabalhista' },
      { codigo: 'TRT9', nome: 'TRT 9ª Região (PR)', grupo: 'Trabalhista' },
      { codigo: 'TRT10', nome: 'TRT 10ª Região (DF, TO)', grupo: 'Trabalhista' },
      { codigo: 'TRT11', nome: 'TRT 11ª Região (AM, RR)', grupo: 'Trabalhista' },
      { codigo: 'TRT12', nome: 'TRT 12ª Região (SC)', grupo: 'Trabalhista' },
      { codigo: 'TRT13', nome: 'TRT 13ª Região (PB)', grupo: 'Trabalhista' },
      { codigo: 'TRT14', nome: 'TRT 14ª Região (RO, AC)', grupo: 'Trabalhista' },
      { codigo: 'TRT15', nome: 'TRT 15ª Região (Campinas/SP)', grupo: 'Trabalhista' },
      { codigo: 'TRT16', nome: 'TRT 16ª Região (MA)', grupo: 'Trabalhista' },
      { codigo: 'TRT17', nome: 'TRT 17ª Região (ES)', grupo: 'Trabalhista' },
      { codigo: 'TRT18', nome: 'TRT 18ª Região (GO)', grupo: 'Trabalhista' },
      { codigo: 'TRT19', nome: 'TRT 19ª Região (AL)', grupo: 'Trabalhista' },
      { codigo: 'TRT20', nome: 'TRT 20ª Região (SE)', grupo: 'Trabalhista' },
      { codigo: 'TRT21', nome: 'TRT 21ª Região (RN)', grupo: 'Trabalhista' },
      { codigo: 'TRT22', nome: 'TRT 22ª Região (PI)', grupo: 'Trabalhista' },
      { codigo: 'TRT23', nome: 'TRT 23ª Região (MT)', grupo: 'Trabalhista' },
      { codigo: 'TRT24', nome: 'TRT 24ª Região (MS)', grupo: 'Trabalhista' },

      // Tribunais de Justiça Estaduais
      { codigo: 'TJAC', nome: 'Tribunal de Justiça do Acre', grupo: 'Estadual' },
      { codigo: 'TJAL', nome: 'Tribunal de Justiça de Alagoas', grupo: 'Estadual' },
      { codigo: 'TJAP', nome: 'Tribunal de Justiça do Amapá', grupo: 'Estadual' },
      { codigo: 'TJAM', nome: 'Tribunal de Justiça do Amazonas', grupo: 'Estadual' },
      { codigo: 'TJBA', nome: 'Tribunal de Justiça da Bahia', grupo: 'Estadual' },
      { codigo: 'TJCE', nome: 'Tribunal de Justiça do Ceará', grupo: 'Estadual' },
      { codigo: 'TJDFT', nome: 'Tribunal de Justiça do Distrito Federal e Territórios', grupo: 'Estadual' },
      { codigo: 'TJES', nome: 'Tribunal de Justiça do Espírito Santo', grupo: 'Estadual' },
      { codigo: 'TJGO', nome: 'Tribunal de Justiça de Goiás', grupo: 'Estadual' },
      { codigo: 'TJMA', nome: 'Tribunal de Justiça do Maranhão', grupo: 'Estadual' },
      { codigo: 'TJMT', nome: 'Tribunal de Justiça do Mato Grosso', grupo: 'Estadual' },
      { codigo: 'TJMS', nome: 'Tribunal de Justiça do Mato Grosso do Sul', grupo: 'Estadual' },
      { codigo: 'TJMG', nome: 'Tribunal de Justiça de Minas Gerais', grupo: 'Estadual' },
      { codigo: 'TJPA', nome: 'Tribunal de Justiça do Pará', grupo: 'Estadual' },
      { codigo: 'TJPB', nome: 'Tribunal de Justiça da Paraíba', grupo: 'Estadual' },
      { codigo: 'TJPR', nome: 'Tribunal de Justiça do Paraná', grupo: 'Estadual' },
      { codigo: 'TJPE', nome: 'Tribunal de Justiça de Pernambuco', grupo: 'Estadual' },
      { codigo: 'TJPI', nome: 'Tribunal de Justiça do Piauí', grupo: 'Estadual' },
      { codigo: 'TJRJ', nome: 'Tribunal de Justiça do Rio de Janeiro', grupo: 'Estadual' },
      { codigo: 'TJRN', nome: 'Tribunal de Justiça do Rio Grande do Norte', grupo: 'Estadual' },
      { codigo: 'TJRS', nome: 'Tribunal de Justiça do Rio Grande do Sul', grupo: 'Estadual' },
      { codigo: 'TJRO', nome: 'Tribunal de Justiça de Rondônia', grupo: 'Estadual' },
      { codigo: 'TJRR', nome: 'Tribunal de Justiça de Roraima', grupo: 'Estadual' },
      { codigo: 'TJSC', nome: 'Tribunal de Justiça de Santa Catarina', grupo: 'Estadual' },
      { codigo: 'TJSP', nome: 'Tribunal de Justiça de São Paulo', grupo: 'Estadual' },
      { codigo: 'TJSE', nome: 'Tribunal de Justiça de Sergipe', grupo: 'Estadual' },
      { codigo: 'TJTO', nome: 'Tribunal de Justiça do Tocantins', grupo: 'Estadual' },

      // Tribunais Regionais Eleitorais
      { codigo: 'TRE-AC', nome: 'TRE Acre', grupo: 'Eleitoral' },
      { codigo: 'TRE-AL', nome: 'TRE Alagoas', grupo: 'Eleitoral' },
      { codigo: 'TRE-AP', nome: 'TRE Amapá', grupo: 'Eleitoral' },
      { codigo: 'TRE-AM', nome: 'TRE Amazonas', grupo: 'Eleitoral' },
      { codigo: 'TRE-BA', nome: 'TRE Bahia', grupo: 'Eleitoral' },
      { codigo: 'TRE-CE', nome: 'TRE Ceará', grupo: 'Eleitoral' },
      { codigo: 'TRE-DF', nome: 'TRE Distrito Federal', grupo: 'Eleitoral' },
      { codigo: 'TRE-ES', nome: 'TRE Espírito Santo', grupo: 'Eleitoral' },
      { codigo: 'TRE-GO', nome: 'TRE Goiás', grupo: 'Eleitoral' },
      { codigo: 'TRE-MA', nome: 'TRE Maranhão', grupo: 'Eleitoral' },
      { codigo: 'TRE-MT', nome: 'TRE Mato Grosso', grupo: 'Eleitoral' },
      { codigo: 'TRE-MS', nome: 'TRE Mato Grosso do Sul', grupo: 'Eleitoral' },
      { codigo: 'TRE-MG', nome: 'TRE Minas Gerais', grupo: 'Eleitoral' },
      { codigo: 'TRE-PA', nome: 'TRE Pará', grupo: 'Eleitoral' },
      { codigo: 'TRE-PB', nome: 'TRE Paraíba', grupo: 'Eleitoral' },
      { codigo: 'TRE-PR', nome: 'TRE Paraná', grupo: 'Eleitoral' },
      { codigo: 'TRE-PE', nome: 'TRE Pernambuco', grupo: 'Eleitoral' },
      { codigo: 'TRE-PI', nome: 'TRE Piauí', grupo: 'Eleitoral' },
      { codigo: 'TRE-RJ', nome: 'TRE Rio de Janeiro', grupo: 'Eleitoral' },
      { codigo: 'TRE-RN', nome: 'TRE Rio Grande do Norte', grupo: 'Eleitoral' },
      { codigo: 'TRE-RS', nome: 'TRE Rio Grande do Sul', grupo: 'Eleitoral' },
      { codigo: 'TRE-RO', nome: 'TRE Rondônia', grupo: 'Eleitoral' },
      { codigo: 'TRE-RR', nome: 'TRE Roraima', grupo: 'Eleitoral' },
      { codigo: 'TRE-SC', nome: 'TRE Santa Catarina', grupo: 'Eleitoral' },
      { codigo: 'TRE-SP', nome: 'TRE São Paulo', grupo: 'Eleitoral' },
      { codigo: 'TRE-SE', nome: 'TRE Sergipe', grupo: 'Eleitoral' },
      { codigo: 'TRE-TO', nome: 'TRE Tocantins', grupo: 'Eleitoral' }
    ];

    try {
      switch (step) {
        case 0:
          // Step 0: Seleção do Tipo de Documento
          return (
            <Box>
              <DocumentTypeSelector
                selectedType={selectedDocumentType}
                onTypeSelect={(type) => {
                  setSelectedDocumentType(type);
                  setAiAssistedMode(false); // Desativar modo assistido quando seleciona tipo

                  // Inicializar valores padrão baseado no tipo de documento
                  const isContestacao = type.id === 'contestacao_civel' || type.id === 'contestacao_trabalhista';
                  const isReplica = type.id === 'replica_civel' || type.id === 'replica_trabalhista';
                  const isManifestacao = type.id === 'manifestacao_processual';
                  const isAlegacoesFinais = type.id === 'alegacoes_finais' || type.id === 'alegacoes_finais_criminais';
                  const isAgravoInstrumento = type.id === 'agravo_instrumento';
                  const isAgravoInterno = type.id === 'agravo_interno';
                  const isRecursoEspecial = type.id === 'recurso_especial' || type.id === 'recurso_especial_previdenciario';
                  const isEmbargosDeclaracao = type.id === 'embargos_declaracao';
                  const isCumprimentoSentenca = type.id === 'cumprimento_sentenca';
                  const isApelacao = type.id === 'apelacao' || type.id === 'apelacao_criminal';
                  const isEmbargosTerceiro = type.id === 'embargos_terceiro';
                  const isTutelaIncidental = type.id === 'tutela_incidental';
                  const isTutelaEvidencia = type.id === 'tutela_evidencia';

                  setWizardData(prev => ({
                    ...prev,
                    tipo_documento: type.id,
                    area_direito: type.area,
                    campos_dinamicos: {
                      ...prev.campos_dinamicos,
                      // Valores padrão para Contestação
                      ...(isContestacao && {
                        tipo_peca: 'comum',
                        peca_rebater: 'Petição Inicial'
                      }),
                      // Valores padrão para Réplica
                      ...(isReplica && {
                        peca_rebater: 'Contestação'
                      }),
                      // Valores padrão para Manifestação
                      ...(isManifestacao && {
                        tipo_peca: 'breve'
                      }),
                      // Valores padrão para Alegações Finais
                      ...(isAlegacoesFinais && {
                        peca_rebater: 'Processo Inteiro'
                      }),
                      // Valores padrão para Agravo de Instrumento
                      ...(isAgravoInstrumento && {
                        peca_rebater: 'Decisão Interlocutória'
                      }),
                      // Valores padrão para Agravo Interno
                      ...(isAgravoInterno && {
                        peca_rebater: 'Decisão Monocrática'
                      }),
                      // Valores padrão para Recurso Especial
                      ...(isRecursoEspecial && {
                        peca_rebater: 'Acórdão'
                      }),
                      // Valores padrão para Embargos de Declaração
                      ...(isEmbargosDeclaracao && {
                        peca_rebater: 'Decisão Judicial'
                      }),
                      // Valores padrão para Cumprimento de Sentença
                      ...(isCumprimentoSentenca && {
                        peca_rebater: 'Sentença'
                      }),
                      // Valores padrão para Apelação
                      ...(isApelacao && {
                        peca_rebater: 'Sentença'
                      }),
                      // Valores padrão para Embargos de Terceiro
                      ...(isEmbargosTerceiro && {
                        peca_rebater: 'Processo Inteiro'
                      }),
                      // Valores padrão para Tutela Incidental
                      ...(isTutelaIncidental && {
                        peca_rebater: 'Processo Inteiro'
                      }),
                      // Valores padrão para Tutela da Evidência
                      ...(isTutelaEvidencia && {
                        peca_rebater: 'Processo Inteiro'
                      }),
                      // Valores padrão para Emenda à Inicial
                      ...(type.id === 'emenda_inicial' && {
                        peca_rebater: 'Despacho'
                      }),
                      // Valores padrão para Recuperação Judicial
                      ...(type.id === 'recuperacao_judicial' && {
                        peca_rebater: 'Demonstrativos Contábeis'
                      }),
                      // Valores padrão para Contrarrazões de Apelação
                      ...(type.id === 'contrarrazoes_apelacao' && {
                        peca_rebater: 'Apelação'
                      }),
                      // Valores padrão para Recurso Inominado
                      ...(type.id === 'recurso_inominado' && {
                        peca_rebater: 'Sentença'
                      }),
                      // Valores padrão para Contrarrazões ao Recurso Inominado
                      ...(type.id === 'contrarrazoes_recurso_inominado' && {
                        peca_rebater: 'Recurso Inominado'
                      }),
                      // Valores padrão para Contraminuta ao Agravo de Instrumento
                      ...(type.id === 'contraminuta_agravo' && {
                        peca_rebater: 'Agravo de Instrumento'
                      }),
                      // Valores padrão para Contraminuta ao Agravo Interno
                      ...(type.id === 'contraminuta_agravo_interno' && {
                        peca_rebater: 'Agravo Interno'
                      }),
                      // Valores padrão para Recurso Ordinário Constitucional
                      ...(type.id === 'recurso_ordinario_constitucional' && {
                        peca_rebater: 'Decisão Denegatória'
                      }),
                      // Valores padrão para Memoriais Recursais
                      ...(type.id === 'memoriais_recursais' && {
                        peca_rebater: 'Razões Recursais'
                      }),
                      // Valores padrão para Memoriais STJ
                      ...(type.id === 'memoriais_stj' && {
                        peca_rebater: 'Razões Recursais'
                      }),
                      // Valores padrão para Contrarrazões ao REsp
                      ...(type.id === 'contrarrazoes_resp' && {
                        peca_rebater: 'Recurso Especial'
                      }),
                      // Valores padrão para Agravo em REsp
                      ...(type.id === 'agravo_resp' && {
                        peca_rebater: 'Despacho Denegatório'
                      }),
                      // Valores padrão para Contraminuta ao Agravo em REsp
                      ...(type.id === 'contraminuta_agravo_resp' && {
                        peca_rebater: 'Agravo em Recurso Especial'
                      }),
                      // Valores padrão para Recurso Extraordinário
                      ...(type.id === 'recurso_extraordinario' && {
                        peca_rebater: 'Acórdão'
                      }),
                      // Valores padrão para Contrarrazões ao RE
                      ...(type.id === 'contrarrazoes_re' && {
                        peca_rebater: 'Recurso Extraordinário'
                      }),
                      // Valores padrão para Embargos de Divergência
                      ...(type.id === 'embargos_divergencia' && {
                        peca_rebater: 'Acórdão'
                      }),
                      // Valores padrão para Impugnação aos Embargos de Divergência
                      ...(type.id === 'impugnacao_embargos_divergencia' && {
                        peca_rebater: 'Embargos de Divergência'
                      }),
                      // Valores padrão para Contrarrazões aos Embargos de Declaração
                      ...(type.id === 'contrarrazoes_embargos_declaracao' && {
                        peca_rebater: 'Embargos de Declaração'
                      }),
                      // Valores padrão para Impugnação ao Cumprimento de Sentença
                      ...(type.id === 'impugnacao_cumprimento_sentenca' && {
                        peca_rebater: 'Cumprimento de Sentença'
                      }),
                      // Valores padrão para Embargos à Execução
                      ...(type.id === 'embargos_execucao' && {
                        peca_rebater: 'Petição de Execução'
                      }),
                      // Valores padrão para Impugnação aos Embargos à Execução
                      ...(type.id === 'impugnacao_embargos_execucao' && {
                        peca_rebater: 'Embargos à Execução'
                      }),
                      // Valores padrão para Embargos à Ação Monitória
                      ...(type.id === 'embargos_monitoria' && {
                        peca_rebater: 'Ação Monitória'
                      }),
                      // Valores padrão para Exceção de Pré-executividade
                      ...(type.id === 'excecao_pre_executividade' && {
                        peca_rebater: 'Petição de Execução'
                      }),
                      // Valores padrão para Impugnação à Penhora
                      ...(type.id === 'impugnacao_penhora' && {
                        peca_rebater: 'Auto de Penhora'
                      }),
                      // Valores padrão para Impugnação à Sentença de Homologação dos Cálculos
                      ...(type.id === 'impugnacao_homologacao_calculos' && {
                        peca_rebater: 'Sentença de Homologação dos Cálculos'
                      }),
                      // Valores padrão para Pedido de Levantamento de Valores
                      ...(type.id === 'pedido_levantamento_valores' && {
                        peca_rebater: 'Sentença'
                      }),
                      // Valores padrão para Pedido de Penhora de Faturamento
                      ...(type.id === 'pedido_penhora_faturamento' && {
                        peca_rebater: 'Despacho para prosseguimento da execução'
                      }),
                      // Valores padrão para Pedido de Penhora no Rosto dos Autos
                      ...(type.id === 'pedido_penhora_rosto_autos' && {
                        peca_rebater: 'Processo de Execução'
                      }),
                      // Valores padrão para Pedido de Expedição de RPV
                      ...(type.id === 'pedido_expedicao_rpv' && {
                        peca_rebater: 'Despacho'
                      }),
                      // Valores padrão para Ação Rescisória
                      ...(type.id === 'acao_rescissoria' && {
                        peca_rebater: 'Processo Original'
                      }),
                      // Valores padrão para Acordo Judicial e Pedido de Extinção
                      ...(type.id === 'acordo_judicial_extincao' && {
                        peca_rebater: 'Sentença Homologatória'
                      }),
                      // Valores padrão para Cumprimento Provisório de Sentença
                      ...(type.id === 'cumprimento_provisorio' && {
                        peca_rebater: 'Sentença'
                      }),
                    }
                  }));
                }}
                onNeedHelp={() => {
                  // Ativar modo assistido e ir para o formulário livre
                  setAiAssistedMode(true);
                  setSelectedDocumentType(null);
                  setActiveStep(1); // Ir para a etapa de descrição
                }}
              />
            </Box>
          );

        case 1:
          // Step 1: Descrição do Caso (com formulário dinâmico se tipo selecionado)
          return (
            <Box>
              {/* Header especial para modo assistido */}
              {aiAssistedMode && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    bgcolor: 'primary.50',
                    border: '1px solid',
                    borderColor: 'primary.200',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Typography sx={{ fontSize: 20 }}>✨</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} color="primary.dark" gutterBottom>
                        Modo Assistente IA Ativado
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Descreva seu caso, anexe documentos ou grave um áudio. A IA irá analisar e sugerir o melhor tipo de documento para sua situação.
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              {selectedDocumentType && !aiAssistedMode ? (
                // Formulário dinâmico baseado no tipo de documento
                <DynamicDocumentForm
                  documentType={selectedDocumentType}
                  values={wizardData.campos_dinamicos}
                  onChange={(newValues) => {
                    setWizardData(prev => ({
                      ...prev,
                      campos_dinamicos: newValues,
                      // Sincronizar campos dinâmicos com campos do wizard
                      descricao_fatos: newValues.fatos_caso || prev.descricao_fatos,
                      dados_autor: {
                        ...prev.dados_autor,
                        nome: newValues.nome_cliente || prev.dados_autor.nome
                      },
                      dados_reu: {
                        ...prev.dados_reu,
                        nome: newValues.parte_contraria || prev.dados_reu.nome
                      },
                      valor_causa: newValues.valor_causa || prev.valor_causa
                    }));
                  }}
                  uploadedFiles={uploadedFiles}
                  onFileUpload={(files) => setUploadedFiles(prev => [...prev, ...files])}
                  onFileRemove={(index) => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                />
              ) : (
                // Formulário padrão (fallback se tipo não selecionado)
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Descrição do Caso
                  </Typography>

                  {/* Dicas para preencher fatos completos */}
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      💡 Para uma petição de qualidade, inclua o máximo de detalhes:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2, '& li': { fontSize: '0.875rem', mb: 0.5 } }}>
                      <li><strong>Quem:</strong> Nomes completos de todas as partes envolvidas</li>
                      <li><strong>Quando:</strong> Datas específicas de eventos, prazos, notificações</li>
                      <li><strong>Onde:</strong> Locais exatos (endereços, cidades, comarcas)</li>
                      <li><strong>Quanto:</strong> Valores monetários, quantidades, percentuais</li>
                      <li><strong>Como:</strong> Sequência cronológica dos acontecimentos</li>
                      <li><strong>Provas:</strong> Documentos, testemunhas, fotos, contratos que comprovam</li>
                    </Box>
                  </Alert>

                  {/* Campo de Descrição dos Fatos */}
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    label="Descrição dos Fatos *"
                    placeholder="Ex: Em 15/03/2023, João da Silva (CPF 123.456.789-00) firmou contrato de locação com Maria Santos (CPF 987.654.321-00) do imóvel localizado na Rua XYZ, nº 123, São Paulo/SP, pelo valor mensal de R$ 2.500,00..."
                    value={wizardData.descricao_fatos}
                    onChange={(e) => setWizardData(prev => ({ ...prev, descricao_fatos: e.target.value }))}
                    sx={{ mb: 1 }}
                  />

                  {/* Helper text separado para evitar erro de nesting */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {wizardData.descricao_fatos.length} caracteres, {wizardData.descricao_fatos.split(/\s+/).filter(w => w.length > 0).length} palavras
                    </Typography>
                    {wizardData.descricao_fatos.length < 200 && wizardData.descricao_fatos.length > 0 && (
                      <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                        ⚠️ Adicione mais detalhes (mín. 200 caracteres)
                      </Typography>
                    )}
                    {wizardData.descricao_fatos.length >= 200 && wizardData.descricao_fatos.length < 500 && (
                      <Typography variant="caption" sx={{ color: 'info.main' }}>
                        ℹ️ Bom! Quanto mais detalhes, melhor
                      </Typography>
                    )}
                    {wizardData.descricao_fatos.length >= 500 && (
                      <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                        ✓ Excelente! Fatos detalhados
                      </Typography>
                    )}
                  </Box>

                  {/* Aviso quando fatos estão muito curtos */}
                  {wizardData.descricao_fatos.length > 0 && wizardData.descricao_fatos.length < 100 && uploadedFiles.length === 0 && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        ⚠️ Fatos insuficientes para gerar uma petição completa
                      </Typography>
                      <Typography variant="caption">
                        Forneça mais detalhes (mínimo 100 caracteres) ou anexe documentos para continuar.
                        Quanto mais informações você fornecer, mais completa e profissional será a petição gerada.
                      </Typography>
                    </Alert>
                  )}

                  {/* Seção de Upload de Documentos */}
                  <Paper sx={{ p: 3, mb: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon fontSize="small" color="primary" />
                      Anexar Documentos e Imagens (Opcional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Envie contratos, petições anteriores, <strong>fotos/prints</strong> e outros documentos relevantes.
                      Imagens podem ser incluídas no corpo da petição ou como anexos.
                    </Typography>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.txt,audio/*,image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />

                    <Button
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ borderRadius: 2 }}
                    >
                      Anexar Arquivos/Imagens
                    </Button>

                    {/* Lista de arquivos com preview de imagens */}
                    {uploadedFiles.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
                          Arquivos anexados ({uploadedFiles.length}):
                        </Typography>
                        <Stack spacing={2}>
                          {uploadedFiles.map((file, index) => {
                            const isImage = file.type.startsWith('image/');
                            const previewUrl = imagePreviews.get(file.name);
                            const inclusionMode = imageInclusionMode.get(file.name) || 'attachment';

                            return (
                              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                  {/* Preview de imagem */}
                                  {isImage && previewUrl && (
                                    <Box
                                      component="img"
                                      src={previewUrl}
                                      alt={file.name}
                                      sx={{
                                        width: 100,
                                        height: 100,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        border: '2px solid',
                                        borderColor: 'grey.300',
                                        flexShrink: 0
                                      }}
                                    />
                                  )}

                                  {/* Informações do arquivo */}
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      {file.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                      {(file.size / 1024).toFixed(1)} KB • {isImage ? 'Imagem' : file.type.includes('audio') ? 'Áudio' : 'Documento'}
                                    </Typography>

                                    {/* Opções apenas para imagens */}
                                    {isImage && (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                                          Como incluir esta imagem:
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                          <Chip
                                            label="No corpo da petição"
                                            size="small"
                                            color={inclusionMode === 'body' ? 'primary' : 'default'}
                                            variant={inclusionMode === 'body' ? 'filled' : 'outlined'}
                                            onClick={() => {
                                              const newModes = new Map(imageInclusionMode);
                                              newModes.set(file.name, 'body');
                                              setImageInclusionMode(newModes);
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                          />
                                          <Chip
                                            label="Como anexo"
                                            size="small"
                                            color={inclusionMode === 'attachment' ? 'secondary' : 'default'}
                                            variant={inclusionMode === 'attachment' ? 'filled' : 'outlined'}
                                            onClick={() => {
                                              const newModes = new Map(imageInclusionMode);
                                              newModes.set(file.name, 'attachment');
                                              setImageInclusionMode(newModes);
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                          />
                                        </Stack>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Botão remover */}
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveFile(index)}
                                    sx={{ flexShrink: 0 }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Paper>

                  {/* Seção de Gravação de Áudio - Separada */}
                  <Paper sx={{ p: 3, mb: 3, bgcolor: '#f5f9ff', border: '1px solid', borderColor: 'info.light', borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MicIcon fontSize="small" color="info" />
                      Gravar Áudio (Opcional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Grave um áudio rápido descrevendo os fatos do caso. 
                      Isso ajuda a IA a entender melhor o contexto e gerar um documento mais preciso.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AudioRecorder
                        onRecordingComplete={handleRecordingComplete}
                        disabled={false}
                      />
                      <Typography variant="caption" color="text.disabled">
                        Clique no microfone para iniciar a gravação
                      </Typography>
                    </Box>
                  </Paper>

                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                    Aceita: PDF, DOCX, TXT, JPG, PNG, GIF, WEBP, MP3, WAV
                  </Typography>
                </Box>
              )}
            </Box>
          );

        case 2:
          // Pegar ações de todas as áreas selecionadas (para sugestões quando genérico)
          const todasAcoes: string[] = [];
          selectedAreas.forEach(areaId => {
            const acoes = acoesDisponiveis[areaId as keyof typeof acoesDisponiveis] || [];
            todasAcoes.push(...acoes);
          });

          // Remover duplicatas
          const acoesUnicas = Array.from(new Set(todasAcoes));

          // Verificar se o tipo de ação é automático ou livre
          const tipoAcaoInfo = getTipoAcaoParaDocumento();

          // Estrutura de tribunais para seleção
          const tribunaisGrupos = {
            superiores: [
              { codigo: 'STF', nome: 'Supremo Tribunal Federal' },
              { codigo: 'STJ', nome: 'Superior Tribunal de Justiça' },
              { codigo: 'TST', nome: 'Tribunal Superior do Trabalho' },
              { codigo: 'TSE', nome: 'Tribunal Superior Eleitoral' },
              { codigo: 'STM', nome: 'Superior Tribunal Militar' },
            ],
            trfs: [
              { codigo: 'TRF1', nome: 'TRF 1ª Região' },
              { codigo: 'TRF2', nome: 'TRF 2ª Região' },
              { codigo: 'TRF3', nome: 'TRF 3ª Região' },
              { codigo: 'TRF4', nome: 'TRF 4ª Região' },
              { codigo: 'TRF5', nome: 'TRF 5ª Região' },
              { codigo: 'TRF6', nome: 'TRF 6ª Região' },
            ],
            tjs: tribunaisDisponiveis.filter(t => t.grupo === 'Estadual'),
            trts: tribunaisDisponiveis.filter(t => t.grupo === 'Trabalhista'),
          };

          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                {tipoAcaoInfo.automatico ? 'Tribunais para Jurisprudência' : 'Tipo de Ação e Tribunal'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {tipoAcaoInfo.automatico
                  ? 'Selecione os tribunais para pesquisa de jurisprudência.'
                  : 'Especifique o tipo de ação e selecione os tribunais para pesquisa.'}
              </Typography>

              <Box>
                {/* Tipo de Ação - Automático ou Livre */}
                {tipoAcaoInfo.automatico ? (
                  // Tipo de ação automático - mostrar como info
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Tipo de Ação:</strong> {tipoAcaoInfo.valor}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Identificado automaticamente pelo tipo de documento selecionado.
                    </Typography>
                  </Alert>
                ) : (
                  // Tipo de ação livre - campo de texto ou seleção
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                      Qual é o tipo de ação que está sendo contestada? (Opcional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Selecione uma ou mais opções abaixo e/ou utilize o campo "Outra" para especificar.
                    </Typography>

                    {/* Sugestões rápidas (Multi-seleção) */}
                    {acoesUnicas.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Sugestões para a área selecionada (clique para selecionar):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {acoesUnicas.slice(0, 12).map((acao) => {
                            const isSelected = wizardData.acoes_selecionadas?.includes(acao);
                            return (
                              <Chip
                                key={acao}
                                label={acao}
                                size="small"
                                variant={isSelected ? "filled" : "outlined"}
                                color={isSelected ? "primary" : "default"}
                                onClick={() => {
                                  setWizardData(prev => {
                                    const current = prev.acoes_selecionadas || [];
                                    const newSelection = isSelected
                                      ? current.filter(a => a !== acao)
                                      : [...current, acao];
                                    return { ...prev, acoes_selecionadas: newSelection };
                                  });
                                }}
                                sx={{
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: isSelected ? 'primary.dark' : 'action.hover' }
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    <TextField
                      fullWidth
                      label="Outra / Especifique o tipo de ação"
                      value={wizardData.nome_acao}
                      onChange={(e) => setWizardData(prev => ({ ...prev, nome_acao: e.target.value }))}
                      placeholder="Ex: Ação de Indenização por Danos Morais"
                      helperText="Digite aqui caso a ação não esteja listada acima ou para complementar"
                      sx={{ mb: 2 }}
                    />
                  </Box>
                )}

                {/* Seleção de Tribunais - Sempre visível */}
                <Box>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Selecionar Tribunais
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowTribunalModal(true)}
                    >
                      {wizardData.tribunais_selecionados.length > 0
                        ? `${wizardData.tribunais_selecionados.length} selecionado(s)`
                        : 'Selecionar'}
                    </Button>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Priorize até <strong>2 tribunais</strong> da nossa base jurisprudencial para sua pesquisa.
                  </Typography>

                  {/* Chips dos tribunais selecionados */}
                  {wizardData.tribunais_selecionados.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {wizardData.tribunais_selecionados.map(codigo => (
                        <Chip
                          key={codigo}
                          label={codigo}
                          onDelete={() => handleTribunalToggle(codigo)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        ⚠️ Nenhum tribunal selecionado. Selecione ao menos 1 tribunal para busca de jurisprudência.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              </Box>
            </Box>
          );

        case 3:
          // Pegar tópicos de todas as áreas selecionadas
          let topicosArea: any[] = [];
          const titulosAdicionados = new Set<string>(); // Para evitar duplicatas por título
          selectedAreas.forEach(areaId => {
            const topicos = topicosJuridicos[areaId as keyof typeof topicosJuridicos] || [];
            topicos.forEach(topico => {
              // Normalizar título para comparação (lowercase e sem espaços extras)
              const tituloNormalizado = topico.titulo.toLowerCase().trim();
              if (!titulosAdicionados.has(tituloNormalizado)) {
                titulosAdicionados.add(tituloNormalizado);
                topicosArea.push(topico);
              }
            });
          });

          // Se não há tópicos específicos para as áreas, usar tópicos genéricos
          if (topicosArea.length === 0) {
            topicosArea = [
              {
                id: 'generico_1',
                titulo: 'Dos fundamentos jurídicos da demanda',
                conteudo: 'Com base na legislação vigente e nos fatos narrados, restam configurados os elementos necessários para o acolhimento da presente demanda.',
                jurisprudencias: [],
                obrigatorio: true
              },
              {
                id: 'generico_2',
                titulo: 'Do direito aplicável ao caso',
                conteudo: 'A situação fática apresentada encontra amparo na legislação específica da área, devendo ser aplicadas as normas pertinentes ao caso concreto.',
                jurisprudencias: [],
                obrigatorio: true
              },
              {
                id: 'generico_3',
                titulo: 'Da procedência do pedido',
                conteudo: 'Diante dos fatos expostos e do direito aplicável, o pedido formulado deve ser julgado procedente, com a condenação da parte requerida.',
                jurisprudencias: [],
                obrigatorio: false
              },
              {
                id: 'generico_4',
                titulo: 'Da gratuidade de justiça',
                conteudo: 'O Requerente não possui condições financeiras para arcar com as custas processuais, razão pela qual postula os benefícios da justiça gratuita.',
                jurisprudencias: [],
                obrigatorio: false
              }
            ];
          }
          return (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6">
                  Tópicos Jurídicos
                </Typography>
                <Chip label="Opcional" size="small" color="info" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecione os tópicos que deseja abordar na petição. Esta etapa é <strong>totalmente opcional</strong> - você pode escolher quantos tópicos quiser ou pular esta etapa sem selecionar nenhum.
              </Typography>

              {wizardData.descricao_fatos && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    Fatos descritos:
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {wizardData.descricao_fatos.length > 200
                      ? `${wizardData.descricao_fatos.substring(0, 200)}...`
                      : wizardData.descricao_fatos}
                  </Typography>
                </Box>
              )}

              {/* Alert para tópicos sugeridos pela IA */}
              {aiSuggestedTopics.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="success" icon={false} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                      <strong>Tópicos Sugeridos pela IA baseados no seu caso:</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Nossa IA analisou os fatos descritos e sugeriu os tópicos mais relevantes.
                      Você pode manter, remover ou adicionar outros tópicos conforme necessário.
                    </Typography>
                  </Alert>

                  {/* Mostrar tópicos sugeridos pela IA */}
                  <Stack spacing={1.5}>
                    {aiSuggestedTopics.map((topic, index) => {
                      const isSelected = wizardData.topicos_selecionados.includes(topic.id);
                      return (
                        <Paper
                          key={topic.id}
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: isSelected ? 'success.50' : 'grey.100',
                            borderColor: isSelected ? 'success.main' : 'grey.300',
                            borderWidth: 2,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 2,
                              borderColor: 'success.main'
                            }
                          }}
                          onClick={() => handleTopicoToggle(topic.id)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation(); // Prevenir duplo toggle
                                handleTopicoToggle(topic.id);
                              }}
                              onClick={(e) => e.stopPropagation()} // Prevenir propagação do click
                              sx={{ p: 0, mt: -0.5 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: isSelected ? 'success.dark' : 'text.primary', mb: 0.5 }}>
                                {index + 1}. {topic.titulo}
                              </Typography>
                              {topic.reasoning && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontStyle: 'italic' }}>
                                  {topic.reasoning}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                    Você pode desmarcar tópicos sugeridos ou adicionar outros da lista abaixo.
                  </Typography>
                </Box>
              )}

              {/* Fundamentos Jurídicos Sugeridos pela IA */}
              {aiSuggestedFundamentos.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                      <strong>📖 Fundamentos Jurídicos Sugeridos:</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      A IA identificou os seguintes fundamentos legais relevantes para o seu caso.
                      Estes serão utilizados automaticamente na fundamentação da petição.
                    </Typography>
                  </Alert>

                  <Grid container spacing={2}>
                    {aiSuggestedFundamentos.map((fundamento, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            height: '100%',
                            bgcolor: fundamento.tipo === 'sumula' ? 'warning.50' : 'primary.50',
                            borderColor: fundamento.tipo === 'sumula' ? 'warning.main' : 'primary.main',
                            borderWidth: 2
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip
                              label={fundamento.tipo === 'sumula' ? '⚖️ Súmula' : fundamento.tipo === 'principio' ? '📜 Princípio' : '📖 Artigo'}
                              size="small"
                              color={fundamento.tipo === 'sumula' ? 'warning' : 'primary'}
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            {fundamento.referencia}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {fundamento.descricao}
                          </Typography>
                          {fundamento.aplicacao && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: 'success.dark' }}>
                              💡 {fundamento.aplicacao}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Alert para tópicos genéricos pré-selecionados */}
              {suggestedTopics.length > 0 && aiSuggestedTopics.length === 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                    <strong>Tópicos Pré-Selecionados:</strong>
                  </Typography>
                  <Typography variant="body2">
                    Os tópicos mais relevantes já foram selecionados automaticamente.
                    Você pode ajustar a seleção conforme necessário.
                  </Typography>
                </Alert>
              )}

              {/* Título para tópicos adicionais */}
              {aiSuggestedTopics.length > 0 && topicosArea.length > 0 && (
                <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
                  Outros Tópicos Disponíveis
                </Typography>
              )}

              <Stack spacing={2}>
                {topicosArea.map((topico, index) => {
                  const isSelected = wizardData.topicos_selecionados.includes(topico.id);
                  const categoriaInfo = topico.categoria ? categoriaLabels[topico.categoria] : null;

                  return (
                    <Paper
                      key={topico.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderWidth: isSelected ? 2 : 1,
                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1
                        }
                      }}
                      onClick={() => handleTopicoToggle(topico.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleTopicoToggle(topico.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ p: 0, mt: 0.5 }}
                        />

                        <Box sx={{ flex: 1 }}>
                          {/* Cabeçalho com título e badges */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isSelected ? 'primary.dark' : 'text.primary' }}>
                              {topico.titulo}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {/* Badge de categoria */}
                              {categoriaInfo && (
                                <Chip
                                  label={categoriaInfo.label}
                                  size="small"
                                  color={categoriaInfo.color}
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                              {/* Badge sugerido */}
                              {suggestedTopics.includes(topico.id) && (
                                <Chip
                                  label="✨ Sugerido"
                                  size="small"
                                  color="success"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                              {/* Badge obrigatório */}
                              {topico.obrigatorio && (
                                <Chip
                                  label="Recomendado"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Base legal */}
                          {topico.baseLegal && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'inline-block',
                                mb: 1,
                                px: 1,
                                py: 0.25,
                                bgcolor: 'grey.100',
                                borderRadius: 0.5,
                                fontWeight: 600,
                                color: 'primary.main'
                              }}
                            >
                              📖 {topico.baseLegal}
                            </Typography>
                          )}

                          {/* Conteúdo */}
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                            {topico.conteudo}
                          </Typography>

                          {/* Jurisprudências */}
                          {topico.jurisprudencias && topico.jurisprudencias.length > 0 && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.dark', display: 'block', mb: 1 }}>
                                📚 {topico.jurisprudencias.length} Jurisprudência{topico.jurisprudencias.length > 1 ? 's' : ''} relacionada{topico.jurisprudencias.length > 1 ? 's' : ''}
                              </Typography>

                              {topico.jurisprudencias.map((jurisp: any) => (
                                <Box key={jurisp.id} sx={{ mt: 1, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip label={jurisp.tribunal} size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      Nº {jurisp.numero_processo}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                                    "{jurisp.ementa_resumo}"
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          );

        case 4:
          // Step 4: Pedidos
          const pedidosDisponiveis = selectedAreas.flatMap(areaId => pedidosPorArea[areaId] || []);

          const handlePedidoToggle = (pedido: typeof pedidosDisponiveis[0]) => {
            setWizardData(prev => {
              const exists = prev.pedidos_selecionados.find(p => p.id === pedido.id);
              if (exists) {
                return {
                  ...prev,
                  pedidos_selecionados: prev.pedidos_selecionados.filter(p => p.id !== pedido.id)
                };
              } else {
                return {
                  ...prev,
                  pedidos_selecionados: [...prev.pedidos_selecionados, {
                    id: pedido.id,
                    titulo: pedido.titulo,
                    descricao: pedido.descricao,
                    baseLegal: pedido.baseLegal,
                    valor: '',
                    incluirValor: pedido.permiteValor,
                    customizado: false
                  }]
                };
              }
            });
          };

          const handlePedidoValorChange = (pedidoId: string, valor: string) => {
            setWizardData(prev => ({
              ...prev,
              pedidos_selecionados: prev.pedidos_selecionados.map(p =>
                p.id === pedidoId ? { ...p, valor } : p
              )
            }));
          };

          const categoriaPedidoLabels: Record<string, { label: string; color: 'primary' | 'success' | 'info' }> = {
            principal: { label: 'Principal', color: 'primary' },
            acessorio: { label: 'Acessório', color: 'success' },
            processual: { label: 'Processual', color: 'info' }
          };

          return (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6">
                  Seleção de Pedidos
                </Typography>
                <Chip
                  label={`${wizardData.pedidos_selecionados.length} selecionado${wizardData.pedidos_selecionados.length !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Selecione os pedidos que deseja incluir na petição. Para pedidos com valor, você pode especificar o montante pretendido.
              </Typography>

              {/* Pedidos Sugeridos pela IA */}
              {aiSuggestedPedidos.length > 0 && (
                <Alert severity="success" icon={false} sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                    <strong>💡 Pedidos Sugeridos pela IA para este caso:</strong>
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {aiSuggestedPedidos.map((pedido, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          label={`${index + 1}`}
                          size="small"
                          color="success"
                          sx={{ minWidth: 24, height: 24 }}
                        />
                        <Typography variant="body2">
                          <strong>{pedido.titulo}</strong>
                          {pedido.fundamentacao && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main' }}>
                              ({pedido.fundamentacao})
                            </Typography>
                          )}
                          {pedido.sugestao_valor && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'success.dark', fontWeight: 600 }}>
                              - Valor sugerido: {pedido.sugestao_valor}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    Selecione os pedidos correspondentes na lista abaixo para incluí-los na petição.
                  </Typography>
                </Alert>
              )}

              {pedidosDisponiveis.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Selecione ao menos uma área do direito no passo anterior para ver os pedidos disponíveis.
                </Alert>
              ) : (
                <>
                  {/* Agrupar por categoria */}
                  {['principal', 'acessorio', 'processual'].map(categoria => {
                    const pedidosCategoria = pedidosDisponiveis.filter(p => p.categoria === categoria);
                    if (pedidosCategoria.length === 0) return null;

                    const catInfo = categoriaPedidoLabels[categoria];

                    return (
                      <Box key={categoria} sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Chip label={catInfo.label} color={catInfo.color} size="small" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            Pedidos {catInfo.label === 'Principal' ? 'Principais' : catInfo.label === 'Acessório' ? 'Acessórios' : 'Processuais'}
                          </Typography>
                        </Box>

                        <Stack spacing={2}>
                          {pedidosCategoria.map(pedido => {
                            const isSelected = wizardData.pedidos_selecionados.some(p => p.id === pedido.id);
                            const pedidoSelecionado = wizardData.pedidos_selecionados.find(p => p.id === pedido.id);

                            return (
                              <Paper
                                key={pedido.id}
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  borderWidth: isSelected ? 2 : 1,
                                  borderColor: isSelected ? `${catInfo.color}.main` : 'grey.300',
                                  bgcolor: isSelected ? `${catInfo.color}.50` : 'background.paper',
                                  transition: 'all 0.2s ease',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    borderColor: `${catInfo.color}.main`,
                                    boxShadow: 1
                                  }
                                }}
                                onClick={() => handlePedidoToggle(pedido)}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handlePedidoToggle(pedido);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ p: 0, mt: 0.5 }}
                                  />

                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        {pedido.titulo}
                                      </Typography>
                                      {pedido.permiteValor && (
                                        <Chip
                                          label="💰 Permite valor"
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 20, fontSize: '0.65rem' }}
                                        />
                                      )}
                                    </Box>

                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'inline-block',
                                        mb: 1,
                                        px: 1,
                                        py: 0.25,
                                        bgcolor: 'grey.100',
                                        borderRadius: 0.5,
                                        fontWeight: 600,
                                        color: 'primary.main'
                                      }}
                                    >
                                      📖 {pedido.baseLegal}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary">
                                      {pedido.descricao}
                                    </Typography>

                                    {/* Campo de valor quando selecionado e permite valor */}
                                    {isSelected && pedido.permiteValor && (
                                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }} onClick={(e) => e.stopPropagation()}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                                          💰 Valor pretendido:
                                        </Typography>
                                        <TextField
                                          size="small"
                                          placeholder="Ex: R$ 10.000,00"
                                          value={pedidoSelecionado?.valor || ''}
                                          onChange={(e) => handlePedidoValorChange(pedido.id, e.target.value)}
                                          sx={{ width: '100%', maxWidth: 250 }}
                                        />
                                        {pedido.sugestaoValor && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            💡 Sugestão: {pedido.sugestaoValor}
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    );
                  })}

                  {/* Pedido customizado */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderStyle: 'dashed',
                      bgcolor: 'grey.50',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'primary.50'
                      }
                    }}
                    onClick={() => setShowPedidoModal(true)}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'primary.main' }}>
                      ➕ Adicionar pedido personalizado
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-line' }}>
                      Além dos pedidos mais comuns, uma contestação pode conter pedidos acessórios, sucessivos, subsidiários ou condicionais.
                      Utilize este campo para incluir todos os pedidos adicionais que entender pertinentes ao caso.

                      É possível adicionar mais de um pedido personalizado, conforme a necessidade.
                    </Typography>
                  </Paper>

                  {/* Lista de pedidos personalizados */}
                  {wizardData.pedidos_selecionados.filter(p => p.customizado).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                        Pedidos personalizados:
                      </Typography>
                      <Stack spacing={1}>
                        {wizardData.pedidos_selecionados.filter(p => p.customizado).map(pedido => (
                          <Paper
                            key={pedido.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              bgcolor: 'info.50',
                              borderColor: 'info.main',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {pedido.titulo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pedido.descricao}
                              </Typography>
                              {pedido.baseLegal && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'primary.main' }}>
                                  📖 {pedido.baseLegal}
                                </Typography>
                              )}
                              {pedido.valor && (
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                  💰 {pedido.valor}
                                </Typography>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setWizardData(prev => ({
                                  ...prev,
                                  pedidos_selecionados: prev.pedidos_selecionados.filter(p => p.id !== pedido.id)
                                }));
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}

              {/* Resumo dos pedidos selecionados */}
              {wizardData.pedidos_selecionados.length > 0 && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    ✅ Pedidos selecionados ({wizardData.pedidos_selecionados.length}):
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {wizardData.pedidos_selecionados.map(p => (
                      <Box component="li" key={p.id}>
                        <Typography variant="body2">
                          {p.titulo}
                          {p.valor && <strong> - {p.valor}</strong>}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          );

        case 5:
          return (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Configurações do Documento
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Personalize a formatação do documento conforme padrões profissionais
              </Typography>

              <Stack spacing={4}>
                {/* Nome do Documento */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                    Nome do Documento
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Nome do documento"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                  />
                </Box>

                {/* Seleção de Pasta */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Pasta (Opcional)
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowNewFolderDialog(true)}
                    >
                      Nova Pasta
                    </Button>
                  </Box>

                  {loadingFolders ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <FormControl fullWidth>
                      <Select
                        value={selectedFolder || ''}
                        onChange={(e) => setSelectedFolder(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem key="root" value="">
                          Raiz (sem pasta)
                        </MenuItem>
                        {Array.isArray(availableFolders) && availableFolders.map((folder: any) => (
                          <MenuItem key={folder.id} value={folder.id}>
                            {folder.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {/* Opções de Formatação Profissional */}
                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    Formatação Profissional
                    <Chip label="Opcional" size="small" color="info" variant="outlined" />
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Padrão OAB:</strong> Margens de 3cm (superior/esquerda) e 2cm (inferior/direita),
                      fonte Arial ou Times New Roman 12pt, espaçamento 1,5 entre linhas.
                    </Typography>
                  </Alert>

                  <Grid container spacing={3}>
                    {/* Estilo de Petição */}
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>Modelo da Advogada Parceira</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Paper
                            onClick={() => aplicarEstilo('tradicional')}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: formatOptions.estilo === 'tradicional' ? 'primary.main' : 'grey.200',
                              bgcolor: formatOptions.estilo === 'tradicional' ? 'primary.50' : 'background.paper',
                              borderRadius: 2,
                              transition: 'all 0.2s',
                              height: '100%',
                              '&:hover': { borderColor: 'primary.light', bgcolor: 'grey.50', transform: 'translateY(-2px)', boxShadow: 1 }
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: formatOptions.estilo === 'tradicional' ? 'primary.main' : 'text.primary' }}>
                              ⚖️ Tradicional
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                              Formato clássico forense. Times New Roman, espaçamento 1,5, margens OAB. Ideal para tribunais.
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Paper
                            onClick={() => aplicarEstilo('moderno')}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: formatOptions.estilo === 'moderno' ? 'primary.main' : 'grey.200',
                              bgcolor: formatOptions.estilo === 'moderno' ? 'primary.50' : 'background.paper',
                              borderRadius: 2,
                              transition: 'all 0.2s',
                              height: '100%',
                              '&:hover': { borderColor: 'primary.light', bgcolor: 'grey.50', transform: 'translateY(-2px)', boxShadow: 1 }
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: formatOptions.estilo === 'moderno' ? 'primary.main' : 'text.primary' }}>
                              ✨ Moderno
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                              Visual limpo e contemporâneo. Calibri, seções destacadas. Ideal para escritórios modernos.
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Paper
                            onClick={() => aplicarEstilo('minimalista')}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: formatOptions.estilo === 'minimalista' ? 'primary.main' : 'grey.200',
                              bgcolor: formatOptions.estilo === 'minimalista' ? 'primary.50' : 'background.paper',
                              borderRadius: 2,
                              transition: 'all 0.2s',
                              height: '100%',
                              '&:hover': { borderColor: 'primary.light', bgcolor: 'grey.50', transform: 'translateY(-2px)', boxShadow: 1 }
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: formatOptions.estilo === 'minimalista' ? 'primary.main' : 'text.primary' }}>
                              📄 Minimalista
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                              Direto ao ponto. Arial, espaçamento simples, margens estreitas. Economia de papel.
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Fonte */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Família da Fonte</InputLabel>
                        <Select
                          value={formatOptions.fonte}
                          onChange={(e) => setFormatOptions(prev => ({ ...prev, fonte: e.target.value }))}
                          label="Família da Fonte"
                        >
                          <MenuItem value="arial">Arial (Recomendado OAB)</MenuItem>
                          <MenuItem value="times">Times New Roman (Clássico)</MenuItem>
                          <MenuItem value="calibri">Calibri (Moderno)</MenuItem>
                          <MenuItem value="georgia">Georgia (Elegante)</MenuItem>
                          <MenuItem value="garamond">Garamond (Sofisticado)</MenuItem>
                          <MenuItem value="verdana">Verdana (Legível)</MenuItem>
                          <MenuItem value="cambria">Cambria (Tribunais)</MenuItem>
                          <MenuItem value="bookman">Bookman Old Style</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Tamanho da Fonte */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tamanho da Fonte</InputLabel>
                        <Select
                          value={formatOptions.tamanhoFonte}
                          onChange={(e) => setFormatOptions(prev => ({ ...prev, tamanhoFonte: e.target.value }))}
                          label="Tamanho da Fonte"
                        >
                          <MenuItem value="11">11pt</MenuItem>
                          <MenuItem value="12">12pt (Padrão OAB)</MenuItem>
                          <MenuItem value="13">13pt</MenuItem>
                          <MenuItem value="14">14pt</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Espaçamento */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Espaçamento entre Linhas</InputLabel>
                        <Select
                          value={formatOptions.espacamento}
                          onChange={(e) => setFormatOptions(prev => ({ ...prev, espacamento: e.target.value }))}
                          label="Espaçamento entre Linhas"
                        >
                          <MenuItem value="1.0">Simples (1.0)</MenuItem>
                          <MenuItem value="1.5">1,5 linhas (Padrão OAB)</MenuItem>
                          <MenuItem value="2.0">Duplo (2.0)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Margens */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Margens</InputLabel>
                        <Select
                          value={formatOptions.margens}
                          onChange={(e) => setFormatOptions(prev => ({ ...prev, margens: e.target.value }))}
                          label="Margens"
                        >
                          <MenuItem value="oab">Padrão OAB (3cm/2cm)</MenuItem>
                          <MenuItem value="normais">Normais (2,5cm)</MenuItem>
                          <MenuItem value="estreitas">Estreitas (1,27cm)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Numeração de Páginas */}
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formatOptions.numerarPaginas}
                            onChange={(e) => setFormatOptions(prev => ({ ...prev, numerarPaginas: e.target.checked }))}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Numerar Páginas</Typography>
                            <Typography variant="caption" color="text.secondary">Rodapé centralizado</Typography>
                          </Box>
                        }
                      />
                    </Grid>

                    {/* Cabeçalho com OAB */}
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formatOptions.incluirCabecalho}
                            onChange={(e) => setFormatOptions(prev => ({ ...prev, incluirCabecalho: e.target.checked }))}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Incluir Cabeçalho</Typography>
                            <Typography variant="caption" color="text.secondary">Dados do advogado/escritório</Typography>
                          </Box>
                        }
                      />
                    </Grid>

                    {/* Posição das Imagens */}
                    {uploadedFiles.some((f: File) => f.type.startsWith('image/')) && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Posição das Imagens</InputLabel>
                          <Select
                            value={formatOptions.posicaoImagens}
                            onChange={(e) => setFormatOptions(prev => ({ ...prev, posicaoImagens: e.target.value }))}
                            label="Posição das Imagens"
                          >
                            <MenuItem value="corpo">No corpo do texto (onde citadas)</MenuItem>
                            <MenuItem value="anexo">Ao final como anexos</MenuItem>
                            <MenuItem value="ambos">Miniatura no corpo + anexo completo</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>

                  {/* Preview Visual */}
                  <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Preview da Formatação ({formatOptions.estilo.charAt(0).toUpperCase() + formatOptions.estilo.slice(1)})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatOptions.fonte.charAt(0).toUpperCase() + formatOptions.fonte.slice(1)} • {formatOptions.tamanhoFonte}pt • Espaçamento {formatOptions.espacamento}
                      </Typography>
                    </Box>
                    <Paper
                      elevation={2}
                      sx={{
                        p: formatOptions.margens === 'estreitas' ? 2 : formatOptions.margens === 'normais' ? 3 : 4,
                        bgcolor: 'white',
                        borderRadius: 0,
                        minHeight: 350,
                        fontFamily: formatOptions.fonte === 'times' ? '"Times New Roman", serif' :
                          formatOptions.fonte === 'georgia' ? 'Georgia, serif' :
                            formatOptions.fonte === 'calibri' ? 'Calibri, sans-serif' :
                              formatOptions.fonte === 'garamond' ? 'Garamond, serif' :
                                formatOptions.fonte === 'verdana' ? 'Verdana, sans-serif' :
                                  formatOptions.fonte === 'cambria' ? 'Cambria, serif' :
                                    formatOptions.fonte === 'bookman' ? '"Bookman Old Style", serif' : 'Arial, sans-serif',
                        fontSize: `${formatOptions.tamanhoFonte}px`,
                        lineHeight: formatOptions.espacamento
                      }}
                    >
                      {/* Cabeçalho */}
                      {formatOptions.incluirCabecalho && (
                        <Box sx={{
                          mb: 3,
                          pb: 1,
                          borderBottom: formatOptions.estilo === 'tradicional' ? '1px solid #ccc' : 'none',
                          textAlign: formatOptions.estilo === 'moderno' ? 'left' : 'center'
                        }}>
                          {formatOptions.estilo === 'tradicional' && (
                            <>
                              <Typography sx={{ fontFamily: 'inherit', fontSize: '0.85em', fontWeight: 400, color: 'text.secondary' }}>
                                Dr. Ruy Barbosa • OAB/BA 000.001
                              </Typography>
                              <Typography sx={{ fontFamily: 'inherit', fontSize: '0.75em', color: 'text.secondary' }}>
                                Rua Ruy Barbosa nº 12, Cep: 40020-070, Centro Histórico-Salvador (BA)
                                <br />
                                Celular/WhatsApp: (41) 9999-9999
                              </Typography>
                            </>
                          )}

                          {formatOptions.estilo === 'moderno' && (
                            <>
                              <Typography sx={{ fontFamily: 'inherit', fontSize: '0.85em', fontWeight: 600, color: 'primary.main' }}>
                                Dr. Luke Skywalker • OAB/Tatoooine 001.342
                              </Typography>
                              <Typography sx={{ fontFamily: 'inherit', fontSize: '0.75em', color: 'text.secondary' }}>
                                Rua do Palácio Jabba the Hutt nº 23, Cep: 60320-171, Centro Mos Eisley (Tatooine)
                                <br />
                                Celular/WhatsApp: (99) 9999-9999
                              </Typography>
                            </>
                          )}

                          {formatOptions.estilo === 'minimalista' && (
                            <>
                              <Typography sx={{ fontFamily: 'inherit', fontSize: '0.85em', fontWeight: 400, color: 'text.secondary' }}>
                                Dra. Ally McBeal • OAB/Boston 19.970
                              </Typography>
                              <Typography sx={{ fontFamily: 'inherit', fontSize: '0.75em', color: 'text.secondary' }}>
                                Rua Beacon Hill, nº 110 – 4º Andar, Cep: 02108-001, Boston – MA
                                <br />
                                Celular/WhatsApp: (617) 555-ALLY
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}

                      {/* Endereçamento */}
                      <Typography
                        sx={{
                          fontWeight: formatOptions.estilo === 'tradicional' ? 'bold' : 600,
                          textAlign: 'center',
                          mb: formatOptions.estilo === 'minimalista' ? 1 : 2,
                          fontFamily: 'inherit',
                          fontSize: formatOptions.estilo === 'minimalista' ? '0.9em' : 'inherit',
                          textTransform: 'uppercase',
                          letterSpacing: formatOptions.estilo === 'tradicional' ? '0.5px' : '0'
                        }}
                      >
                        AO JUÍZO DA {formatOptions.estilo === 'minimalista' ? '' : '__ª '}VARA CÍVEL DA COMARCA DE SÃO PAULO - SP
                      </Typography>

                      {formatOptions.estilo !== 'minimalista' && <Box sx={{ mb: 3 }} />}

                      {/* Qualificação */}
                      <Typography
                        sx={{
                          fontFamily: 'inherit',
                          fontSize: 'inherit',
                          lineHeight: 'inherit',
                          textAlign: 'justify',
                          textIndent: formatOptions.estilo === 'minimalista' ? '0' : '2em',
                          mb: formatOptions.estilo === 'minimalista' ? 1 : 2
                        }}
                      >
                        <strong>MARIA DA SILVA</strong>, brasileira, casada, empresária, portadora do RG nº 12.345.678-9 SSP/SP e inscrita no CPF sob nº 123.456.789-00, residente e domiciliada na Rua das Flores, nº 100, Bairro Jardim, CEP 01234-567, São Paulo/SP, por seu advogado que esta subscreve, com procuração em anexo, vem, respeitosamente, à presença de Vossa Excelência, propor a presente
                      </Typography>

                      {/* Título da Ação */}
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          my: formatOptions.estilo === 'minimalista' ? 1 : 2,
                          fontFamily: 'inherit',
                          fontSize: formatOptions.estilo === 'moderno' ? '1.1em' : 'inherit',
                          textTransform: 'uppercase',
                          color: formatOptions.estilo === 'moderno' ? 'primary.main' : 'inherit'
                        }}
                      >
                        AÇÃO DE INDENIZAÇÃO POR DANOS MORAIS E MATERIAIS
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: 'inherit',
                          fontSize: 'inherit',
                          lineHeight: 'inherit',
                          textAlign: 'justify',
                          textIndent: formatOptions.estilo === 'minimalista' ? '0' : '2em',
                          mb: formatOptions.estilo === 'minimalista' ? 1 : 2
                        }}
                      >
                        em face de <strong>EMPRESA XYZ LTDA</strong>, pessoa jurídica de direito privado, inscrita no CNPJ sob nº 12.345.678/0001-90, com sede na Avenida Principal, nº 500, Centro, CEP 01000-000, São Paulo/SP, pelos fatos e fundamentos jurídicos a seguir expostos.
                      </Typography>

                      {/* Seção DOS FATOS */}
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          mt: formatOptions.estilo === 'minimalista' ? 1 : 3,
                          mb: 1,
                          fontFamily: 'inherit',
                          fontSize: formatOptions.estilo === 'moderno' ? '1.05em' : 'inherit',
                          textTransform: formatOptions.estilo === 'tradicional' ? 'uppercase' : 'none',
                          borderBottom: formatOptions.estilo === 'moderno' ? '2px solid' : 'none',
                          borderColor: 'primary.main',
                          pb: formatOptions.estilo === 'moderno' ? 0.5 : 0,
                          display: 'inline-block'
                        }}
                      >
                        {formatOptions.estilo === 'tradicional' ? 'I - DOS FATOS' : formatOptions.estilo === 'moderno' ? '1. Dos Fatos' : 'DOS FATOS'}
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: 'inherit',
                          fontSize: 'inherit',
                          lineHeight: 'inherit',
                          textAlign: 'justify',
                          textIndent: formatOptions.estilo === 'minimalista' ? '0' : '2em'
                        }}
                      >
                        A Autora celebrou contrato de prestação de serviços com a Ré em 15 de janeiro de 2024, conforme documento em anexo. Ocorre que a Ré não cumpriu com suas obrigações contratuais, causando prejuízos materiais e morais à Autora...
                      </Typography>

                      {/* Numeração de página */}
                      {formatOptions.numerarPaginas && (
                        <Typography sx={{
                          textAlign: 'center',
                          mt: 3,
                          pt: 1,
                          borderTop: '1px solid #eee',
                          fontSize: '0.8em',
                          color: 'text.secondary',
                          fontFamily: 'inherit'
                        }}>
                          1
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                </Box>
              </Stack>

              {/* Dialog para criar nova pasta */}
              <Dialog
                open={showNewFolderDialog}
                onClose={() => setShowNewFolderDialog(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Criar Nova Pasta</DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Nome da Pasta"
                    fullWidth
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Ex: Processos 2025"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    setShowNewFolderDialog(false);
                    setNewFolderName('');
                  }}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!newFolderName.trim()) return;

                      try {
                        const api = (await import('../../services/api')).default;
                        const response = await api.post('/documents/folders', {
                          name: newFolderName.trim()
                        });

                        // A resposta vem com {status: "success", data: {...}}
                        const newFolder = response.data.data || response.data;

                        // Adicionar nova pasta à lista e selecioná-la
                        setAvailableFolders(prev => [...prev, newFolder]);
                        setSelectedFolder(newFolder.id);

                        // Fechar dialog e limpar
                        setShowNewFolderDialog(false);
                        setNewFolderName('');
                      } catch (error: any) {
                        alert(error.response?.data?.detail || 'Erro ao criar pasta');
                      }
                    }}
                    variant="contained"
                    disabled={!newFolderName.trim()}
                  >
                    Criar
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          );

        case 6:
          // Calcular estatísticas para o resumo
          const totalCaracteresFatos = wizardData.descricao_fatos.length + (extractedContext?.length || 0);
          const totalPalavrasFatos = (wizardData.descricao_fatos + ' ' + (extractedContext || '')).split(/\s+/).filter(Boolean).length;
          const qualidadeFatos = totalCaracteresFatos < 200 ? 'insuficiente' : totalCaracteresFatos < 500 ? 'razoável' : 'detalhado';
          const temPedidos = wizardData.pedidos_selecionados.length > 0;
          const temTopicos = wizardData.topicos_selecionados.length > 0;
          const temTribunais = wizardData.tribunais_selecionados.length > 0;
          const temAnexos = uploadedFiles.length > 0;
          const temFundamentos = aiSuggestedFundamentos.length > 0;
          const temJurisprudencias = pandoraJurisprudences.length > 0;

          // Calcular score de completude
          let completudeScore = 0;
          if (selectedAreas.length > 0) completudeScore += 15;
          if (wizardData.nome_acao || (wizardData.acoes_selecionadas && wizardData.acoes_selecionadas.length > 0)) completudeScore += 10;
          if (qualidadeFatos === 'detalhado') completudeScore += 25;
          else if (qualidadeFatos === 'razoável') completudeScore += 15;
          if (temTopicos) completudeScore += 15;
          if (temPedidos) completudeScore += 20;
          if (temTribunais) completudeScore += 10;
          if (temAnexos) completudeScore += 5;

          return (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Revisão Final
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Revise todos os dados coletados antes de gerar seu documento jurídico personalizado.
              </Typography>

              {/* Resumo Executivo */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  mb: 3,
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Resumo do Documento
                  </Typography>
                  <Chip
                    label={`${completudeScore}% completo`}
                    size="small"
                    sx={{
                      bgcolor: completudeScore >= 80 ? 'success.light' : completudeScore >= 50 ? 'warning.light' : 'error.light',
                      color: completudeScore >= 80 ? 'success.dark' : completudeScore >= 50 ? 'warning.dark' : 'error.dark',
                      fontWeight: 600
                    }}
                  />
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {selectedAreas.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedAreas.length === 1 ? 'Área' : 'Áreas'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {wizardData.pedidos_selecionados.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {wizardData.pedidos_selecionados.length === 1 ? 'Pedido' : 'Pedidos'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {totalPalavrasFatos}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Palavras
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      textAlign: 'center',
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {uploadedFiles.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {uploadedFiles.length === 1 ? 'Anexo' : 'Anexos'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Barra de progresso */}
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Progresso do preenchimento
                    </Typography>
                    <Typography variant="caption" sx={{
                      fontWeight: 600,
                      color: completudeScore >= 80 ? 'success.main' : completudeScore >= 50 ? 'warning.main' : 'error.main'
                    }}>
                      {completudeScore >= 80 ? 'Excelente' : completudeScore >= 50 ? 'Bom' : 'Incompleto'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={completudeScore}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        bgcolor: completudeScore >= 80 ? 'success.main' : completudeScore >= 50 ? 'warning.main' : 'error.main'
                      }
                    }}
                  />
                </Box>
              </Paper>

              {/* Alertas de Validação */}
              {(qualidadeFatos === 'insuficiente' || !temPedidos || selectedAreas.length === 0) && (
                <Alert
                  severity={qualidadeFatos === 'insuficiente' ? 'warning' : 'info'}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Recomendações para melhorar o documento:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {qualidadeFatos === 'insuficiente' && (
                      <li>
                        <Typography variant="body2">
                          Fatos insuficientes ({totalCaracteresFatos} caracteres). Recomendamos pelo menos 500 caracteres para uma petição completa.
                          <Button size="small" onClick={() => setActiveStep(0)} sx={{ ml: 1 }}>
                            Editar fatos
                          </Button>
                        </Typography>
                      </li>
                    )}
                    {!temPedidos && (
                      <li>
                        <Typography variant="body2">
                          Nenhum pedido selecionado. Os pedidos são importantes para estruturar a petição.
                          <Button size="small" onClick={() => setActiveStep(4)} sx={{ ml: 1 }}>
                            Adicionar pedidos
                          </Button>
                        </Typography>
                      </li>
                    )}
                    {selectedAreas.length === 0 && (
                      <li>
                        <Typography variant="body2">
                          Nenhuma área do direito selecionada.
                          <Button size="small" onClick={() => setActiveStep(1)} sx={{ ml: 1 }}>
                            Selecionar área
                          </Button>
                        </Typography>
                      </li>
                    )}
                  </Box>
                </Alert>
              )}

              {/* Recursos Encontrados */}
              {(temFundamentos || temJurisprudencias) && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Recursos jurídicos encontrados para enriquecer o documento:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {temFundamentos && (
                      <Chip
                        label={`${aiSuggestedFundamentos.length} fundamentos legais`}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {temJurisprudencias && (
                      <Chip
                        label={`${pandoraJurisprudences.length} jurisprudências`}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {temAnexos && (
                      <Chip
                        label={`${uploadedFiles.length} anexo(s)`}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Alert>
              )}

              <Stack spacing={3}>
                {/* Área do Direito e Ação */}
                <Paper
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: 2
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      Área do Direito e Ação
                    </Typography>
                    <Button size="small" onClick={() => setShowAreaEditDialog(true)} sx={{ minWidth: 'auto' }}>
                      Editar
                    </Button>
                  </Box>
                  <Box sx={{ ml: 2 }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {selectedAreas.length > 1 ? 'Áreas Jurídicas' : 'Área Jurídica'}
                      </Typography>
                      {selectedAreas.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {selectedAreas.map(areaId => (
                            <Chip
                              key={areaId}
                              label={areasDisponiveis.find(a => a.id === areaId)?.nome || areaId}
                              variant="outlined"
                              size="small"
                              sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          Não selecionado
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Tipo de Ação
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {[
                          ...(wizardData.acoes_selecionadas || []),
                          wizardData.nome_acao
                        ].filter(Boolean).join(', ') || 'Não selecionado'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Descrição dos Fatos */}
                <Paper
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: 2
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'info.main' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      Descrição dos Fatos
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${totalPalavrasFatos} palavras`}
                        size="small"
                        color={qualidadeFatos === 'detalhado' ? 'success' : qualidadeFatos === 'razoável' ? 'warning' : 'error'}
                      />
                      <Button size="small" onClick={() => setActiveStep(0)} sx={{ minWidth: 'auto' }}>
                        Editar
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ ml: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontStyle: wizardData.descricao_fatos ? 'normal' : 'italic',
                        color: wizardData.descricao_fatos ? 'text.primary' : 'text.secondary',
                        lineHeight: 1.6,
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        maxHeight: 200,
                        overflow: 'auto'
                      }}
                    >
                      {wizardData.descricao_fatos || 'Nenhuma descrição fornecida'}
                    </Typography>
                  </Box>
                </Paper>

                {/* Imagens e Anexos */}
                {uploadedFiles.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:hover': {
                        boxShadow: 2
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'warning.main' }}>
                      Imagens e Anexos ({uploadedFiles.length})
                    </Typography>
                    <Box sx={{ ml: 5 }}>
                      <Grid container spacing={2}>
                        {uploadedFiles.map((file, index) => {
                          const isImage = file.type.startsWith('image/');
                          const previewUrl = imagePreviews.get(file.name);
                          const mode = imageInclusionMode.get(file.name);

                          return (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Card variant="outlined">
                                {isImage && previewUrl && (
                                  <Box
                                    component="img"
                                    src={previewUrl}
                                    alt={file.name}
                                    sx={{
                                      width: '100%',
                                      height: 150,
                                      objectFit: 'cover'
                                    }}
                                  />
                                )}
                                <CardContent sx={{ p: 1.5 }}>
                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                    {file.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {(file.size / 1024).toFixed(1)} KB
                                  </Typography>
                                  {isImage && mode && (
                                    <Chip
                                      label={mode === 'body' ? 'No corpo' : 'Anexo'}
                                      size="small"
                                      color={mode === 'body' ? 'primary' : 'secondary'}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Paper>
                )}

                {/* Tópicos Jurídicos */}
                <Paper
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: 2
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'success.main' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      Tópicos Jurídicos Selecionados
                    </Typography>
                    <Button size="small" onClick={() => setActiveStep(3)} sx={{ minWidth: 'auto' }}>
                      Editar
                    </Button>
                  </Box>
                  <Box sx={{ ml: 2 }}>
                    {wizardData.topicos_selecionados.length > 0 ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {wizardData.topicos_selecionados.length} tópico{wizardData.topicos_selecionados.length !== 1 ? 's' : ''} selecionado{wizardData.topicos_selecionados.length !== 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {wizardData.topicos_selecionados.map((topicoId, index) => {
                            // Procurar o tópico em todas as áreas selecionadas
                            let topico = null;
                            const areasParaBuscar = selectedAreas.length > 0 ? selectedAreas : [wizardData.area_direito];

                            for (const areaId of areasParaBuscar) {
                              if (!areaId) continue;
                              topico = topicosJuridicos[areaId as keyof typeof topicosJuridicos]?.find((t: any) => t.id === topicoId);
                              if (topico) break;
                            }

                            return (
                              <Chip
                                key={topicoId}
                                label={topico?.titulo || `Tópico ${index + 1}`}
                                variant="outlined"
                                size="small"
                                sx={{
                                  borderColor: 'success.main',
                                  color: 'success.main',
                                  '&:hover': {
                                    backgroundColor: 'success.50'
                                  }
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        Nenhum tópico selecionado
                      </Typography>
                    )}
                  </Box>
                </Paper>

                {/* Pedidos Selecionados */}
                <Paper
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: 2
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'secondary.main' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      Pedidos Selecionados ({wizardData.pedidos_selecionados.length})
                    </Typography>
                    <Button size="small" onClick={() => setActiveStep(4)} sx={{ minWidth: 'auto' }}>
                      Editar
                    </Button>
                  </Box>
                  <Box sx={{ ml: 2 }}>
                    {wizardData.pedidos_selecionados.length > 0 ? (
                      <Stack spacing={1.5}>
                        {wizardData.pedidos_selecionados.map((pedido, index) => (
                          <Box key={pedido.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 20 }}>
                              {index + 1}.
                            </Typography>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {pedido.titulo}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'primary.main' }}>
                                📖 {pedido.baseLegal}
                              </Typography>
                              {pedido.valor && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'success.dark', fontWeight: 600 }}>
                                  💰 Valor: {pedido.valor}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        Nenhum pedido selecionado
                      </Typography>
                    )}
                  </Box>
                </Paper>

                {/* Tribunais */}
                <Paper
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: 2
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'warning.main' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      Tribunais para Jurisprudências
                    </Typography>
                    <Button size="small" onClick={() => setActiveStep(5)} sx={{ minWidth: 'auto' }}>
                      Editar
                    </Button>
                  </Box>
                  <Box sx={{ ml: 2 }}>
                    {wizardData.tribunais_selecionados.length > 0 ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {wizardData.tribunais_selecionados.length} tribunal{wizardData.tribunais_selecionados.length !== 1 ? 's' : ''} selecionado{wizardData.tribunais_selecionados.length !== 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {wizardData.tribunais_selecionados.map((codigo) => {
                            const tribunal = tribunaisDisponiveis.find(t => t.codigo === codigo);
                            return (
                              <Chip
                                key={codigo}
                                label={tribunal?.nome || codigo}
                                variant="outlined"
                                size="small"
                                sx={{
                                  borderColor: 'warning.main',
                                  color: 'warning.main',
                                  '&:hover': {
                                    backgroundColor: 'warning.50'
                                  }
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        Nenhum tribunal selecionado
                      </Typography>
                    )}
                  </Box>
                </Paper>

                {/* Configurações do Documento */}
                <Paper
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: 2
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'secondary.main' }}>
                    Documento
                  </Typography>
                  <Box sx={{ ml: 5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                      {documentName || [
                        ...(wizardData.acoes_selecionadas || []),
                        wizardData.nome_acao
                      ].filter(Boolean).join(', ') || 'Documento Jurídico'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedFolder
                        ? `📁 ${Array.isArray(availableFolders) ? availableFolders.find((f: any) => f.id === selectedFolder)?.name : 'Pasta'}`
                        : '📁 Raiz'}
                    </Typography>
                  </Box>
                </Paper>

                {/* Resumo de Geração */}
                <Paper
                  sx={{
                    p: 3,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    backgroundColor: 'primary.50'
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
                    Tudo pronto!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Agora é comigo. Vou gerar sua peça com base em todas as informações que você me contou — técnica jurídica, estrutura correta e sem “juridiquês” desnecessário.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Advogada Parceira em ação"
                      size="small"
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Custo: 8 créditos
                    </Typography>
                  </Box>
                </Paper>
              </Stack>
            </Box>
          );

        default:
          return 'Etapa desconhecida';
      }
    } catch (error: any) {
      console.error('Erro ao renderizar step:', error);
      return (
        <Box>
          <Alert severity="error">
            Erro ao carregar etapa. Detalhes no console.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {error?.message || 'Erro desconhecido'}
          </Typography>
        </Box>
      );
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        // Step 0: Tipo de Documento
        // Precisa selecionar um tipo OU ativar modo assistido
        return selectedDocumentType !== null || aiAssistedMode;
      case 1:
        // Step 1: Descrição do Caso
        // Validação específica para tipos de documento que têm formulário próprio
        const isContestacaoType = selectedDocumentType?.id === 'contestacao_civel' ||
          selectedDocumentType?.id === 'contestacao_trabalhista';
        const isReplicaType = selectedDocumentType?.id === 'replica_civel' ||
          selectedDocumentType?.id === 'replica_trabalhista';
        const isManifestacaoType = selectedDocumentType?.id === 'manifestacao_processual';
        const isAlegacoesFinaisType = selectedDocumentType?.id === 'alegacoes_finais' ||
          selectedDocumentType?.id === 'alegacoes_finais_criminais';
        const isAgravoInstrumentoType = selectedDocumentType?.id === 'agravo_instrumento';
        const isAgravoInternoType = selectedDocumentType?.id === 'agravo_interno';
        const isRecursoEspecialType = selectedDocumentType?.id === 'recurso_especial' ||
          selectedDocumentType?.id === 'recurso_especial_previdenciario';
        const isEmbargosDeclaracaoType = selectedDocumentType?.id === 'embargos_declaracao';
        const isCumprimentoSentencaType = selectedDocumentType?.id === 'cumprimento_sentenca';
        const isApelacaoType = selectedDocumentType?.id === 'apelacao' || selectedDocumentType?.id === 'apelacao_criminal';
        const isEmbargosTerceiroType = selectedDocumentType?.id === 'embargos_terceiro';
        const isTutelaIncidentalType = selectedDocumentType?.id === 'tutela_incidental';
        const isTutelaEvidenciaType = selectedDocumentType?.id === 'tutela_evidencia';
        const isAdjudicacaoCompulsoriaType = selectedDocumentType?.id === 'adjudicacao_compulsoria';
        const isSustacaoProtestoType = selectedDocumentType?.id === 'sustacao_protesto';
        const isBuscaApreensaoMenorType = selectedDocumentType?.id === 'busca_apreensao_menor';
        const isEmendaInicialType = selectedDocumentType?.id === 'emenda_inicial';
        const isRecuperacaoJudicialType = selectedDocumentType?.id === 'recuperacao_judicial';
        const isMandadoSegurancaType = selectedDocumentType?.id === 'mandado_seguranca';
        const isExecucaoTituloExtrajudicialType = selectedDocumentType?.id === 'execucao_titulo_extrajudicial';
        const isTutelaCautelarType = selectedDocumentType?.id === 'tutela_provisoria_cautelar' ||
          selectedDocumentType?.id === 'tutela_provisoria_antecipada';

        // Validação especial para Tutela Cautelar (formulário tipo petição inicial)
        if (isTutelaCautelarType) {
          const nomeClienteOk = (wizardData.campos_dinamicos?.nome_cliente || '').trim().length > 0;
          const fatosOk = (wizardData.campos_dinamicos?.fatos_caso || '').trim().length >= 200;
          const parteContrariaOk = (wizardData.campos_dinamicos?.parte_contraria || '').trim().length > 0;
          return nomeClienteOk && fatosOk && parteContrariaOk;
        }

        // Validação para Adjudicação Compulsória (formulário tipo petição inicial)
        if (isAdjudicacaoCompulsoriaType) {
          const nomeClienteOk = (wizardData.campos_dinamicos?.nome_cliente || '').trim().length > 0;
          const fatosOk = (wizardData.campos_dinamicos?.fatos_caso || '').trim().length >= 200;
          const parteContrariaOk = (wizardData.campos_dinamicos?.parte_contraria || '').trim().length > 0;
          return nomeClienteOk && fatosOk && parteContrariaOk;
        }

        // Validação para Sustação de Protesto (formulário tipo petição inicial)
        if (isSustacaoProtestoType) {
          const nomeClienteOk = (wizardData.campos_dinamicos?.nome_cliente || '').trim().length > 0;
          const fatosOk = (wizardData.campos_dinamicos?.fatos_caso || '').trim().length >= 200;
          const parteContrariaOk = (wizardData.campos_dinamicos?.parte_contraria || '').trim().length > 0;
          return nomeClienteOk && fatosOk && parteContrariaOk;
        }

        // Validação para Busca e Apreensão de Menor (formulário tipo petição inicial)
        if (isBuscaApreensaoMenorType) {
          const nomeClienteOk = (wizardData.campos_dinamicos?.nome_cliente || '').trim().length > 0;
          const fatosOk = (wizardData.campos_dinamicos?.fatos_caso || '').trim().length >= 200;
          const parteContrariaOk = (wizardData.campos_dinamicos?.parte_contraria || '').trim().length > 0;
          return nomeClienteOk && fatosOk && parteContrariaOk;
        }

        // Validação para Mandado de Segurança (formulário tipo petição inicial)
        if (isMandadoSegurancaType) {
          const nomeClienteOk = (wizardData.campos_dinamicos?.nome_cliente || '').trim().length > 0;
          const fatosOk = (wizardData.campos_dinamicos?.fatos_caso || '').trim().length >= 200;
          const parteContrariaOk = (wizardData.campos_dinamicos?.parte_contraria || '').trim().length > 0;
          return nomeClienteOk && fatosOk && parteContrariaOk;
        }

        // Validação para Execução de Título Extrajudicial (formulário tipo petição inicial)
        if (isExecucaoTituloExtrajudicialType) {
          const nomeClienteOk = (wizardData.campos_dinamicos?.nome_cliente || '').trim().length > 0;
          const fatosOk = (wizardData.campos_dinamicos?.fatos_caso || '').trim().length >= 200;
          const parteContrariaOk = (wizardData.campos_dinamicos?.parte_contraria || '').trim().length > 0;
          return nomeClienteOk && fatosOk && parteContrariaOk;
        }

        const isContrarrazoesApelacaoType = selectedDocumentType?.id === 'contrarrazoes_apelacao';
        const isRecursoInominadoType = selectedDocumentType?.id === 'recurso_inominado';
        const isContrarrazoesRecursoInominadoType = selectedDocumentType?.id === 'contrarrazoes_recurso_inominado';
        const isContraminutaAgravoType = selectedDocumentType?.id === 'contraminuta_agravo';
        const isContraminutaAgravoInternoType = selectedDocumentType?.id === 'contraminuta_agravo_interno';
        const isRecursoOrdinarioConstitucionalType = selectedDocumentType?.id === 'recurso_ordinario_constitucional';
        const isMemoriaisRecursaisType = selectedDocumentType?.id === 'memoriais_recursais';
        const isMemoriaisSTJType = selectedDocumentType?.id === 'memoriais_stj';
        const isContrarrazoesREspType = selectedDocumentType?.id === 'contrarrazoes_resp';
        const isAgravoREspType = selectedDocumentType?.id === 'agravo_resp';
        const isContraminutaAgravoREspType = selectedDocumentType?.id === 'contraminuta_agravo_resp';
        const isRecursoExtraordinarioType = selectedDocumentType?.id === 'recurso_extraordinario';
        const isContrarrazoesREType = selectedDocumentType?.id === 'contrarrazoes_re';
        const isEmbargosDivergenciaType = selectedDocumentType?.id === 'embargos_divergencia';
        const isImpugnacaoEmbargosDivergenciaType = selectedDocumentType?.id === 'impugnacao_embargos_divergencia';
        const isContrarrazoesEmbargosDeclaracaoType = selectedDocumentType?.id === 'contrarrazoes_embargos_declaracao';
        const isImpugnacaoCumprimentoSentencaType = selectedDocumentType?.id === 'impugnacao_cumprimento_sentenca';
        const isEmbargosExecucaoType = selectedDocumentType?.id === 'embargos_execucao';
        const isImpugnacaoEmbargosExecucaoType = selectedDocumentType?.id === 'impugnacao_embargos_execucao';
        const isEmbargosMonitoriaType = selectedDocumentType?.id === 'embargos_monitoria';
        const isExcecaoPreExecutividadeType = selectedDocumentType?.id === 'excecao_pre_executividade';
        const isImpugnacaoPenhoraType = selectedDocumentType?.id === 'impugnacao_penhora';
        const isImpugnacaoHomologacaoCalculosType = selectedDocumentType?.id === 'impugnacao_homologacao_calculos';
        const isPedidoLevantamentoValoresType = selectedDocumentType?.id === 'pedido_levantamento_valores';
        const isPedidoPenhoraFaturamentoType = selectedDocumentType?.id === 'pedido_penhora_faturamento';
        const isPedidoPenhoraRostoAutosType = selectedDocumentType?.id === 'pedido_penhora_rosto_autos';
        const isPedidoExpedicaoRPVType = selectedDocumentType?.id === 'pedido_expedicao_rpv';
        const isAcaoRescissoriaType = selectedDocumentType?.id === 'acao_rescissoria';
        const isAcordoJudicialExtincaoType = selectedDocumentType?.id === 'acordo_judicial_extincao';
        const isAcordoDivorcioType = selectedDocumentType?.id === 'acordo_divorcio';
        const isAcordoAlimentosGuardaType = selectedDocumentType?.id === 'acordo_alimentos_guarda';
        const isCumprimentoProvisorioType = selectedDocumentType?.id === 'cumprimento_provisorio';
        const isImpugnacaoCreditoLiquidacaoType = selectedDocumentType?.id === 'impugnacao_credito_liquidacao';
        const isHabilitacaoCreditoRetardatariaType = selectedDocumentType?.id === 'habilitacao_credito_retardataria';
        const isHabilitacaoCreditoAdministrativaType = selectedDocumentType?.id === 'habilitacao_credito_administrativa';
        const isAdaptarModeloType = selectedDocumentType?.id === 'adaptar_modelo';

        if (isContestacaoType || isReplicaType || isManifestacaoType || isAlegacoesFinaisType || isAgravoInstrumentoType || isAgravoInternoType || isRecursoEspecialType || isEmbargosDeclaracaoType || isCumprimentoSentencaType || isApelacaoType || isEmbargosTerceiroType || isTutelaIncidentalType || isTutelaEvidenciaType || isEmendaInicialType || isRecuperacaoJudicialType || isContrarrazoesApelacaoType || isRecursoInominadoType || isContrarrazoesRecursoInominadoType || isContraminutaAgravoType || isContraminutaAgravoInternoType || isRecursoOrdinarioConstitucionalType || isMemoriaisRecursaisType || isMemoriaisSTJType || isContrarrazoesREspType || isAgravoREspType || isContraminutaAgravoREspType || isRecursoExtraordinarioType || isContrarrazoesREType || isEmbargosDivergenciaType || isImpugnacaoEmbargosDivergenciaType || isContrarrazoesEmbargosDeclaracaoType || isImpugnacaoCumprimentoSentencaType || isEmbargosExecucaoType || isImpugnacaoEmbargosExecucaoType || isEmbargosMonitoriaType || isExcecaoPreExecutividadeType || isImpugnacaoPenhoraType || isImpugnacaoHomologacaoCalculosType || isPedidoLevantamentoValoresType || isPedidoPenhoraFaturamentoType || isPedidoPenhoraRostoAutosType || isPedidoExpedicaoRPVType || isAcaoRescissoriaType || isAcordoJudicialExtincaoType || isAcordoDivorcioType || isAcordoAlimentosGuardaType || isCumprimentoProvisorioType || isImpugnacaoCreditoLiquidacaoType || isHabilitacaoCreditoRetardatariaType || isHabilitacaoCreditoAdministrativaType || isAdaptarModeloType) {
          // Formulários específicos: precisa de nome_cliente e peca_rebater
          // fatos_caso é opcional, mas recomendado anexar arquivos
          const nomeClienteOk = (wizardData.campos_dinamicos?.nome_cliente || '').trim().length > 0;
          const pecaRebaterOk = (wizardData.campos_dinamicos?.peca_rebater || '').trim().length > 0;

          // nome do cliente + peça a rebater são obrigatórios
          // Arquivos são fortemente recomendados mas não obrigatórios
          return nomeClienteOk && pecaRebaterOk;
        }

        // Formulário padrão: exige pelo menos 200 caracteres de fatos OU arquivos anexados
        const fatosLength = (wizardData.descricao_fatos || wizardData.campos_dinamicos?.fatos_caso || '').trim().length;
        const hasFiles = uploadedFiles.length > 0;

        // Pode avançar se:
        // - Tiver pelo menos 200 caracteres de fatos, OU
        // - Tiver arquivos anexados
        return (
          fatosLength >= 200 ||
          hasFiles
        );
      case 2:
        // Step 2: Tipo de Ação e Tribunal
        // Tipo de ação agora é opcional para tipos genéricos (usuário pode detalhar se quiser)
        // Para tipos específicos, é preenchido automaticamente
        // Só tribunais são obrigatórios
        return wizardData.tribunais_selecionados.length > 0;
      case 3:
        // Step 3: Tópicos Jurídicos - Livre para o usuário escolher (pode ser 0)
        return true;
      case 4:
        // Step 4: Pedidos (livre para o usuário escolher)
        return true;
      case 5:
        // Step 5: Configurações do Documento (sempre pode avançar, nome tem padrão)
        return true;
      case 6:
        // Step 6: Revisão Final
        return true;
      default:
        return true;
    }
  };

  return (
    <Box
      ref={wizardTopRef}
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        pb: 4
      }}
    >
      {/* Header Compacto */}
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'white',
          py: { xs: 2, md: 2.5 },
          px: { xs: 2, md: 4 },
          mb: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                }}
              >
                Assistente de Criação de Documento
              </Typography>
            </Box>
            <Chip
              label={`Etapa ${activeStep + 1} de ${steps.length}`}
              size="small"
              sx={{
                bgcolor: 'primary.50',
                color: 'primary.main',
                fontWeight: 500
              }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
        {/* Credit Alerts */}
        {shouldShowWarning && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            ⚠️ <strong>Atenção:</strong> Seus créditos estão acabando ({credits} restantes). Recarregue para evitar interrupções.
          </Alert>
        )}

        {!isBlocked && credits < 12 && !overdraftUsed && (
          <Alert severity="info" sx={{ mb: 3 }}>
            💡 <strong>Cheque Especial:</strong> Você não possui créditos suficientes ({credits}) para gerar este documento (custo: 12), mas permitiremos esta geração como cortesia única.
          </Alert>
        )}

        {isBlocked && (
          <Alert severity="error" sx={{ mb: 3 }}>
            🚫 <strong>Conta Bloqueada:</strong> Seus créditos acabaram e o limite de cheque especial já foi utilizado. Recarregue agora para continuar.
          </Alert>
        )}

        {/* Alert quando vem do chat */}
        {fromChat && chatContext && (
          <Alert
            severity="success"
            sx={{
              mb: 3,
              background: 'linear-gradient(135deg, rgba(235, 93, 54, 0.08) 0%, rgba(255, 143, 78, 0.08) 100%)',
              border: '1px solid rgba(235, 93, 54, 0.3)',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: '#EB5D36'
              }
            }}
            onClose={() => navigate('/documents/wizard', { replace: true, state: {} })}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              ✨ Contexto importado do chat!
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Utilizamos as informações da sua conversa para pré-preencher o campo de descrição de fatos.
            </Typography>
          </Alert>
        )}

        {/* Stepper Responsivo */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            overflowX: 'auto'
          }}
        >
          <Stepper
            activeStep={activeStep}
            orientation="horizontal"
            sx={{
              minWidth: { xs: 700, md: 'auto' },
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              },
              '& .MuiStepConnector-line': {
                borderTopWidth: 2
              },
              '& .MuiStepIcon-root': {
                fontSize: { xs: '1.5rem', md: '2rem' }
              },
              '& .MuiStepIcon-root.Mui-active': {
                color: 'primary.main'
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: 'success.main'
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Modal de Loading durante análise */}
        <Dialog
          open={analyzingFiles}
          PaperProps={{
            sx: {
              borderRadius: 4,
              p: 4,
              maxWidth: 400,
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }
          }}
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(4px)',
              }
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            {/* Logo com animação */}
            <Box
              sx={{
                position: 'relative',
                width: 100,
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Círculo pulsante */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(235, 93, 54, 0.15)',
                  animation: 'pulseRing 2s ease-in-out infinite',
                  '@keyframes pulseRing': {
                    '0%': { transform: 'scale(0.9)', opacity: 1 },
                    '50%': { transform: 'scale(1.2)', opacity: 0.5 },
                    '100%': { transform: 'scale(0.9)', opacity: 1 }
                  }
                }}
              />

              {/* Logo flutuante */}
              <Box
                component="img"
                src="/favicon.svg"
                alt="Advogada Parceira"
                sx={{
                  width: 64,
                  height: 64,
                  zIndex: 1,
                  animation: 'logoFloat 3s ease-in-out infinite',
                  '@keyframes logoFloat': {
                    '0%, 100%': { transform: 'translateY(0) scale(1)' },
                    '50%': { transform: 'translateY(-8px) scale(1.08)' }
                  }
                }}
              />
            </Box>

            {/* Texto */}
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#EB5D36', mb: 1 }}
              >
                Analisando seu caso
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', maxWidth: 300, mx: 'auto', lineHeight: 1.6 }}
              >
                Identificando as áreas do direito mais relevantes para seu documento...
              </Typography>
            </Box>

            {/* Bolinhas animadas */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #EB5D36 0%, #FF8F4E 100%)',
                    animation: 'bounce 1.4s ease-in-out infinite',
                    animationDelay: `${i * 0.16}s`,
                    '@keyframes bounce': {
                      '0%, 80%, 100%': { transform: 'translateY(0) scale(1)', opacity: 0.5 },
                      '40%': { transform: 'translateY(-14px) scale(1.3)', opacity: 1 }
                    }
                  }}
                />
              ))}
            </Box>

            {/* Mensagem adicional */}
            <Typography variant="caption" color="text.disabled">
              Aguarde, isso pode levar alguns segundos
            </Typography>
          </Box>
        </Dialog>

        {/* Conteúdo da Etapa */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            minHeight: { xs: 300, md: 400 }
          }}
        >
          {renderStepContent(activeStep)}
        </Paper>

        {/* Botões de Navegação */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            position: 'sticky',
            bottom: 16,
            zIndex: 10,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBackIcon />}
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: 3,
                order: { xs: 2, sm: 1 }
              }}
            >
              Voltar
            </Button>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                order: { xs: 1, sm: 2 }
              }}
            >
              <Button
                variant="text"
                onClick={() => navigate('/documents')}
                sx={{
                  color: 'text.secondary',
                  order: { xs: 2, sm: 1 }
                }}
              >
                Cancelar
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleFinish}
                  disabled={!canProceed() || isGenerating || isBlocked} // Block generation
                  startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : undefined}
                  sx={{
                    minWidth: { xs: '100%', sm: 180 },
                    borderRadius: 2,
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    order: { xs: 1, sm: 2 },
                    ...(isGenerating && {
                      '&.Mui-disabled': {
                        background: 'primary.main',
                        color: 'white',
                        opacity: 0.8
                      }
                    })
                  }}
                >
                  {isGenerating ? 'Gerando...' : 'Gerar Documento'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceed() || analyzingFiles || isBlocked} // Block navigation
                  endIcon={analyzingFiles ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                  sx={{
                    minWidth: { xs: '100%', sm: 140 },
                    borderRadius: 2,
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    order: { xs: 1, sm: 2 },
                    ...(analyzingFiles && {
                      '&.Mui-disabled': {
                        background: 'primary.main',
                        color: 'white',
                        opacity: 0.8
                      }
                    })
                  }}
                >
                  {analyzingFiles ? 'Analisando...' : 'Próximo'}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Modal de Seleção de Tribunais - Compacto */}
      <Dialog
        open={showTribunalModal}
        onClose={() => {
          setShowTribunalModal(false);
          setTribunalSearchTerm('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Selecionar tribunais
            </Typography>
            <IconButton size="small" onClick={() => {
              setShowTribunalModal(false);
              setTribunalSearchTerm('');
            }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Digite para buscar ou selecione abaixo
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {/* Mapa de nomes completos */}
          {(() => {
            const nomesTribunais: Record<string, string> = {
              // Superiores
              'STF': 'Supremo Tribunal Federal',
              'STJ': 'Superior Tribunal de Justiça',
              'TST': 'Tribunal Superior do Trabalho',
              'TSE': 'Tribunal Superior Eleitoral',
              'STM': 'Superior Tribunal Militar',
              // TRFs
              'TRF1': 'TRF 1ª Região (DF, GO, TO, MT, BA, PI, MA, PA, AP, RR, RO, AC, AM)',
              'TRF2': 'TRF 2ª Região (RJ, ES)',
              'TRF3': 'TRF 3ª Região (SP, MS)',
              'TRF4': 'TRF 4ª Região (RS, PR, SC)',
              'TRF5': 'TRF 5ª Região (CE, AL, SE, PB, RN, PE)',
              'TRF6': 'TRF 6ª Região (MG)',
              // TJs
              'TJAC': 'Tribunal de Justiça do Acre',
              'TJAL': 'Tribunal de Justiça de Alagoas',
              'TJAP': 'Tribunal de Justiça do Amapá',
              'TJAM': 'Tribunal de Justiça do Amazonas',
              'TJBA': 'Tribunal de Justiça da Bahia',
              'TJCE': 'Tribunal de Justiça do Ceará',
              'TJDFT': 'Tribunal de Justiça do DF e Territórios',
              'TJES': 'Tribunal de Justiça do Espírito Santo',
              'TJGO': 'Tribunal de Justiça de Goiás',
              'TJMA': 'Tribunal de Justiça do Maranhão',
              'TJMT': 'Tribunal de Justiça do Mato Grosso',
              'TJMS': 'Tribunal de Justiça do Mato Grosso do Sul',
              'TJMG': 'Tribunal de Justiça de Minas Gerais',
              'TJPA': 'Tribunal de Justiça do Pará',
              'TJPB': 'Tribunal de Justiça da Paraíba',
              'TJPR': 'Tribunal de Justiça do Paraná',
              'TJPE': 'Tribunal de Justiça de Pernambuco',
              'TJPI': 'Tribunal de Justiça do Piauí',
              'TJRJ': 'Tribunal de Justiça do Rio de Janeiro',
              'TJRN': 'Tribunal de Justiça do Rio Grande do Norte',
              'TJRS': 'Tribunal de Justiça do Rio Grande do Sul',
              'TJRO': 'Tribunal de Justiça de Rondônia',
              'TJRR': 'Tribunal de Justiça de Roraima',
              'TJSC': 'Tribunal de Justiça de Santa Catarina',
              'TJSP': 'Tribunal de Justiça de São Paulo',
              'TJSE': 'Tribunal de Justiça de Sergipe',
              'TJTO': 'Tribunal de Justiça do Tocantins',
              // TRTs
              'TRT1': 'TRT 1ª Região (RJ)',
              'TRT2': 'TRT 2ª Região (SP Capital)',
              'TRT3': 'TRT 3ª Região (MG)',
              'TRT4': 'TRT 4ª Região (RS)',
              'TRT5': 'TRT 5ª Região (BA)',
              'TRT6': 'TRT 6ª Região (PE)',
              'TRT7': 'TRT 7ª Região (CE)',
              'TRT8': 'TRT 8ª Região (PA, AP)',
              'TRT9': 'TRT 9ª Região (PR)',
              'TRT10': 'TRT 10ª Região (DF, TO)',
              'TRT11': 'TRT 11ª Região (AM, RR)',
              'TRT12': 'TRT 12ª Região (SC)',
              'TRT13': 'TRT 13ª Região (PB)',
              'TRT14': 'TRT 14ª Região (RO, AC)',
              'TRT15': 'TRT 15ª Região (Campinas/SP)',
              'TRT16': 'TRT 16ª Região (MA)',
              'TRT17': 'TRT 17ª Região (ES)',
              'TRT18': 'TRT 18ª Região (GO)',
              'TRT19': 'TRT 19ª Região (AL)',
              'TRT20': 'TRT 20ª Região (SE)',
              'TRT21': 'TRT 21ª Região (RN)',
              'TRT22': 'TRT 22ª Região (PI)',
              'TRT23': 'TRT 23ª Região (MT)',
              'TRT24': 'TRT 24ª Região (MS)',
            };

            const getNome = (codigo: string) => nomesTribunais[codigo] || codigo;

            // Todos os tribunais para busca
            const todosTribunais = [
              ...['STF', 'STJ', 'TST', 'TSE', 'STM'],
              ...['TRF1', 'TRF2', 'TRF3', 'TRF4', 'TRF5', 'TRF6'],
              ...['TJSP', 'TJRJ', 'TJMG', 'TJRS', 'TJPR', 'TJSC', 'TJBA', 'TJPE', 'TJCE', 'TJGO', 'TJDFT', 'TJPA', 'TJAM', 'TJMT', 'TJMS', 'TJES', 'TJMA', 'TJPI', 'TJRN', 'TJPB', 'TJAL', 'TJSE', 'TJAC', 'TJAP', 'TJRO', 'TJRR', 'TJTO'],
              ...Array.from({ length: 24 }, (_, i) => `TRT${i + 1}`)
            ];

            // Filtrar tribunais baseado na busca
            const searchLower = tribunalSearchTerm.toLowerCase();
            const tribunaisFiltrados = tribunalSearchTerm
              ? todosTribunais.filter(t =>
                t.toLowerCase().includes(searchLower) ||
                getNome(t).toLowerCase().includes(searchLower)
              )
              : [];

            return (
              <>
                {/* Campo de Busca */}
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Buscar tribunal (ex: São Paulo, TRF3, Trabalho...)"
                  value={tribunalSearchTerm}
                  onChange={(e) => setTribunalSearchTerm(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <Box sx={{ mr: 1, color: 'text.secondary', display: 'flex' }}>🔍</Box>,
                    endAdornment: tribunalSearchTerm && (
                      <IconButton size="small" onClick={() => setTribunalSearchTerm('')}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )
                  }}
                />

                {/* Resultados da Busca */}
                {tribunalSearchTerm && (
                  <Box sx={{ mb: 2 }}>
                    {tribunaisFiltrados.length > 0 ? (
                      <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {tribunaisFiltrados.length} resultado(s) encontrado(s)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {tribunaisFiltrados.map(codigo => (
                            <Tooltip key={codigo} title={getNome(codigo)} arrow placement="top">
                              <Chip
                                label={codigo}
                                size="small"
                                variant={wizardData.tribunais_selecionados.includes(codigo) ? 'filled' : 'outlined'}
                                color={wizardData.tribunais_selecionados.includes(codigo) ? 'primary' : 'default'}
                                onClick={() => {
                                  handleTribunalToggle(codigo);
                                  setTribunalSearchTerm('');
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </Paper>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        Nenhum tribunal encontrado para "{tribunalSearchTerm}"
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Tribunais quando não está buscando */}
                {!tribunalSearchTerm && (
                  <>
                    {/* Tribunais Superiores - Grid compacto */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {['STF', 'STJ', 'TST', 'TSE', 'STM'].map(codigo => (
                        <Tooltip key={codigo} title={getNome(codigo)} arrow placement="top">
                          <Chip
                            label={codigo}
                            size="small"
                            variant={wizardData.tribunais_selecionados.includes(codigo) ? 'filled' : 'outlined'}
                            color={wizardData.tribunais_selecionados.includes(codigo) ? 'primary' : 'default'}
                            onClick={() => handleTribunalToggle(codigo)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      ))}
                    </Box>

                    {/* Grupos em Accordion compacto */}
                    {[
                      { id: 'trfs', label: 'TRFs', items: ['TRF1', 'TRF2', 'TRF3', 'TRF4', 'TRF5', 'TRF6'] },
                      { id: 'tjs', label: 'TJs', items: ['TJSP', 'TJRJ', 'TJMG', 'TJRS', 'TJPR', 'TJSC', 'TJBA', 'TJPE', 'TJCE', 'TJGO', 'TJDFT', 'TJPA', 'TJAM', 'TJMT', 'TJMS', 'TJES', 'TJMA', 'TJPI', 'TJRN', 'TJPB', 'TJAL', 'TJSE', 'TJAC', 'TJAP', 'TJRO', 'TJRR', 'TJTO'] },
                      { id: 'trts', label: 'TRTs', items: Array.from({ length: 24 }, (_, i) => `TRT${i + 1}`) },
                    ].map(grupo => (
                      <Accordion
                        key={grupo.id}
                        expanded={expandedTribunalGroup === grupo.id}
                        onChange={() => setExpandedTribunalGroup(expandedTribunalGroup === grupo.id ? null : grupo.id)}
                        disableGutters
                        elevation={0}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:not(:last-child)': { mb: 1 },
                          '&:before': { display: 'none' }
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{grupo.label}</Typography>
                            <Typography variant="caption" color="text.secondary">({grupo.items.length})</Typography>
                            {grupo.items.some(t => wizardData.tribunais_selecionados.includes(t)) && (
                              <Chip
                                label={grupo.items.filter(t => wizardData.tribunais_selecionados.includes(t)).length}
                                size="small"
                                color="primary"
                                sx={{ height: 18, fontSize: '0.65rem', ml: 'auto', mr: 1 }}
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 1, px: 1.5 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {grupo.items.map(codigo => (
                              <Tooltip key={codigo} title={getNome(codigo)} arrow placement="top">
                                <Chip
                                  label={codigo}
                                  size="small"
                                  variant={wizardData.tribunais_selecionados.includes(codigo) ? 'filled' : 'outlined'}
                                  color={wizardData.tribunais_selecionados.includes(codigo) ? 'primary' : 'default'}
                                  onClick={() => handleTribunalToggle(codigo)}
                                  sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 24 }}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </>
                )}
              </>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, mr: 2 }}>
            {wizardData.tribunais_selecionados.map(t => (
              <Tooltip key={t} title={(() => {
                const nomes: Record<string, string> = {
                  'STF': 'Supremo Tribunal Federal', 'STJ': 'Superior Tribunal de Justiça', 'TST': 'Tribunal Superior do Trabalho', 'TSE': 'Tribunal Superior Eleitoral', 'STM': 'Superior Tribunal Militar',
                  'TRF1': 'TRF 1ª Região', 'TRF2': 'TRF 2ª Região', 'TRF3': 'TRF 3ª Região', 'TRF4': 'TRF 4ª Região', 'TRF5': 'TRF 5ª Região', 'TRF6': 'TRF 6ª Região',
                  'TJSP': 'TJ São Paulo', 'TJRJ': 'TJ Rio de Janeiro', 'TJMG': 'TJ Minas Gerais', 'TJRS': 'TJ Rio Grande do Sul', 'TJPR': 'TJ Paraná', 'TJSC': 'TJ Santa Catarina',
                  'TJBA': 'TJ Bahia', 'TJPE': 'TJ Pernambuco', 'TJCE': 'TJ Ceará', 'TJGO': 'TJ Goiás', 'TJDFT': 'TJ DF e Territórios', 'TJPA': 'TJ Pará',
                };
                return nomes[t] || t;
              })()} arrow>
                <Chip label={t} size="small" onDelete={() => handleTribunalToggle(t)} color="primary" />
              </Tooltip>
            ))}
            {wizardData.tribunais_selecionados.length === 0 && (
              <Typography variant="caption" color="text.secondary">Nenhum selecionado</Typography>
            )}
          </Box>
          <Button size="small" onClick={() => setShowTribunalModal(false)}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => setShowTribunalModal(false)}
            disabled={wizardData.tribunais_selecionados.length === 0}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Pedido Personalizado */}
      <Dialog
        open={showPedidoModal}
        onClose={() => {
          setShowPedidoModal(false);
          setNovoPedido({
            titulo: '',
            natureza: 'principal',
            descricao: '',
            baseLegal: '',
            tipoValor: 'sem_valor',
            valor: '',
            incluirValor: false,
            condicao: '',
            impactoValor: 'nao_altera'
          });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Adicionar pedido personalizado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Preencha as informações abaixo para que eu possa formular corretamente o pedido na contestação, respeitando a técnica processual e a estratégia da defesa.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Título do Pedido */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Título do pedido *</Typography>
              <TextField
                fullWidth
                size="small"
                value={novoPedido.titulo}
                onChange={(e) => setNovoPedido(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ex: Improcedência do pedido de indenização"
                helperText="Indique, de forma direta, o que se pretende com o pedido."
              />
            </Box>

            {/* Natureza do Pedido */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Natureza do pedido (Opcional)</Typography>
              <RadioGroup
                row
                value={novoPedido.natureza}
                onChange={(e) => setNovoPedido(prev => ({ ...prev, natureza: e.target.value as any }))}
              >
                <FormControlLabel value="principal" control={<Radio size="small" />} label="Principal" />
                <FormControlLabel value="acessorio" control={<Radio size="small" />} label="Acessório" />
                <FormControlLabel value="subsidiario" control={<Radio size="small" />} label="Subsidiário" />
                <FormControlLabel value="alternativo" control={<Radio size="small" />} label="Alternativo" />
                <FormControlLabel value="condicional" control={<Radio size="small" />} label="Condicional" />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                (Essa informação ajuda a organizar a ordem e a lógica dos pedidos.)
              </Typography>
            </Box>

            {/* Descrição */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Descrição do pedido (Recomendado)</Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={novoPedido.descricao}
                onChange={(e) => setNovoPedido(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Explique o conteúdo, contexto e efeito jurídico esperado."
                helperText="Ex: Caso ultrapassada a preliminar, requer a improcedência por ausência de ato ilícito."
              />
            </Box>

            {/* Fundamento Jurídico */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Fundamento jurídico (Opcional)</Typography>
              <TextField
                fullWidth
                size="small"
                value={novoPedido.baseLegal}
                onChange={(e) => setNovoPedido(prev => ({ ...prev, baseLegal: e.target.value }))}
                placeholder="Ex: Art. 186 e 927 do Código Civil"
                helperText="Indique dispositivos legais ou precedentes que sustentam o pedido."
              />
            </Box>

            {/* Valor */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Valor vinculado ao pedido (Opcional)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Valor</InputLabel>
                    <Select
                      value={novoPedido.tipoValor}
                      label="Tipo de Valor"
                      onChange={(e) => {
                        const tipo = e.target.value;
                        setNovoPedido(prev => ({
                          ...prev,
                          tipoValor: tipo,
                          incluirValor: tipo !== 'sem_valor'
                        }));
                      }}
                    >
                      <MenuItem value="sem_valor">Sem valor definido</MenuItem>
                      <MenuItem value="certo">Valor certo</MenuItem>
                      <MenuItem value="estimado">Valor estimado</MenuItem>
                      <MenuItem value="percentual">Valor em percentuais</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {novoPedido.tipoValor !== 'sem_valor' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={novoPedido.tipoValor === 'percentual' ? 'Percentual' : 'Valor'}
                      value={novoPedido.valor}
                      onChange={(e) => setNovoPedido(prev => ({ ...prev, valor: e.target.value }))}
                      placeholder={novoPedido.tipoValor === 'percentual' ? 'Ex: 10%' : 'Ex: R$ 10.000,00'}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Campos Condicionais */}
            {(novoPedido.natureza === 'subsidiario' || novoPedido.natureza === 'condicional') && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Condição para apreciação (Opcional)</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={novoPedido.condicao}
                  onChange={(e) => setNovoPedido(prev => ({ ...prev, condicao: e.target.value }))}
                  placeholder="Ex: Apenas se rejeitada a preliminar de mérito"
                  helperText="Descreva brevemente em que hipótese o pedido deve ser analisado."
                />
              </Box>
            )}

            {/* Impacto no Valor da Causa */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Impacto no valor da causa (Opcional – avançado)</Typography>
              <RadioGroup
                row
                value={novoPedido.impactoValor}
                onChange={(e) => setNovoPedido(prev => ({ ...prev, impactoValor: e.target.value as any }))}
              >
                <FormControlLabel value="nao_altera" control={<Radio size="small" />} label="Não altera" />
                <FormControlLabel value="altera" control={<Radio size="small" />} label="Altera" />
                <FormControlLabel value="depende_apuracao" control={<Radio size="small" />} label="Depende de apuração" />
              </RadioGroup>
            </Box>

          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setShowPedidoModal(false);
              setNovoPedido({
                titulo: '',
                natureza: 'principal',
                descricao: '',
                baseLegal: '',
                tipoValor: 'sem_valor',
                valor: '',
                incluirValor: false,
                condicao: '',
                impactoValor: 'nao_altera'
              });
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!novoPedido.titulo.trim()}
            onClick={() => {
              const pedidoPersonalizado: PedidoSelecionado = {
                id: `custom_${Date.now()}`,
                titulo: novoPedido.titulo.trim(),
                descricao: novoPedido.descricao.trim(),
                baseLegal: novoPedido.baseLegal.trim(),
                valor: novoPedido.tipoValor !== 'sem_valor' ? novoPedido.valor.trim() : undefined,
                incluirValor: novoPedido.tipoValor !== 'sem_valor',
                customizado: true,
                natureza: novoPedido.natureza as any,
                tipoValor: novoPedido.tipoValor as any,
                condicao: novoPedido.condicao,
                impactoValor: novoPedido.impactoValor as any
              };

              setWizardData(prev => ({
                ...prev,
                pedidos_selecionados: [...prev.pedidos_selecionados, pedidoPersonalizado]
              }));

              setShowPedidoModal(false);
              setNovoPedido({
                titulo: '',
                natureza: 'principal',
                descricao: '',
                baseLegal: '',
                tipoValor: 'sem_valor',
                valor: '',
                incluirValor: false,
                condicao: '',
                impactoValor: 'nao_altera'
              });
            }}
          >
            Adicionar Pedido
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog de Edição de Áreas */}
      <Dialog
        open={showAreaEditDialog}
        onClose={() => setShowAreaEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Áreas do Direito</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Selecione as áreas jurídicas aplicáveis ao seu caso. Isso ajuda a IA a contextualizar melhor o documento.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {areasDisponiveis.map((area) => (
              <Chip
                key={area.id}
                label={area.nome}
                icon={<span>{area.icone}</span>}
                onClick={() => handleAreaChange(area.id)}
                color={selectedAreas.includes(area.id) ? "primary" : "default"}
                variant={selectedAreas.includes(area.id) ? "filled" : "outlined"}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAreaEditDialog(false)}>Concluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentWizard; 